import { LOCALE_CONFIG } from './config';

// Note: bg, ja, th fall back to English — jsPDF's built-in Helvetica font
// does not support Cyrillic, CJK, or Thai scripts.
// Eastern European languages (pl, cs, hu, ro, sk, sl, et, lv, lt) are
// translated but some diacritics may not render until a Unicode font is added.

export function getPdfStrings(locale) {
  const lang = LOCALE_CONFIG[locale]?.lang || 'en';
  if (['bg', 'ja', 'th'].includes(lang)) return STRINGS.en;
  return STRINGS[lang] || STRINGS.en;
}

// ─────────────────────────────────────────────
// ENGLISH (base)
// ─────────────────────────────────────────────
const en = {
  months: ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"],
  monthsFull: ["January","February","March","April","May","June","July","August","September","October","November","December"],
  fmtDuration: (months) => {
    const y = Math.floor(months / 12), m = months % 12;
    if (!y) return `${months} months`;
    if (!m) return `${y}yr`;
    return `${y}yr ${m}mo`;
  },

  // Header / Footer
  preparedBy: "Prepared by Weelco Inc.",
  disclaimer: "This report is for informational purposes only. Weelco Inc. is not a financial advisor.",

  // Page 1 — Debt Snapshot
  p1Title: "Your Debt Snapshot",
  whereYouStand: "WHERE YOU STAND TODAY",
  totalDebt: "TOTAL DEBT",
  acrossAccounts: n => `across ${n} account${n !== 1 ? "s" : ""}`,
  minPaymentPerMo: "Min. Payment per mo",
  avgInterestRate: "Avg. Interest Rate",
  payingMinimumsOnly: "Paying minimums only",
  minimumsWarning: (cost, date) => `Paying only minimums costs ${cost} in interest and takes until ${date}.`,
  withExtraSavings: (extra, saved, time) => `With your ${extra}/mo extra you save ${saved} and finish ${time} sooner.`,
  recommended: "RECOMMENDED",
  methodSavingsDesc: (desc, saved, worst, extra) => `${desc} · saves ${saved} vs ${worst} at ${extra}/mo`,
  yourScenarios: "YOUR PAYMENT SCENARIOS",
  yourDebts: "YOUR DEBTS",
  conservative: "Conservative",
  balanced: "Balanced",
  aggressive: "Aggressive",
  saves: amt => `saves ${amt}`,

  // Page 2 — All 4 Methods
  p2Title: "All 4 Methods Compared",
  basedOn: (debt, extra) => `Based on your ${debt} debt with ${extra}/mo extra`,
  debtFreeIn: "Debt-free in",
  totalInterest: "Total interest",
  interestVsWorst: "Interest vs worst",
  summary: "Summary",
  recommendedSummary: (method, saved, worst, extra) => `Recommended: ${method} — saves ${saved} vs ${worst} method at ${extra}/mo extra.`,
  methodLabels: { avalanche: "Avalanche", snowball: "Snowball", highbal: "High Balance", lowint: "Low Interest" },
  methodDescs: { avalanche: "Highest interest rate first", snowball: "Smallest balance first", highbal: "Largest balance first", lowint: "Lowest rate first" },
  methodDescsLong: { avalanche: "Highest interest rate first", snowball: "Smallest balance first", highbal: "Largest balance first", lowint: "Lowest interest rate first" },
  methodDescsP2: {
    avalanche: "Pay highest interest rate first. Mathematically optimal — saves the most money.",
    snowball:  "Pay smallest balance first. Builds momentum with quick wins.",
    highbal:   "Target largest debt first. Reduces biggest liability fastest.",
    lowint:    "Pay lowest rate first. Frees up cash flow quickly.",
  },

  // Page 3 — Attack Order
  p3Title: "Which Debt to Attack First",
  apr: "APR",
  balance: "balance",
  minPerMo: amt => `Min: ${amt}/mo`,
  attackFirst: (reason, extra) => `Attack first -- ${reason}. All ${extra}/mo extra goes here.`,
  payMinimumsOthers: "Pay minimums on all other debts.",
  attackSecond: debt => `Attack second. When ${debt} is paid off,`,
  rollPaymentHere: amt => `roll its ${amt}/mo payment here on top of your minimum.`,
  attackN: (n, prevN) => `Attack #${n}. Pay minimums until the ${prevN} debt${prevN > 1 ? "s" : ""} above are cleared,`,
  rollFreed: "then roll all freed-up payments here.",
  reasonHighestRate: rate => `highest rate at ${rate}%`,
  reasonSmallestBal: bal => `smallest balance at ${bal}`,
  reasonLargestBal: bal => `largest balance at ${bal}`,
  reasonLowestRate: rate => `lowest rate at ${rate}%`,
  consistency: {
    avalanche: extra => `The key to Avalanche is consistency -- never miss a minimum payment on any debt, and always put your extra ${extra} toward debt #1 until it is gone.`,
    snowball:  extra => `The key to Snowball is consistency -- never miss a minimum payment on any debt, and always put your extra ${extra} toward the smallest balance until it is gone.`,
    highbal:   extra => `The key to High Balance is consistency -- never miss a minimum payment on any debt, and always put your extra ${extra} toward the largest balance until it is gone.`,
    lowint:    extra => `The key to Low Interest is consistency -- never miss a minimum payment on any debt, and always put your extra ${extra} toward the lowest-rate debt until it is gone.`,
  },

  // Page 4 — What If You Pay More?
  p4Title: "What If You Pay More?",
  marginalAnalysis: (method, sweet) => `Marginal analysis — ${method} method · sweet spot: ${sweet}/mo`,
  scenarioCol: "Scenario",
  debtFreeCol: "Debt-Free",
  totalInterestCol: "Total Interest",
  totalSavedCol: "Total Saved",
  marginalGainCol: "Marginal Gain",
  minimumsOnlyRow: amt => `${amt}/mo (minimums only)`,
  scenarioRow: (amt, label) => `${amt}/mo — ${label}`,
  sweetSpotNote: sweet => `Sweet spot: ${sweet}/mo gives the best return — each extra dollar starts saving less after this point.`,
  payAsMuchNote: "Pay as much extra as you can — each dollar reduces interest significantly.",
  aggressiveNote: (saved, extra) => `Going Aggressive saves ${saved} more but requires ${extra}/mo extra — use your budget to decide.`,
  bonusNote: "If you receive a bonus or tax refund, put it toward debt first.",

  // Page 5 — Month-by-Month
  p5Title: "Month-by-Month Schedule",
  methodAllDebts: (method, months) => `${method} method - all debts - ${months} months total`,
  monthCol: "Month",
  focusDebt: "Focus Debt",
  paymentCol: "Payment",
  principalCol: "Principal",
  interestCol: "Interest",
  remainingCol: "Remaining",
  paidOffRolling: (names, next) => `${names} PAID OFF - rolling payment to ${next}`,
  paidOff: names => `${names} PAID OFF`,
  debtFreeRow: date => `DEBT FREE - ${date}`,
  allDebtsNote: (n, date, interest) => `All ${n} debt${n !== 1 ? "s" : ""} paid off by ${date}. Total interest paid: ${interest}.`,

  // Page 6 — Consolidation
  p6Title: "Debt Consolidation Analysis",
  isItWorthIt: "Is it worth it for you?",
  aprConsolidationLoan: "APR consolidation loan",
  totalInterestLabel: "total interest",
  savesAmount: amt => `Saves ${amt}`,
  costsMore: amt => `Costs ${amt} more`,
  breakEvenPoint: fee => `Break-Even Point (including ${fee} origination fee)`,
  neverBreaksEven: "Never breaks even - not recommended",
  breakEvenAfterPayoff: "Break-even after payoff date - barely worth it",
  breakEvenSaves: (month, amt) => `Break-even at Month ${month} - saves ${amt}/mo after`,
  breakEvenBarely: month => `Break-even at Month ${month} - barely worth it`,
  atApr: rate => `At ${rate}% APR`,
  ourRecommendation: "Our Recommendation",
  consolidationBelowApr: rate => `Consolidation only makes sense below ${rate}% APR.`,
  noneConsolidationRates: "None of these consolidation rates beat your current payoff plan.",
  debtExpensive: (name, rate) => `Your ${name} at ${rate}% is expensive -`,
  qualifyConsolidation: "if you qualify for 10% consolidation, consider it seriously.",
  stopUsingCard: "Important: stop using the credit card after consolidating.",

  // Page 7 — Action Plan + FAQ
  p7Title: "Your Action Plan + FAQ",
  stepsToFreedom: "3 steps to debt freedom - starting today",
  startingDate: date => `STARTING ${date}`,
  debtFreeDateLabel: "DEBT-FREE DATE",
  youWillSave: amt => `You will save ${amt} vs paying minimums only`,
  glossaryFaq: "Glossary & FAQ",
  step1: (d1, extra, d1combined) => `Now: Set autopay minimums on all debts except ${d1}. Put all your ${extra}/mo extra + ${d1} minimum = ${d1combined}/mo total on ${d1}. Do not touch this until it is gone.`,
  step2: (date, d1, d1combined, d2, d2combined) => `Around ${date}: ${d1} is paid off. Roll the full ${d1combined}/mo into ${d2}. You will now pay ${d2combined}/mo on ${d2}.`,
  step3withD3: (date, d2, totalFreed, d3) => `Around ${date}: ${d2} is done. Roll everything -- ${totalFreed}/mo -- into ${d3}. Final push. You are almost there.`,
  step3noD3: (date, d2) => `Around ${date}: ${d2} is done. You are debt free!`,
  faqs: [
    { q: "What is the Debt Avalanche method?",   a: "Pay minimums on all debts, then put all extra money toward the highest interest rate debt. Once paid off, move to the next highest rate. Saves the most money in interest over time." },
    { q: "What is the Debt Snowball method?",    a: "Attack the smallest balance first regardless of rate. Once paid off, roll that payment into the next smallest. Costs slightly more in interest, but quick wins keep you motivated." },
    { q: "What is the High Balance method?",     a: "Focus on the largest balance first. Reduces your biggest debt fastest but is not always the most efficient financially since high-balance debts do not always have the highest rates." },
    { q: "What is the Low Interest method?",     a: "Pay off the lowest interest rate debt first. Frees up minimum payments fastest but costs the most in total interest. Generally not recommended unless you have a specific reason." },
    { q: "What does rolling a payment mean?",    a: "When a debt is paid off, add that money to the next debt's payment. Your total monthly spending stays the same -- you just redirect it. This is how avalanche and snowball effects accelerate payoff." },
    { q: "What is APR?",                         a: "Annual Percentage Rate -- the yearly cost of borrowing as a percentage of your balance. At 22% APR you pay about 1.83% per month in interest. Lower APR means cheaper debt." },
    { q: "What is debt consolidation?",          a: "One new loan to pay off multiple debts. If the new rate is lower than your current average, you save money. Risk: if you keep using credit cards after consolidating, you could end up deeper in debt." },
    { q: "How accurate are these calculations?", a: "Assumes fixed rates and consistent payments. Real results may vary due to rate changes or fees. Treat this as a reliable roadmap, not a guarantee. Not financial advice." },
  ],
};

// ─────────────────────────────────────────────
// GERMAN
// ─────────────────────────────────────────────
const de = {
  ...en,
  months: ["Jan","Feb","Mrz","Apr","Mai","Jun","Jul","Aug","Sep","Okt","Nov","Dez"],
  monthsFull: ["Januar","Februar","Marz","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"],
  fmtDuration: (months) => {
    const y = Math.floor(months / 12), m = months % 12;
    if (!y) return `${months} Mon.`;
    if (!m) return `${y} J.`;
    return `${y} J. ${m} M.`;
  },
  preparedBy: "Erstellt von Weelco Inc.",
  disclaimer: "Dieser Bericht dient nur zu Informationszwecken. Weelco Inc. ist kein Finanzberater.",
  p1Title: "Ihre Schuldenubersicht",
  whereYouStand: "IHR AKTUELLER STAND",
  totalDebt: "GESAMTSCHULDEN",
  acrossAccounts: n => `auf ${n} Konto${n !== 1 ? "en" : ""}`,
  minPaymentPerMo: "Mindestrate pro Monat",
  avgInterestRate: "Durchschn. Zinssatz",
  payingMinimumsOnly: "Nur Mindestraten zahlen",
  minimumsWarning: (cost, date) => `Nur Mindestraten zahlen kostet ${cost} Zinsen und dauert bis ${date}.`,
  withExtraSavings: (extra, saved, time) => `Mit ${extra}/Mon. extra sparen Sie ${saved} und werden ${time} fruher schuldenfrei.`,
  recommended: "EMPFOHLEN",
  methodSavingsDesc: (desc, saved, worst, extra) => `${desc} · spart ${saved} vs. ${worst} bei ${extra}/Mon.`,
  yourScenarios: "IHRE ZAHLUNGSSZENARIEN",
  yourDebts: "IHRE SCHULDEN",
  conservative: "Konservativ",
  balanced: "Ausgewogen",
  aggressive: "Aggressiv",
  saves: amt => `spart ${amt}`,
  p2Title: "Alle 4 Methoden im Vergleich",
  basedOn: (debt, extra) => `Basierend auf ${debt} Schulden mit ${extra}/Mon. extra`,
  debtFreeIn: "Schuldenfrei in",
  totalInterest: "Gesamtzinsen",
  interestVsWorst: "Zinsen vs. schlechteste",
  summary: "Zusammenfassung",
  recommendedSummary: (method, saved, worst, extra) => `Empfohlen: ${method} — spart ${saved} vs. ${worst}-Methode bei ${extra}/Mon. extra.`,
  methodLabels: { avalanche: "Lawine", snowball: "Schneeball", highbal: "Hochster Saldo", lowint: "Niedrigster Zins" },
  methodDescs: { avalanche: "Hochster Zinssatz zuerst", snowball: "Kleinster Saldo zuerst", highbal: "Grosster Saldo zuerst", lowint: "Niedrigster Zinssatz zuerst" },
  methodDescsLong: { avalanche: "Hochster Zinssatz zuerst", snowball: "Kleinster Saldo zuerst", highbal: "Grosster Saldo zuerst", lowint: "Niedrigster Zinssatz zuerst" },
  methodDescsP2: {
    avalanche: "Hochsten Zinssatz zuerst zahlen. Mathematisch optimal — spart am meisten.",
    snowball:  "Kleinsten Saldo zuerst zahlen. Schnelle Erfolge schaffen Motivation.",
    highbal:   "Grossten Saldo zuerst angreifen. Reduziert die grosste Schuld am schnellsten.",
    lowint:    "Niedrigsten Zinssatz zuerst zahlen. Setzt schnell Cashflow frei.",
  },
  p3Title: "Welche Schuld zuerst angreifen",
  minPerMo: amt => `Mind.: ${amt}/Mon.`,
  attackFirst: (reason, extra) => `Zuerst angreifen -- ${reason}. Alle ${extra}/Mon. extra kommen hierher.`,
  payMinimumsOthers: "Mindestraten fur alle anderen Schulden zahlen.",
  attackSecond: debt => `Als zweites angreifen. Wenn ${debt} abbezahlt ist,`,
  rollPaymentHere: amt => `dessen ${amt}/Mon. hier obendrauf zahlen.`,
  attackN: (n, prevN) => `#${n} angreifen. Mindestraten zahlen bis die ${prevN} Schuld${prevN > 1 ? "en" : ""} oben getilgt sind,`,
  rollFreed: "dann alle freigewordenen Zahlungen hierher umleiten.",
  reasonHighestRate: rate => `hochster Zinssatz bei ${rate}%`,
  reasonSmallestBal: bal => `kleinster Saldo bei ${bal}`,
  reasonLargestBal: bal => `grosster Saldo bei ${bal}`,
  reasonLowestRate: rate => `niedrigster Zinssatz bei ${rate}%`,
  consistency: {
    avalanche: extra => `Der Schlussel zur Lawine ist Konsequenz -- verpassen Sie nie eine Mindestrate und setzen Sie immer ${extra} extra auf Schuld Nr. 1, bis sie abbezahlt ist.`,
    snowball:  extra => `Der Schlussel zum Schneeball ist Konsequenz -- verpassen Sie nie eine Mindestrate und setzen Sie immer ${extra} extra auf den kleinsten Saldo, bis er abbezahlt ist.`,
    highbal:   extra => `Der Schlussel zur Hochster-Saldo-Methode ist Konsequenz -- verpassen Sie nie eine Mindestrate und setzen Sie immer ${extra} extra auf den grossten Saldo, bis er abbezahlt ist.`,
    lowint:    extra => `Der Schlussel zur Niedrigster-Zins-Methode ist Konsequenz -- verpassen Sie nie eine Mindestrate und setzen Sie immer ${extra} extra auf die Schuld mit dem niedrigsten Zinssatz.`,
  },
  p4Title: "Was wenn Sie mehr zahlen?",
  marginalAnalysis: (method, sweet) => `Grenzanalyse — ${method}-Methode · Optimalpunkt: ${sweet}/Mon.`,
  scenarioCol: "Szenario",
  debtFreeCol: "Schuldenfrei",
  totalInterestCol: "Gesamtzinsen",
  totalSavedCol: "Ersparnis",
  marginalGainCol: "Grenzgewinn",
  minimumsOnlyRow: amt => `${amt}/Mon. (nur Mindestraten)`,
  scenarioRow: (amt, label) => `${amt}/Mon. — ${label}`,
  sweetSpotNote: sweet => `Optimalpunkt: ${sweet}/Mon. bietet die beste Rendite — jeder weitere Euro spart danach weniger.`,
  payAsMuchNote: "Zahlen Sie so viel extra wie moglich — jeder Euro reduziert die Zinsen deutlich.",
  aggressiveNote: (saved, extra) => `Aggressiv zu zahlen spart ${saved} mehr, erfordert aber ${extra}/Mon. extra — entscheiden Sie nach Budget.`,
  bonusNote: "Wenn Sie einen Bonus oder Steuererstattung erhalten, setzen Sie ihn zuerst auf die Schulden.",
  p5Title: "Monatlicher Zahlungsplan",
  methodAllDebts: (method, months) => `${method}-Methode - alle Schulden - ${months} Monate gesamt`,
  monthCol: "Monat",
  focusDebt: "Fokus-Schuld",
  paymentCol: "Zahlung",
  principalCol: "Tilgung",
  interestCol: "Zinsen",
  remainingCol: "Restschuld",
  paidOffRolling: (names, next) => `${names} ABBEZAHLT - Zahlung wird auf ${next} ubertragen`,
  paidOff: names => `${names} ABBEZAHLT`,
  debtFreeRow: date => `SCHULDENFREI - ${date}`,
  allDebtsNote: (n, date, interest) => `Alle ${n} Schulden abbezahlt bis ${date}. Gezahlte Zinsen gesamt: ${interest}.`,
  p6Title: "Schuldenkonsolidierung",
  isItWorthIt: "Lohnt es sich fur Sie?",
  aprConsolidationLoan: "Jahreszins Konsolidierungskredit",
  totalInterestLabel: "Gesamtzinsen",
  savesAmount: amt => `Spart ${amt}`,
  costsMore: amt => `Kostet ${amt} mehr`,
  breakEvenPoint: fee => `Break-Even-Punkt (inkl. ${fee} Bearbeitungsgebur)`,
  neverBreaksEven: "Erreicht niemals Break-Even - nicht empfohlen",
  breakEvenAfterPayoff: "Break-Even nach Tilgungsdatum - kaum lohnenswert",
  breakEvenSaves: (month, amt) => `Break-Even in Monat ${month} - spart danach ${amt}/Mon.`,
  breakEvenBarely: month => `Break-Even in Monat ${month} - kaum lohnenswert`,
  atApr: rate => `Bei ${rate}% Jahreszins`,
  ourRecommendation: "Unsere Empfehlung",
  consolidationBelowApr: rate => `Konsolidierung lohnt sich nur unter ${rate}% Jahreszins.`,
  noneConsolidationRates: "Keiner dieser Zinssatze schlagt Ihren aktuellen Tilgungsplan.",
  debtExpensive: (name, rate) => `Ihre ${name} bei ${rate}% ist teuer -`,
  qualifyConsolidation: "wenn Sie 10% Kredit bekommen, ernsthaft erwagen.",
  stopUsingCard: "Wichtig: Kreditkarte nach Konsolidierung nicht mehr nutzen.",
  p7Title: "Ihr Aktionsplan + FAQ",
  stepsToFreedom: "3 Schritte zur Schuldenfreiheit - ab heute",
  startingDate: date => `START ${date}`,
  debtFreeDateLabel: "SCHULDENFREIHEITSDATUM",
  youWillSave: amt => `Sie sparen ${amt} gegenuber Mindestraten`,
  glossaryFaq: "Glossar & FAQ",
  step1: (d1, extra, d1combined) => `Jetzt: Autopay-Mindestraten fur alle Schulden ausser ${d1} einrichten. Alle ${extra}/Mon. extra + ${d1}-Mindestrate = ${d1combined}/Mon. auf ${d1}. Nicht antasten bis abbezahlt.`,
  step2: (date, d1, d1combined, d2, d2combined) => `Um ${date}: ${d1} abbezahlt. Die ${d1combined}/Mon. auf ${d2} umleiten. Sie zahlen jetzt ${d2combined}/Mon. auf ${d2}.`,
  step3withD3: (date, d2, totalFreed, d3) => `Um ${date}: ${d2} fertig. Alles -- ${totalFreed}/Mon. -- auf ${d3} umleiten. Endspurt. Fast geschafft.`,
  step3noD3: (date, d2) => `Um ${date}: ${d2} fertig. Sie sind schuldenfrei!`,
  faqs: [
    { q: "Was ist die Lawinen-Methode?",         a: "Mindestraten auf alle Schulden zahlen, dann alles extra auf die Schuld mit dem hochsten Zinssatz. Spart am meisten Zinsen insgesamt." },
    { q: "Was ist die Schneeball-Methode?",      a: "Den kleinsten Saldo zuerst angreifen. Nach Tilgung die Zahlung auf den nachsten kleinsten ubertragen. Kostet etwas mehr Zinsen, aber schnelle Erfolge motivieren." },
    { q: "Was ist die Hochster-Saldo-Methode?",  a: "Den grossten Saldo zuerst tilgen. Reduziert die grosste Schuld am schnellsten, ist aber nicht immer finanziell optimal." },
    { q: "Was ist die Niedrigster-Zins-Methode?",a: "Die Schuld mit dem niedrigsten Zinssatz zuerst tilgen. Setzt Mindestraten schnell frei, kostet aber am meisten Gesamtzinsen. Generell nicht empfohlen." },
    { q: "Was bedeutet Zahlung ubertragen?",     a: "Wenn eine Schuld abbezahlt ist, wird die Zahlung zur nachsten Schuld addiert. Die monatlichen Gesamtausgaben bleiben gleich -- nur die Richtung andert sich." },
    { q: "Was ist der effektive Jahreszins?",    a: "Die jahrlichen Kreditkosten als Prozentsatz des Saldos. Bei 22% zahlen Sie monatlich ca. 1,83% Zinsen. Niedrigerer Zinssatz bedeutet gunstigere Schulden." },
    { q: "Was ist Schuldenkonsolidierung?",      a: "Ein neuer Kredit zum Ablosen mehrerer Schulden. Wenn der neue Zinssatz niedriger ist, sparen Sie Geld. Risiko: Kreditkarten nach Konsolidierung weiter nutzen kann zu mehr Schulden fuhren." },
    { q: "Wie genau sind diese Berechnungen?",   a: "Setzt feste Zinssatze und gleichbleibende Zahlungen voraus. Tatsachliche Ergebnisse konnen abweichen. Als zuverlassige Orientierung, nicht als Garantie betrachten. Keine Finanzberatung." },
  ],
};

// ─────────────────────────────────────────────
// FRENCH
// ─────────────────────────────────────────────
const fr = {
  ...en,
  months: ["Jan","Fev","Mar","Avr","Mai","Jun","Jul","Aou","Sep","Oct","Nov","Dec"],
  monthsFull: ["Janvier","Fevrier","Mars","Avril","Mai","Juin","Juillet","Aout","Septembre","Octobre","Novembre","Decembre"],
  fmtDuration: (months) => {
    const y = Math.floor(months / 12), m = months % 12;
    if (!y) return `${months} mois`;
    if (!m) return `${y} an${y > 1 ? "s" : ""}`;
    return `${y} an${y > 1 ? "s" : ""} ${m} mois`;
  },
  preparedBy: "Prepare par Weelco Inc.",
  disclaimer: "Ce rapport est fourni a titre informatif uniquement. Weelco Inc. n'est pas un conseiller financier.",
  p1Title: "Votre bilan de dettes",
  whereYouStand: "OU VOUS EN ETES AUJOURD'HUI",
  totalDebt: "DETTE TOTALE",
  acrossAccounts: n => `sur ${n} compte${n !== 1 ? "s" : ""}`,
  minPaymentPerMo: "Paiement minimum/mois",
  avgInterestRate: "Taux d'interet moyen",
  payingMinimumsOnly: "Paiements minimums seuls",
  minimumsWarning: (cost, date) => `Ne payer que les minimums coute ${cost} d'interets et dure jusqu'en ${date}.`,
  withExtraSavings: (extra, saved, time) => `Avec ${extra}/mois en plus vous economisez ${saved} et finissez ${time} plus tot.`,
  recommended: "RECOMMANDE",
  methodSavingsDesc: (desc, saved, worst, extra) => `${desc} · economise ${saved} vs ${worst} a ${extra}/mois`,
  yourScenarios: "VOS SCENARIOS DE PAIEMENT",
  yourDebts: "VOS DETTES",
  conservative: "Conservateur",
  balanced: "Equilibre",
  aggressive: "Agressif",
  saves: amt => `economise ${amt}`,
  p2Title: "Les 4 methodes comparees",
  basedOn: (debt, extra) => `Base sur ${debt} de dettes avec ${extra}/mois en plus`,
  debtFreeIn: "Sans dettes dans",
  totalInterest: "Interets totaux",
  interestVsWorst: "Interets vs pire",
  summary: "Resume",
  recommendedSummary: (method, saved, worst, extra) => `Recommande : ${method} — economise ${saved} vs methode ${worst} a ${extra}/mois en plus.`,
  methodLabels: { avalanche: "Avalanche", snowball: "Boule de neige", highbal: "Solde eleve", lowint: "Taux bas" },
  methodDescs: { avalanche: "Taux le plus eleve d'abord", snowball: "Solde le plus faible d'abord", highbal: "Solde le plus eleve d'abord", lowint: "Taux le plus bas d'abord" },
  methodDescsLong: { avalanche: "Taux le plus eleve d'abord", snowball: "Solde le plus faible d'abord", highbal: "Solde le plus eleve d'abord", lowint: "Taux d'interet le plus bas d'abord" },
  methodDescsP2: {
    avalanche: "Payer le taux le plus eleve en premier. Mathematiquement optimal — economise le plus.",
    snowball:  "Payer le solde le plus faible en premier. Les petites victoires maintiennent la motivation.",
    highbal:   "Cibler la dette la plus elevee en premier. Reduit la plus grande charge rapidement.",
    lowint:    "Payer le taux le plus bas en premier. Libere des liquidites rapidement.",
  },
  p3Title: "Quelle dette attaquer en premier",
  minPerMo: amt => `Min : ${amt}/mois`,
  attackFirst: (reason, extra) => `Attaquer en premier -- ${reason}. Tout ${extra}/mois en plus va ici.`,
  payMinimumsOthers: "Payer les minimums sur toutes les autres dettes.",
  attackSecond: debt => `Attaquer en second. Quand ${debt} est rembourse,`,
  rollPaymentHere: amt => `reporter ses ${amt}/mois ici en plus du minimum.`,
  attackN: (n, prevN) => `Attaquer #${n}. Payer les minimums jusqu'a ce que les ${prevN} dette${prevN > 1 ? "s" : ""} precedente${prevN > 1 ? "s" : ""} soient remboursee${prevN > 1 ? "s" : ""},`,
  rollFreed: "puis reporter tous les paiements liberes ici.",
  reasonHighestRate: rate => `taux le plus eleve a ${rate}%`,
  reasonSmallestBal: bal => `solde le plus faible a ${bal}`,
  reasonLargestBal: bal => `solde le plus eleve a ${bal}`,
  reasonLowestRate: rate => `taux le plus bas a ${rate}%`,
  consistency: {
    avalanche: extra => `La cle de l'Avalanche est la regularite -- ne manquez jamais un minimum et mettez toujours ${extra} en plus sur la dette n°1 jusqu'a extinction.`,
    snowball:  extra => `La cle de la Boule de neige est la regularite -- ne manquez jamais un minimum et mettez toujours ${extra} en plus sur le solde le plus faible.`,
    highbal:   extra => `La cle du Solde eleve est la regularite -- ne manquez jamais un minimum et mettez toujours ${extra} en plus sur le solde le plus eleve.`,
    lowint:    extra => `La cle du Taux bas est la regularite -- ne manquez jamais un minimum et mettez toujours ${extra} en plus sur la dette au taux le plus bas.`,
  },
  p4Title: "Que se passe-t-il si vous payez plus ?",
  marginalAnalysis: (method, sweet) => `Analyse marginale — methode ${method} · point optimal : ${sweet}/mois`,
  scenarioCol: "Scenario",
  debtFreeCol: "Sans dettes",
  totalInterestCol: "Interets totaux",
  totalSavedCol: "Economies",
  marginalGainCol: "Gain marginal",
  minimumsOnlyRow: amt => `${amt}/mois (minimums seuls)`,
  scenarioRow: (amt, label) => `${amt}/mois — ${label}`,
  sweetSpotNote: sweet => `Point optimal : ${sweet}/mois offre le meilleur rendement — chaque euro supplementaire rapporte moins apres ce point.`,
  payAsMuchNote: "Payez autant que possible en extra — chaque euro reduit significativement les interets.",
  aggressiveNote: (saved, extra) => `Payer agressif economise ${saved} de plus mais necessite ${extra}/mois en plus — decidez selon votre budget.`,
  bonusNote: "Si vous recevez une prime ou un remboursement d'impot, mettez-le d'abord sur vos dettes.",
  p5Title: "Calendrier mois par mois",
  methodAllDebts: (method, months) => `Methode ${method} - toutes dettes - ${months} mois au total`,
  monthCol: "Mois",
  focusDebt: "Dette cible",
  paymentCol: "Paiement",
  principalCol: "Capital",
  interestCol: "Interets",
  remainingCol: "Restant",
  paidOffRolling: (names, next) => `${names} REMBOURSE - paiement reporte sur ${next}`,
  paidOff: names => `${names} REMBOURSE`,
  debtFreeRow: date => `SANS DETTES - ${date}`,
  allDebtsNote: (n, date, interest) => `${n} dette${n !== 1 ? "s" : ""} remboursee${n !== 1 ? "s" : ""} avant ${date}. Interets totaux payes : ${interest}.`,
  p6Title: "Analyse de consolidation de dettes",
  isItWorthIt: "Ca vaut le coup pour vous ?",
  aprConsolidationLoan: "TEG pret de consolidation",
  totalInterestLabel: "interets totaux",
  savesAmount: amt => `Economise ${amt}`,
  costsMore: amt => `Coute ${amt} de plus`,
  breakEvenPoint: fee => `Point de rentabilite (dont ${fee} de frais de dossier)`,
  neverBreaksEven: "Jamais rentable - non recommande",
  breakEvenAfterPayoff: "Rentable apres la date de remboursement - peu avantageux",
  breakEvenSaves: (month, amt) => `Rentable au mois ${month} - economise ${amt}/mois apres`,
  breakEvenBarely: month => `Rentable au mois ${month} - peu avantageux`,
  atApr: rate => `A ${rate}% TEG`,
  ourRecommendation: "Notre recommandation",
  consolidationBelowApr: rate => `La consolidation n'est utile qu'en dessous de ${rate}% TEG.`,
  noneConsolidationRates: "Aucun de ces taux ne bat votre plan actuel.",
  debtExpensive: (name, rate) => `Votre ${name} a ${rate}% est couteux -`,
  qualifyConsolidation: "si vous obtenez 10% de consolidation, considerez-le serieusement.",
  stopUsingCard: "Important : n'utilisez plus la carte de credit apres la consolidation.",
  p7Title: "Votre plan d'action + FAQ",
  stepsToFreedom: "3 etapes vers la liberte financiere - des aujourd'hui",
  startingDate: date => `DEBUT ${date}`,
  debtFreeDateLabel: "DATE DE LIBERATION",
  youWillSave: amt => `Vous economiserez ${amt} vs paiements minimums`,
  glossaryFaq: "Glossaire & FAQ",
  step1: (d1, extra, d1combined) => `Maintenant : Configurez le prelevement automatique des minimums sur toutes les dettes sauf ${d1}. Mettez ${extra}/mois en plus + minimum de ${d1} = ${d1combined}/mois sur ${d1}. Ne touchez pas jusqu'au remboursement.`,
  step2: (date, d1, d1combined, d2, d2combined) => `Vers ${date} : ${d1} est rembourse. Reportez les ${d1combined}/mois sur ${d2}. Vous paierez maintenant ${d2combined}/mois sur ${d2}.`,
  step3withD3: (date, d2, totalFreed, d3) => `Vers ${date} : ${d2} est fini. Reportez tout -- ${totalFreed}/mois -- sur ${d3}. Dernier effort. Vous y etes presque.`,
  step3noD3: (date, d2) => `Vers ${date} : ${d2} est fini. Vous etes libre de dettes !`,
  faqs: [
    { q: "Qu'est-ce que la methode Avalanche ?",       a: "Payer les minimums sur toutes les dettes, puis mettre l'extra sur la dette au taux le plus eleve. Economise le plus en interets." },
    { q: "Qu'est-ce que la methode Boule de neige ?",  a: "Attaquer le solde le plus faible en premier. Apres remboursement, reporter le paiement sur le suivant. Coute un peu plus en interets mais les victoires rapides motivent." },
    { q: "Qu'est-ce que la methode Solde eleve ?",     a: "Se concentrer sur le solde le plus eleve. Reduit la plus grosse dette le plus vite mais n'est pas toujours le plus efficace financierement." },
    { q: "Qu'est-ce que la methode Taux bas ?",        a: "Rembourser la dette au taux le plus bas en premier. Libere des minimums rapidement mais coute le plus en interets totaux. Generalement non recommande." },
    { q: "Que signifie reporter un paiement ?",        a: "Quand une dette est remboursee, ajouter ce montant au paiement de la dette suivante. Les depenses mensuelles totales restent identiques -- on redirige juste l'argent." },
    { q: "Qu'est-ce que le TEG ?",                     a: "Le Taux Effectif Global -- le cout annuel du credit en pourcentage du solde. A 22% TEG vous payez environ 1,83% par mois. Un TEG plus bas signifie une dette moins chere." },
    { q: "Qu'est-ce que la consolidation de dettes ?", a: "Un nouveau pret pour rembourser plusieurs dettes. Si le nouveau taux est plus bas que la moyenne actuelle, vous economisez. Risque : continuer a utiliser les cartes apres consolidation peut aggraver la situation." },
    { q: "Quelle est la precision de ces calculs ?",   a: "Suppose des taux fixes et des paiements constants. Les resultats reels peuvent varier. Considerez ceci comme une feuille de route fiable, pas une garantie. Pas de conseil financier." },
  ],
};

// ─────────────────────────────────────────────
// SPANISH
// ─────────────────────────────────────────────
const es = {
  ...en,
  months: ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"],
  monthsFull: ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"],
  fmtDuration: (months) => {
    const y = Math.floor(months / 12), m = months % 12;
    if (!y) return `${months} meses`;
    if (!m) return `${y} a${y > 1 ? "nos" : "no"}`;
    return `${y} a${y > 1 ? "nos" : "no"} ${m} m.`;
  },
  preparedBy: "Preparado por Weelco Inc.",
  disclaimer: "Este informe es solo informativo. Weelco Inc. no es asesor financiero.",
  p1Title: "Tu panorama de deudas",
  whereYouStand: "DONDE ESTAS HOY",
  totalDebt: "DEUDA TOTAL",
  acrossAccounts: n => `en ${n} cuenta${n !== 1 ? "s" : ""}`,
  minPaymentPerMo: "Pago minimo por mes",
  avgInterestRate: "Tasa de interes prom.",
  payingMinimumsOnly: "Solo pagos minimos",
  minimumsWarning: (cost, date) => `Pagar solo minimos cuesta ${cost} en intereses y dura hasta ${date}.`,
  withExtraSavings: (extra, saved, time) => `Con ${extra}/mes extra ahorras ${saved} y terminas ${time} antes.`,
  recommended: "RECOMENDADO",
  methodSavingsDesc: (desc, saved, worst, extra) => `${desc} · ahorra ${saved} vs ${worst} con ${extra}/mes`,
  yourScenarios: "TUS ESCENARIOS DE PAGO",
  yourDebts: "TUS DEUDAS",
  conservative: "Conservador",
  balanced: "Equilibrado",
  aggressive: "Agresivo",
  saves: amt => `ahorra ${amt}`,
  p2Title: "Los 4 metodos comparados",
  basedOn: (debt, extra) => `Basado en ${debt} de deuda con ${extra}/mes extra`,
  debtFreeIn: "Sin deudas en",
  totalInterest: "Interes total",
  interestVsWorst: "Interes vs peor",
  summary: "Resumen",
  recommendedSummary: (method, saved, worst, extra) => `Recomendado: ${method} — ahorra ${saved} vs metodo ${worst} con ${extra}/mes extra.`,
  methodLabels: { avalanche: "Avalancha", snowball: "Bola de nieve", highbal: "Saldo alto", lowint: "Interes bajo" },
  methodDescs: { avalanche: "Mayor tasa primero", snowball: "Menor saldo primero", highbal: "Mayor saldo primero", lowint: "Menor tasa primero" },
  methodDescsLong: { avalanche: "Mayor tasa primero", snowball: "Menor saldo primero", highbal: "Mayor saldo primero", lowint: "Menor tasa de interes primero" },
  methodDescsP2: {
    avalanche: "Pagar la tasa mas alta primero. Optimo matematicamente — ahorra mas dinero.",
    snowball:  "Pagar el saldo mas pequeno primero. Los logros rapidos generan motivacion.",
    highbal:   "Atacar la deuda mas grande primero. Reduce el mayor pasivo mas rapido.",
    lowint:    "Pagar la tasa mas baja primero. Libera flujo de caja rapidamente.",
  },
  p3Title: "Que deuda atacar primero",
  minPerMo: amt => `Min: ${amt}/mes`,
  attackFirst: (reason, extra) => `Atacar primero -- ${reason}. Todo ${extra}/mes extra va aqui.`,
  payMinimumsOthers: "Pagar minimos en todas las demas deudas.",
  attackSecond: debt => `Atacar segundo. Cuando ${debt} este pagado,`,
  rollPaymentHere: amt => `transferir sus ${amt}/mes aqui ademas del minimo.`,
  attackN: (n, prevN) => `Atacar #${n}. Pagar minimos hasta que las ${prevN} deuda${prevN > 1 ? "s" : ""} anteriores esten saldadas,`,
  rollFreed: "luego transferir todos los pagos liberados aqui.",
  reasonHighestRate: rate => `mayor tasa al ${rate}%`,
  reasonSmallestBal: bal => `menor saldo en ${bal}`,
  reasonLargestBal: bal => `mayor saldo en ${bal}`,
  reasonLowestRate: rate => `menor tasa al ${rate}%`,
  consistency: {
    avalanche: extra => `La clave de la Avalancha es la constancia -- nunca omitas un minimo y pon siempre ${extra} extra en la deuda #1 hasta liquidarla.`,
    snowball:  extra => `La clave de la Bola de nieve es la constancia -- nunca omitas un minimo y pon siempre ${extra} extra en el saldo mas pequeno hasta liquidarlo.`,
    highbal:   extra => `La clave del Saldo alto es la constancia -- nunca omitas un minimo y pon siempre ${extra} extra en el mayor saldo hasta liquidarlo.`,
    lowint:    extra => `La clave del Interes bajo es la constancia -- nunca omitas un minimo y pon siempre ${extra} extra en la deuda con menor tasa hasta liquidarla.`,
  },
  p4Title: "?Y si pagas mas?",
  marginalAnalysis: (method, sweet) => `Analisis marginal — metodo ${method} · punto optimo: ${sweet}/mes`,
  scenarioCol: "Escenario",
  debtFreeCol: "Sin deudas",
  totalInterestCol: "Interes total",
  totalSavedCol: "Ahorro total",
  marginalGainCol: "Ganancia marginal",
  minimumsOnlyRow: amt => `${amt}/mes (solo minimos)`,
  scenarioRow: (amt, label) => `${amt}/mes — ${label}`,
  sweetSpotNote: sweet => `Punto optimo: ${sweet}/mes da el mejor retorno — cada peso extra ahorra menos despues de este punto.`,
  payAsMuchNote: "Paga tanto extra como puedas — cada peso reduce significativamente los intereses.",
  aggressiveNote: (saved, extra) => `Pagar agresivo ahorra ${saved} mas pero requiere ${extra}/mes extra — decide segun tu presupuesto.`,
  bonusNote: "Si recibes un bono o devolucion de impuestos, ponlo primero en tus deudas.",
  p5Title: "Plan de pago mes a mes",
  methodAllDebts: (method, months) => `Metodo ${method} - todas las deudas - ${months} meses en total`,
  monthCol: "Mes",
  focusDebt: "Deuda objetivo",
  paymentCol: "Pago",
  principalCol: "Capital",
  interestCol: "Intereses",
  remainingCol: "Restante",
  paidOffRolling: (names, next) => `${names} PAGADA - transfiriendo pago a ${next}`,
  paidOff: names => `${names} PAGADA`,
  debtFreeRow: date => `SIN DEUDAS - ${date}`,
  allDebtsNote: (n, date, interest) => `${n} deuda${n !== 1 ? "s" : ""} pagada${n !== 1 ? "s" : ""} antes de ${date}. Intereses totales pagados: ${interest}.`,
  p6Title: "Analisis de consolidacion de deudas",
  isItWorthIt: "?Vale la pena para ti?",
  aprConsolidationLoan: "TIE prestamo de consolidacion",
  totalInterestLabel: "interes total",
  savesAmount: amt => `Ahorra ${amt}`,
  costsMore: amt => `Cuesta ${amt} mas`,
  breakEvenPoint: fee => `Punto de equilibrio (incluye ${fee} de comision)`,
  neverBreaksEven: "Nunca alcanza equilibrio - no recomendado",
  breakEvenAfterPayoff: "Equilibrio despues del vencimiento - apenas vale la pena",
  breakEvenSaves: (month, amt) => `Equilibrio en mes ${month} - ahorra ${amt}/mes despues`,
  breakEvenBarely: month => `Equilibrio en mes ${month} - apenas vale la pena`,
  atApr: rate => `Al ${rate}% TIE`,
  ourRecommendation: "Nuestra recomendacion",
  consolidationBelowApr: rate => `La consolidacion solo tiene sentido por debajo del ${rate}% TIE.`,
  noneConsolidationRates: "Ninguna de estas tasas supera tu plan de pago actual.",
  debtExpensive: (name, rate) => `Tu ${name} al ${rate}% es cara -`,
  qualifyConsolidation: "si calificas para 10% de consolidacion, consideralo seriamente.",
  stopUsingCard: "Importante: deja de usar la tarjeta de credito despues de consolidar.",
  p7Title: "Tu plan de accion + FAQ",
  stepsToFreedom: "3 pasos hacia la libertad financiera - desde hoy",
  startingDate: date => `INICIO ${date}`,
  debtFreeDateLabel: "FECHA SIN DEUDAS",
  youWillSave: amt => `Ahorraras ${amt} vs pagos minimos`,
  glossaryFaq: "Glosario y FAQ",
  step1: (d1, extra, d1combined) => `Ahora: Configura debito automatico de minimos en todas las deudas excepto ${d1}. Pon ${extra}/mes extra + minimo de ${d1} = ${d1combined}/mes total en ${d1}. No lo toques hasta liquidarla.`,
  step2: (date, d1, d1combined, d2, d2combined) => `Aprox. ${date}: ${d1} esta pagada. Transfiere los ${d1combined}/mes a ${d2}. Ahora pagaras ${d2combined}/mes en ${d2}.`,
  step3withD3: (date, d2, totalFreed, d3) => `Aprox. ${date}: ${d2} esta lista. Transfiere todo -- ${totalFreed}/mes -- a ${d3}. Ultimo esfuerzo. Casi lo logras.`,
  step3noD3: (date, d2) => `Aprox. ${date}: ${d2} esta lista. !Estas libre de deudas!`,
  faqs: [
    { q: "?Que es el metodo Avalancha?",            a: "Pagar minimos en todas las deudas y poner el extra en la de mayor tasa. Una vez pagada, pasar a la siguiente mayor tasa. Ahorra mas en intereses." },
    { q: "?Que es el metodo Bola de nieve?",        a: "Atacar el saldo mas pequeno primero sin importar la tasa. Al pagarlo, transferir ese pago al siguiente. Cuesta un poco mas en intereses pero los logros rapidos motivan." },
    { q: "?Que es el metodo Saldo alto?",           a: "Enfocarse en el mayor saldo primero. Reduce la deuda mas grande mas rapido pero no siempre es lo mas eficiente financieramente." },
    { q: "?Que es el metodo Interes bajo?",         a: "Pagar la deuda con menor tasa primero. Libera minimos mas rapido pero cuesta mas en intereses totales. Generalmente no recomendado." },
    { q: "?Que significa transferir un pago?",      a: "Cuando una deuda esta pagada, ese dinero se agrega al pago de la siguiente deuda. El gasto mensual total sigue igual -- solo se redirige." },
    { q: "?Que es la TIE?",                         a: "Tasa de Interes Efectiva -- el costo anual del credito como porcentaje del saldo. Al 22% TIE pagas aprox. 1,83% mensual en intereses." },
    { q: "?Que es la consolidacion de deudas?",     a: "Un nuevo prestamo para pagar multiples deudas. Si la nueva tasa es menor, ahorras dinero. Riesgo: seguir usando tarjetas despues puede aumentar la deuda." },
    { q: "?Que tan precisos son estos calculos?",   a: "Asume tasas fijas y pagos constantes. Los resultados reales pueden variar. Tratalo como una hoja de ruta confiable, no como una garantia. No es asesoramiento financiero." },
  ],
};

// ─────────────────────────────────────────────
// PORTUGUESE
// ─────────────────────────────────────────────
const pt = {
  ...en,
  months: ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"],
  monthsFull: ["Janeiro","Fevereiro","Marco","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"],
  fmtDuration: (months) => {
    const y = Math.floor(months / 12), m = months % 12;
    if (!y) return `${months} meses`;
    if (!m) return `${y} ano${y > 1 ? "s" : ""}`;
    return `${y} ano${y > 1 ? "s" : ""} ${m} m.`;
  },
  preparedBy: "Preparado por Weelco Inc.",
  disclaimer: "Este relatorio e apenas informativo. A Weelco Inc. nao e consultora financeira.",
  p1Title: "Seu panorama de dividas",
  whereYouStand: "ONDE VOCE ESTA HOJE",
  totalDebt: "DIVIDA TOTAL",
  acrossAccounts: n => `em ${n} conta${n !== 1 ? "s" : ""}`,
  minPaymentPerMo: "Pagamento minimo/mes",
  avgInterestRate: "Taxa de juros media",
  payingMinimumsOnly: "So pagamentos minimos",
  minimumsWarning: (cost, date) => `Pagar so o minimo custa ${cost} em juros e dura ate ${date}.`,
  withExtraSavings: (extra, saved, time) => `Com ${extra}/mes a mais voce economiza ${saved} e termina ${time} antes.`,
  recommended: "RECOMENDADO",
  methodSavingsDesc: (desc, saved, worst, extra) => `${desc} · economiza ${saved} vs ${worst} com ${extra}/mes`,
  yourScenarios: "SEUS CENARIOS DE PAGAMENTO",
  yourDebts: "SUAS DIVIDAS",
  conservative: "Conservador",
  balanced: "Equilibrado",
  aggressive: "Agressivo",
  saves: amt => `economiza ${amt}`,
  p2Title: "Os 4 metodos comparados",
  basedOn: (debt, extra) => `Baseado em ${debt} de divida com ${extra}/mes a mais`,
  debtFreeIn: "Livre de dividas em",
  totalInterest: "Juros totais",
  interestVsWorst: "Juros vs pior",
  summary: "Resumo",
  recommendedSummary: (method, saved, worst, extra) => `Recomendado: ${method} — economiza ${saved} vs metodo ${worst} com ${extra}/mes a mais.`,
  methodLabels: { avalanche: "Avalanche", snowball: "Bola de neve", highbal: "Saldo alto", lowint: "Juros baixos" },
  methodDescs: { avalanche: "Maior taxa primeiro", snowball: "Menor saldo primeiro", highbal: "Maior saldo primeiro", lowint: "Menor taxa primeiro" },
  methodDescsLong: { avalanche: "Maior taxa primeiro", snowball: "Menor saldo primeiro", highbal: "Maior saldo primeiro", lowint: "Menor taxa de juros primeiro" },
  methodDescsP2: {
    avalanche: "Pagar a maior taxa primeiro. Otimo matematicamente — economiza mais.",
    snowball:  "Pagar o menor saldo primeiro. Vitorias rapidas mantem a motivacao.",
    highbal:   "Atacar a maior divida primeiro. Reduz o maior passivo mais rapidamente.",
    lowint:    "Pagar a menor taxa primeiro. Libera fluxo de caixa rapidamente.",
  },
  p3Title: "Qual divida atacar primeiro",
  minPerMo: amt => `Min: ${amt}/mes`,
  attackFirst: (reason, extra) => `Atacar primeiro -- ${reason}. Todo ${extra}/mes extra vai aqui.`,
  payMinimumsOthers: "Pagar minimos em todas as outras dividas.",
  attackSecond: debt => `Atacar segundo. Quando ${debt} for paga,`,
  rollPaymentHere: amt => `transferir seus ${amt}/mes aqui alem do minimo.`,
  attackN: (n, prevN) => `Atacar #${n}. Pagar minimos ate que as ${prevN} divida${prevN > 1 ? "s" : ""} anteriores sejam quitadas,`,
  rollFreed: "depois transferir todos os pagamentos liberados aqui.",
  reasonHighestRate: rate => `maior taxa em ${rate}%`,
  reasonSmallestBal: bal => `menor saldo em ${bal}`,
  reasonLargestBal: bal => `maior saldo em ${bal}`,
  reasonLowestRate: rate => `menor taxa em ${rate}%`,
  consistency: {
    avalanche: extra => `A chave da Avalanche e a consistencia -- nunca perca um minimo e sempre coloque ${extra} extra na divida #1 ate quita-la.`,
    snowball:  extra => `A chave da Bola de neve e a consistencia -- nunca perca um minimo e sempre coloque ${extra} extra no menor saldo ate quita-lo.`,
    highbal:   extra => `A chave do Saldo alto e a consistencia -- nunca perca um minimo e sempre coloque ${extra} extra no maior saldo ate quita-lo.`,
    lowint:    extra => `A chave dos Juros baixos e a consistencia -- nunca perca um minimo e sempre coloque ${extra} extra na divida com menor taxa ate quita-la.`,
  },
  p4Title: "E se voce pagar mais?",
  marginalAnalysis: (method, sweet) => `Analise marginal — metodo ${method} · ponto otimo: ${sweet}/mes`,
  scenarioCol: "Cenario",
  debtFreeCol: "Livre de dividas",
  totalInterestCol: "Juros totais",
  totalSavedCol: "Total economizado",
  marginalGainCol: "Ganho marginal",
  minimumsOnlyRow: amt => `${amt}/mes (so minimos)`,
  scenarioRow: (amt, label) => `${amt}/mes — ${label}`,
  sweetSpotNote: sweet => `Ponto otimo: ${sweet}/mes oferece o melhor retorno — cada real extra economiza menos apos este ponto.`,
  payAsMuchNote: "Pague o maximo extra possivel — cada real reduz significativamente os juros.",
  aggressiveNote: (saved, extra) => `Pagar agressivo economiza ${saved} a mais mas exige ${extra}/mes extra — decida conforme seu orcamento.`,
  bonusNote: "Se receber um bonus ou restituicao de imposto, coloque-o primeiro nas dividas.",
  p5Title: "Plano de pagamento mes a mes",
  methodAllDebts: (method, months) => `Metodo ${method} - todas as dividas - ${months} meses no total`,
  monthCol: "Mes",
  focusDebt: "Divida foco",
  paymentCol: "Pagamento",
  principalCol: "Principal",
  interestCol: "Juros",
  remainingCol: "Restante",
  paidOffRolling: (names, next) => `${names} QUITADA - transferindo pagamento para ${next}`,
  paidOff: names => `${names} QUITADA`,
  debtFreeRow: date => `LIVRE DE DIVIDAS - ${date}`,
  allDebtsNote: (n, date, interest) => `${n} divida${n !== 1 ? "s" : ""} quitada${n !== 1 ? "s" : ""} ate ${date}. Juros totais pagos: ${interest}.`,
  p6Title: "Analise de consolidacao de dividas",
  isItWorthIt: "Vale a pena para voce?",
  aprConsolidationLoan: "CET emprestimo de consolidacao",
  totalInterestLabel: "juros totais",
  savesAmount: amt => `Economiza ${amt}`,
  costsMore: amt => `Custa ${amt} a mais`,
  breakEvenPoint: fee => `Ponto de equilibrio (incluindo ${fee} de taxa)`,
  neverBreaksEven: "Nunca atinge equilibrio - nao recomendado",
  breakEvenAfterPayoff: "Equilibrio apos a data de quitacao - mal vale a pena",
  breakEvenSaves: (month, amt) => `Equilibrio no mes ${month} - economiza ${amt}/mes depois`,
  breakEvenBarely: month => `Equilibrio no mes ${month} - mal vale a pena`,
  atApr: rate => `Em ${rate}% CET`,
  ourRecommendation: "Nossa recomendacao",
  consolidationBelowApr: rate => `A consolidacao so faz sentido abaixo de ${rate}% CET.`,
  noneConsolidationRates: "Nenhuma dessas taxas supera seu plano atual.",
  debtExpensive: (name, rate) => `Seu ${name} a ${rate}% e caro -`,
  qualifyConsolidation: "se voce qualificar para 10% de consolidacao, considere seriamente.",
  stopUsingCard: "Importante: pare de usar o cartao de credito apos consolidar.",
  p7Title: "Seu plano de acao + FAQ",
  stepsToFreedom: "3 passos para a liberdade financeira - comecando hoje",
  startingDate: date => `INICIO ${date}`,
  debtFreeDateLabel: "DATA DE QUITACAO",
  youWillSave: amt => `Voce economizara ${amt} vs pagamentos minimos`,
  glossaryFaq: "Glossario e FAQ",
  step1: (d1, extra, d1combined) => `Agora: Configure debito automatico dos minimos em todas as dividas exceto ${d1}. Coloque ${extra}/mes extra + minimo de ${d1} = ${d1combined}/mes total em ${d1}. Nao mexa ate quitar.`,
  step2: (date, d1, d1combined, d2, d2combined) => `Por volta de ${date}: ${d1} esta quitada. Transfira os ${d1combined}/mes para ${d2}. Agora voce pagara ${d2combined}/mes em ${d2}.`,
  step3withD3: (date, d2, totalFreed, d3) => `Por volta de ${date}: ${d2} esta pronta. Transfira tudo -- ${totalFreed}/mes -- para ${d3}. Ultimo esforco. Voce esta quase la.`,
  step3noD3: (date, d2) => `Por volta de ${date}: ${d2} esta pronta. Voce esta livre de dividas!`,
  faqs: [
    { q: "O que e o metodo Avalanche?",           a: "Pagar minimos em todas as dividas e colocar o extra na de maior taxa. Economiza mais em juros no total." },
    { q: "O que e o metodo Bola de neve?",        a: "Atacar o menor saldo primeiro. Ao quitar, transferir o pagamento para o proximo. Custa um pouco mais em juros mas as vitorias rapidas motivam." },
    { q: "O que e o metodo Saldo alto?",          a: "Focar no maior saldo primeiro. Reduz a maior divida mais rapido mas nem sempre e o mais eficiente financeiramente." },
    { q: "O que e o metodo Juros baixos?",        a: "Quitar a divida com menor taxa primeiro. Libera minimos mais rapido mas custa mais em juros totais. Geralmente nao recomendado." },
    { q: "O que significa transferir um pagamento?", a: "Quando uma divida e quitada, esse dinheiro e adicionado ao pagamento da proxima divida. O gasto mensal total permanece o mesmo -- apenas se redireciona." },
    { q: "O que e CET?",                          a: "Custo Efetivo Total -- o custo anual do credito como percentual do saldo. Em 22% CET voce paga cerca de 1,83% por mes em juros." },
    { q: "O que e consolidacao de dividas?",      a: "Um novo emprestimo para quitar multiplas dividas. Se a nova taxa for menor, voce economiza. Risco: continuar usando cartoes apos consolidar pode aumentar a divida." },
    { q: "Quao precisos sao esses calculos?",     a: "Assume taxas fixas e pagamentos constantes. Resultados reais podem variar. Trate como um roteiro confiavel, nao uma garantia. Nao e aconselhamento financeiro." },
  ],
};

// ─────────────────────────────────────────────
// ITALIAN
// ─────────────────────────────────────────────
const it = {
  ...en,
  months: ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"],
  monthsFull: ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"],
  fmtDuration: (months) => {
    const y = Math.floor(months / 12), m = months % 12;
    if (!y) return `${months} mesi`;
    if (!m) return `${y} anno${y > 1 ? "i" : ""}`;
    return `${y} anno${y > 1 ? "i" : ""} ${m} m.`;
  },
  preparedBy: "Preparato da Weelco Inc.",
  disclaimer: "Questo rapporto e solo informativo. Weelco Inc. non e un consulente finanziario.",
  p1Title: "Il tuo quadro dei debiti",
  whereYouStand: "DOVE SEI OGGI",
  totalDebt: "DEBITO TOTALE",
  acrossAccounts: n => `su ${n} conto${n !== 1 ? "i" : ""}`,
  minPaymentPerMo: "Pagamento minimo/mese",
  avgInterestRate: "Tasso d'interesse medio",
  payingMinimumsOnly: "Solo pagamenti minimi",
  minimumsWarning: (cost, date) => `Pagare solo i minimi costa ${cost} in interessi e dura fino a ${date}.`,
  withExtraSavings: (extra, saved, time) => `Con ${extra}/mese in piu risparmi ${saved} e finisci ${time} prima.`,
  recommended: "CONSIGLIATO",
  methodSavingsDesc: (desc, saved, worst, extra) => `${desc} · risparmia ${saved} vs ${worst} con ${extra}/mese`,
  yourScenarios: "I TUOI SCENARI DI PAGAMENTO",
  yourDebts: "I TUOI DEBITI",
  conservative: "Conservativo",
  balanced: "Equilibrato",
  aggressive: "Aggressivo",
  saves: amt => `risparmia ${amt}`,
  p2Title: "I 4 metodi a confronto",
  basedOn: (debt, extra) => `Basato su ${debt} di debito con ${extra}/mese in piu`,
  debtFreeIn: "Libero dai debiti in",
  totalInterest: "Interessi totali",
  interestVsWorst: "Interessi vs peggiore",
  summary: "Riepilogo",
  recommendedSummary: (method, saved, worst, extra) => `Consigliato: ${method} — risparmia ${saved} vs metodo ${worst} con ${extra}/mese in piu.`,
  methodLabels: { avalanche: "Valanga", snowball: "Palla di neve", highbal: "Saldo alto", lowint: "Tasso basso" },
  methodDescs: { avalanche: "Tasso piu alto prima", snowball: "Saldo piu basso prima", highbal: "Saldo piu alto prima", lowint: "Tasso piu basso prima" },
  methodDescsLong: { avalanche: "Tasso piu alto prima", snowball: "Saldo piu basso prima", highbal: "Saldo piu alto prima", lowint: "Tasso di interesse piu basso prima" },
  methodDescsP2: {
    avalanche: "Pagare il tasso piu alto per primo. Ottimale matematicamente — risparmia di piu.",
    snowball:  "Pagare il saldo piu basso per primo. Le piccole vittorie mantengono la motivazione.",
    highbal:   "Attaccare il debito piu grande per primo. Riduce la passivita maggiore piu velocemente.",
    lowint:    "Pagare il tasso piu basso per primo. Libera liquidita rapidamente.",
  },
  p3Title: "Quale debito attaccare per primo",
  minPerMo: amt => `Min: ${amt}/mese`,
  attackFirst: (reason, extra) => `Attaccare per primo -- ${reason}. Tutti i ${extra}/mese extra vanno qui.`,
  payMinimumsOthers: "Pagare i minimi su tutti gli altri debiti.",
  attackSecond: debt => `Attaccare per secondo. Quando ${debt} e estinto,`,
  rollPaymentHere: amt => `trasferire i suoi ${amt}/mese qui in aggiunta al minimo.`,
  attackN: (n, prevN) => `Attaccare #${n}. Pagare i minimi finche i ${prevN} debito${prevN > 1 ? "i" : ""} sopra non sono estinti,`,
  rollFreed: "poi trasferire tutti i pagamenti liberati qui.",
  reasonHighestRate: rate => `tasso piu alto al ${rate}%`,
  reasonSmallestBal: bal => `saldo piu basso a ${bal}`,
  reasonLargestBal: bal => `saldo piu alto a ${bal}`,
  reasonLowestRate: rate => `tasso piu basso al ${rate}%`,
  consistency: {
    avalanche: extra => `La chiave della Valanga e la costanza -- non mancare mai un minimo e metti sempre ${extra} extra sul debito #1 finche non e estinto.`,
    snowball:  extra => `La chiave della Palla di neve e la costanza -- non mancare mai un minimo e metti sempre ${extra} extra sul saldo piu basso finche non e estinto.`,
    highbal:   extra => `La chiave del Saldo alto e la costanza -- non mancare mai un minimo e metti sempre ${extra} extra sul saldo piu alto finche non e estinto.`,
    lowint:    extra => `La chiave del Tasso basso e la costanza -- non mancare mai un minimo e metti sempre ${extra} extra sul debito a tasso piu basso finche non e estinto.`,
  },
  p4Title: "Cosa succede se paghi di piu?",
  marginalAnalysis: (method, sweet) => `Analisi marginale — metodo ${method} · punto ottimale: ${sweet}/mese`,
  scenarioCol: "Scenario",
  debtFreeCol: "Libero dai debiti",
  totalInterestCol: "Interessi totali",
  totalSavedCol: "Totale risparmiato",
  marginalGainCol: "Guadagno marginale",
  minimumsOnlyRow: amt => `${amt}/mese (solo minimi)`,
  scenarioRow: (amt, label) => `${amt}/mese — ${label}`,
  sweetSpotNote: sweet => `Punto ottimale: ${sweet}/mese offre il miglior rendimento — ogni euro extra risparmia meno dopo questo punto.`,
  payAsMuchNote: "Paga il massimo extra possibile — ogni euro riduce significativamente gli interessi.",
  aggressiveNote: (saved, extra) => `Pagare aggressivo risparmia ${saved} in piu ma richiede ${extra}/mese extra — decidi in base al budget.`,
  bonusNote: "Se ricevi un bonus o un rimborso fiscale, mettilo prima sui debiti.",
  p5Title: "Piano di pagamento mese per mese",
  methodAllDebts: (method, months) => `Metodo ${method} - tutti i debiti - ${months} mesi totali`,
  monthCol: "Mese",
  focusDebt: "Debito focus",
  paymentCol: "Pagamento",
  principalCol: "Capitale",
  interestCol: "Interessi",
  remainingCol: "Residuo",
  paidOffRolling: (names, next) => `${names} ESTINTO - trasferendo pagamento a ${next}`,
  paidOff: names => `${names} ESTINTO`,
  debtFreeRow: date => `LIBERO DAI DEBITI - ${date}`,
  allDebtsNote: (n, date, interest) => `${n} debito${n !== 1 ? "i" : ""} estinto${n !== 1 ? "i" : ""} entro ${date}. Interessi totali pagati: ${interest}.`,
  p6Title: "Analisi del consolidamento debiti",
  isItWorthIt: "Ne vale la pena per te?",
  aprConsolidationLoan: "TAEG prestito di consolidamento",
  totalInterestLabel: "interessi totali",
  savesAmount: amt => `Risparmia ${amt}`,
  costsMore: amt => `Costa ${amt} in piu`,
  breakEvenPoint: fee => `Punto di pareggio (inclusa commissione di ${fee})`,
  neverBreaksEven: "Non raggiunge mai il pareggio - non consigliato",
  breakEvenAfterPayoff: "Pareggio dopo la data di estinzione - appena conveniente",
  breakEvenSaves: (month, amt) => `Pareggio al mese ${month} - risparmia ${amt}/mese dopo`,
  breakEvenBarely: month => `Pareggio al mese ${month} - appena conveniente`,
  atApr: rate => `Al ${rate}% TAEG`,
  ourRecommendation: "La nostra raccomandazione",
  consolidationBelowApr: rate => `Il consolidamento conviene solo sotto il ${rate}% TAEG.`,
  noneConsolidationRates: "Nessuno di questi tassi batte il tuo piano attuale.",
  debtExpensive: (name, rate) => `Il tuo ${name} al ${rate}% e costoso -`,
  qualifyConsolidation: "se ti qualifichi per il 10% di consolidamento, consideralo seriamente.",
  stopUsingCard: "Importante: smetti di usare la carta di credito dopo il consolidamento.",
  p7Title: "Il tuo piano d'azione + FAQ",
  stepsToFreedom: "3 passi verso la liberta finanziaria - da oggi",
  startingDate: date => `INIZIO ${date}`,
  debtFreeDateLabel: "DATA DI ESTINZIONE",
  youWillSave: amt => `Risparmierai ${amt} vs pagamenti minimi`,
  glossaryFaq: "Glossario e FAQ",
  step1: (d1, extra, d1combined) => `Ora: Imposta il pagamento automatico dei minimi su tutti i debiti tranne ${d1}. Metti ${extra}/mese extra + minimo di ${d1} = ${d1combined}/mese totale su ${d1}. Non toccarlo finche non e estinto.`,
  step2: (date, d1, d1combined, d2, d2combined) => `Verso ${date}: ${d1} e estinto. Trasferisci i ${d1combined}/mese su ${d2}. Pagherai ora ${d2combined}/mese su ${d2}.`,
  step3withD3: (date, d2, totalFreed, d3) => `Verso ${date}: ${d2} e finito. Trasferisci tutto -- ${totalFreed}/mese -- su ${d3}. Ultimo sforzo. Ci sei quasi.`,
  step3noD3: (date, d2) => `Verso ${date}: ${d2} e finito. Sei libero dai debiti!`,
  faqs: [
    { q: "Cos'e il metodo Valanga?",              a: "Pagare i minimi su tutti i debiti e mettere l'extra su quello a tasso piu alto. Risparmia di piu in interessi nel tempo." },
    { q: "Cos'e il metodo Palla di neve?",        a: "Attaccare il saldo piu basso per primo. Dopo l'estinzione trasferire il pagamento al successivo. Costa un po' di piu in interessi ma le vittorie rapide motivano." },
    { q: "Cos'e il metodo Saldo alto?",           a: "Concentrarsi sul saldo piu alto per primo. Riduce il debito piu grande piu velocemente ma non sempre e il piu efficiente finanziariamente." },
    { q: "Cos'e il metodo Tasso basso?",          a: "Estinguere il debito a tasso piu basso per primo. Libera i minimi piu rapidamente ma costa di piu in interessi totali. Generalmente non consigliato." },
    { q: "Cosa significa trasferire un pagamento?", a: "Quando un debito e estinto, quel denaro viene aggiunto al pagamento del debito successivo. La spesa mensile totale rimane uguale -- si redirige solo." },
    { q: "Cos'e il TAEG?",                        a: "Tasso Annuo Effettivo Globale -- il costo annuo del credito in percentuale del saldo. Al 22% TAEG paghi circa l'1,83% al mese in interessi." },
    { q: "Cos'e il consolidamento debiti?",       a: "Un nuovo prestito per estinguere piu debiti. Se il nuovo tasso e piu basso, risparmia. Rischio: continuare a usare le carte dopo il consolidamento puo aumentare il debito." },
    { q: "Quanto sono precisi questi calcoli?",   a: "Assume tassi fissi e pagamenti costanti. I risultati reali possono variare. Consideralo come una roadmap affidabile, non una garanzia. Non e una consulenza finanziaria." },
  ],
};

// ─────────────────────────────────────────────
// DUTCH
// ─────────────────────────────────────────────
const nl = {
  ...en,
  months: ["jan","feb","mrt","apr","mei","jun","jul","aug","sep","okt","nov","dec"],
  monthsFull: ["januari","februari","maart","april","mei","juni","juli","augustus","september","oktober","november","december"],
  fmtDuration: (months) => {
    const y = Math.floor(months / 12), m = months % 12;
    if (!y) return `${months} mnd.`;
    if (!m) return `${y} jr.`;
    return `${y} jr. ${m} mnd.`;
  },
  preparedBy: "Opgesteld door Weelco Inc.",
  disclaimer: "Dit rapport is uitsluitend informatief. Weelco Inc. is geen financieel adviseur.",
  p1Title: "Jouw schuldenOverzicht",
  whereYouStand: "JOUW HUIDIGE SITUATIE",
  totalDebt: "TOTALE SCHULD",
  acrossAccounts: n => `over ${n} rekening${n !== 1 ? "en" : ""}`,
  minPaymentPerMo: "Min. betaling per maand",
  avgInterestRate: "Gemiddeld rentepercentage",
  payingMinimumsOnly: "Alleen minimumbetalingen",
  minimumsWarning: (cost, date) => `Alleen het minimum betalen kost ${cost} aan rente en duurt tot ${date}.`,
  withExtraSavings: (extra, saved, time) => `Met ${extra}/mnd. extra bespaar je ${saved} en ben je ${time} eerder klaar.`,
  recommended: "AANBEVOLEN",
  methodSavingsDesc: (desc, saved, worst, extra) => `${desc} · bespaart ${saved} vs ${worst} bij ${extra}/mnd.`,
  yourScenarios: "JOUW BETALINGSSCENARIO'S",
  yourDebts: "JOUW SCHULDEN",
  conservative: "Conservatief",
  balanced: "Evenwichtig",
  aggressive: "Agressief",
  saves: amt => `bespaart ${amt}`,
  p2Title: "Alle 4 methoden vergeleken",
  basedOn: (debt, extra) => `Gebaseerd op ${debt} schuld met ${extra}/mnd. extra`,
  debtFreeIn: "Schuldenvrij in",
  totalInterest: "Totale rente",
  interestVsWorst: "Rente vs slechtste",
  summary: "Samenvatting",
  recommendedSummary: (method, saved, worst, extra) => `Aanbevolen: ${method} — bespaart ${saved} vs ${worst}-methode bij ${extra}/mnd. extra.`,
  methodLabels: { avalanche: "Lawine", snowball: "Sneeuwbal", highbal: "Hoog saldo", lowint: "Lage rente" },
  methodDescs: { avalanche: "Hoogste rente eerst", snowball: "Laagste saldo eerst", highbal: "Hoogste saldo eerst", lowint: "Laagste rente eerst" },
  methodDescsLong: { avalanche: "Hoogste rente eerst", snowball: "Laagste saldo eerst", highbal: "Hoogste saldo eerst", lowint: "Laagste rentepercentage eerst" },
  methodDescsP2: {
    avalanche: "Hoogste rente als eerste betalen. Wiskundig optimaal — bespaart het meest.",
    snowball:  "Laagste saldo als eerste betalen. Snel resultaat houdt motivatie hoog.",
    highbal:   "Grootste schuld als eerste aanpakken. Vermindert de grootste last snelst.",
    lowint:    "Laagste rente als eerste betalen. Maakt snel cashflow vrij.",
  },
  p3Title: "Welke schuld eerst aanpakken",
  minPerMo: amt => `Min: ${amt}/mnd.`,
  attackFirst: (reason, extra) => `Eerst aanpakken -- ${reason}. Al je ${extra}/mnd. extra gaat hierheen.`,
  payMinimumsOthers: "Minimumbetalingen op alle andere schulden.",
  attackSecond: debt => `Als tweede aanpakken. Als ${debt} is afgelost,`,
  rollPaymentHere: amt => `de ${amt}/mnd. hiernaartoe overhevelen bovenop het minimum.`,
  attackN: (n, prevN) => `#${n} aanpakken. Minimumbetalingen tot de ${prevN} schuld${prevN > 1 ? "en" : ""} hierboven zijn afgelost,`,
  rollFreed: "dan alle vrijgekomen betalingen hiernaartoe overhevelen.",
  reasonHighestRate: rate => `hoogste rente op ${rate}%`,
  reasonSmallestBal: bal => `laagste saldo op ${bal}`,
  reasonLargestBal: bal => `hoogste saldo op ${bal}`,
  reasonLowestRate: rate => `laagste rente op ${rate}%`,
  consistency: {
    avalanche: extra => `De sleutel tot Lawine is consequentie -- mis nooit een minimuumbetaling en zet altijd ${extra} extra op schuld #1 tot die is afgelost.`,
    snowball:  extra => `De sleutel tot Sneeuwbal is consequentie -- mis nooit een minimuumbetaling en zet altijd ${extra} extra op het laagste saldo tot dat is afgelost.`,
    highbal:   extra => `De sleutel tot Hoog saldo is consequentie -- mis nooit een minimuumbetaling en zet altijd ${extra} extra op het hoogste saldo tot dat is afgelost.`,
    lowint:    extra => `De sleutel tot Lage rente is consequentie -- mis nooit een minimuumbetaling en zet altijd ${extra} extra op de schuld met de laagste rente.`,
  },
  p4Title: "Wat als je meer betaalt?",
  marginalAnalysis: (method, sweet) => `Marginale analyse — ${method}-methode · sweet spot: ${sweet}/mnd.`,
  scenarioCol: "Scenario",
  debtFreeCol: "Schuldenvrij",
  totalInterestCol: "Totale rente",
  totalSavedCol: "Totaal bespaard",
  marginalGainCol: "Marginale winst",
  minimumsOnlyRow: amt => `${amt}/mnd. (alleen minimums)`,
  scenarioRow: (amt, label) => `${amt}/mnd. — ${label}`,
  sweetSpotNote: sweet => `Sweet spot: ${sweet}/mnd. geeft het beste rendement — elke extra euro bespaart hierna minder.`,
  payAsMuchNote: "Betaal zoveel extra als mogelijk — elke euro vermindert de rente aanzienlijk.",
  aggressiveNote: (saved, extra) => `Agressief betalen bespaart ${saved} meer maar vereist ${extra}/mnd. extra — beslis op basis van je budget.`,
  bonusNote: "Als je een bonus of belastingteruggave ontvangt, stop die dan eerst in je schulden.",
  p5Title: "Maand-voor-maand schema",
  methodAllDebts: (method, months) => `${method}-methode - alle schulden - ${months} maanden totaal`,
  monthCol: "Maand",
  focusDebt: "Focusschuld",
  paymentCol: "Betaling",
  principalCol: "Aflossing",
  interestCol: "Rente",
  remainingCol: "Restant",
  paidOffRolling: (names, next) => `${names} AFGELOST - betaling overhevelen naar ${next}`,
  paidOff: names => `${names} AFGELOST`,
  debtFreeRow: date => `SCHULDENVRIJ - ${date}`,
  allDebtsNote: (n, date, interest) => `Alle ${n} schuld${n !== 1 ? "en" : ""} afgelost voor ${date}. Totale rente betaald: ${interest}.`,
  p6Title: "Schuldconsolidatie-analyse",
  isItWorthIt: "Is het de moeite waard voor jou?",
  aprConsolidationLoan: "JKP consolidatielening",
  totalInterestLabel: "totale rente",
  savesAmount: amt => `Bespaart ${amt}`,
  costsMore: amt => `Kost ${amt} meer`,
  breakEvenPoint: fee => `Break-even punt (inclusief ${fee} afsluitkosten)`,
  neverBreaksEven: "Bereikt nooit break-even - niet aanbevolen",
  breakEvenAfterPayoff: "Break-even na aflossingsdatum - nauwelijks de moeite",
  breakEvenSaves: (month, amt) => `Break-even in maand ${month} - bespaart daarna ${amt}/mnd.`,
  breakEvenBarely: month => `Break-even in maand ${month} - nauwelijks de moeite`,
  atApr: rate => `Bij ${rate}% JKP`,
  ourRecommendation: "Onze aanbeveling",
  consolidationBelowApr: rate => `Consolidatie is alleen zinvol onder ${rate}% JKP.`,
  noneConsolidationRates: "Geen van deze rentepercentages is beter dan je huidige aflossingsplan.",
  debtExpensive: (name, rate) => `Jouw ${name} op ${rate}% is duur -`,
  qualifyConsolidation: "als je in aanmerking komt voor 10% consolidatie, overweeg het serieus.",
  stopUsingCard: "Belangrijk: stop met het gebruik van de creditcard na consolidatie.",
  p7Title: "Jouw actieplan + FAQ",
  stepsToFreedom: "3 stappen naar schuldenVrijheid - vanaf vandaag",
  startingDate: date => `START ${date}`,
  debtFreeDateLabel: "SCHULDENVRIJ DATUM",
  youWillSave: amt => `Je bespaart ${amt} vs alleen minimumbetalingen`,
  glossaryFaq: "Begrippenlijst & FAQ",
  step1: (d1, extra, d1combined) => `Nu: Stel automatische minimumbetalingen in op alle schulden behalve ${d1}. Zet al je ${extra}/mnd. extra + ${d1} minimum = ${d1combined}/mnd. totaal op ${d1}. Niet aanraken tot het is afgelost.`,
  step2: (date, d1, d1combined, d2, d2combined) => `Rond ${date}: ${d1} is afgelost. Hevel de ${d1combined}/mnd. over naar ${d2}. Je betaalt nu ${d2combined}/mnd. op ${d2}.`,
  step3withD3: (date, d2, totalFreed, d3) => `Rond ${date}: ${d2} is klaar. Hevel alles -- ${totalFreed}/mnd. -- over naar ${d3}. Laatste stap. Je bent er bijna.`,
  step3noD3: (date, d2) => `Rond ${date}: ${d2} is klaar. Je bent schuldenvrij!`,
  faqs: [
    { q: "Wat is de Lawine-methode?",             a: "Minimumbetalingen op alle schulden, dan alles extra naar de hoogste rente. Bespaart het meest aan rente in de loop der tijd." },
    { q: "Wat is de Sneeuwbal-methode?",          a: "Het laagste saldo als eerste aanpakken. Na aflossing die betaling naar de volgende overhevelen. Kost iets meer aan rente maar snelle overwinningen motiveren." },
    { q: "Wat is de Hoog saldo-methode?",         a: "Focussen op het hoogste saldo eerst. Vermindert de grootste schuld het snelst maar is niet altijd financieel het meest efficiënt." },
    { q: "Wat is de Lage rente-methode?",         a: "De schuld met de laagste rente als eerste aflossen. Maakt minimumbetalingen het snelst vrij maar kost het meest aan totale rente. Doorgaans niet aanbevolen." },
    { q: "Wat betekent betaling overhevelen?",    a: "Als een schuld is afgelost, wordt dat bedrag toegevoegd aan de volgende schuldbetaling. De totale maandelijkse uitgaven blijven gelijk -- je leidt ze alleen anders." },
    { q: "Wat is JKP?",                           a: "Jaarlijks Kostenpercentage -- de jaarlijkse kredietkosten als percentage van het saldo. Bij 22% JKP betaal je circa 1,83% per maand aan rente." },
    { q: "Wat is schuldconsolidatie?",            a: "Een nieuwe lening om meerdere schulden af te lossen. Als de nieuwe rente lager is, bespaar je geld. Risico: creditcards blijven gebruiken na consolidatie kan tot meer schuld leiden." },
    { q: "Hoe nauwkeurig zijn deze berekeningen?", a: "Gaat uit van vaste rentes en consistente betalingen. Werkelijke resultaten kunnen afwijken. Beschouw dit als een betrouwbare leidraad, geen garantie. Geen financieel advies." },
  ],
};

// ─────────────────────────────────────────────
// SWEDISH
// ─────────────────────────────────────────────
const sv = {
  ...en,
  months: ["jan","feb","mar","apr","maj","jun","jul","aug","sep","okt","nov","dec"],
  monthsFull: ["januari","februari","mars","april","maj","juni","juli","augusti","september","oktober","november","december"],
  fmtDuration: (months) => {
    const y = Math.floor(months / 12), m = months % 12;
    if (!y) return `${months} man.`;
    if (!m) return `${y} ar`;
    return `${y} ar ${m} man.`;
  },
  preparedBy: "Utarbetad av Weelco Inc.",
  disclaimer: "Denna rapport ar endast informativ. Weelco Inc. ar inte en finansiell radgivare.",
  p1Title: "Din skuld oversikt",
  whereYouStand: "DIN NUVARANDE SITUATION",
  totalDebt: "TOTAL SKULD",
  acrossAccounts: n => `pa ${n} konto${n !== 1 ? "n" : ""}`,
  minPaymentPerMo: "Minbetalning per manad",
  avgInterestRate: "Genomsnittlig rantefot",
  payingMinimumsOnly: "Bara minbetalningar",
  minimumsWarning: (cost, date) => `Att bara betala minimibelopp kostar ${cost} i ranta och tar till ${date}.`,
  withExtraSavings: (extra, saved, time) => `Med ${extra}/man. extra sparar du ${saved} och slutar ${time} tidigare.`,
  recommended: "REKOMMENDERAT",
  methodSavingsDesc: (desc, saved, worst, extra) => `${desc} · sparar ${saved} vs ${worst} med ${extra}/man.`,
  yourScenarios: "DINA BETALNINGSSCENARIER",
  yourDebts: "DINA SKULDER",
  conservative: "Konservativ",
  balanced: "Balanserad",
  aggressive: "Aggressiv",
  saves: amt => `sparar ${amt}`,
  p2Title: "Alla 4 metoder jamforda",
  basedOn: (debt, extra) => `Baserat pa ${debt} skuld med ${extra}/man. extra`,
  debtFreeIn: "Skuldfri om",
  totalInterest: "Total ranta",
  interestVsWorst: "Ranta vs samsta",
  summary: "Sammanfattning",
  recommendedSummary: (method, saved, worst, extra) => `Rekommenderat: ${method} — sparar ${saved} vs ${worst}-metoden med ${extra}/man. extra.`,
  methodLabels: { avalanche: "Lavinen", snowball: "Snobollen", highbal: "Hog saldo", lowint: "Lag ranta" },
  methodDescs: { avalanche: "Hogst ranta forst", snowball: "Lagst saldo forst", highbal: "Hogst saldo forst", lowint: "Lagst ranta forst" },
  methodDescsLong: { avalanche: "Hogst ranta forst", snowball: "Lagst saldo forst", highbal: "Hogst saldo forst", lowint: "Lagst rantefot forst" },
  methodDescsP2: {
    avalanche: "Betala hogst ranta forst. Matematiskt optimalt — sparar mest pengar.",
    snowball:  "Betala lagst saldo forst. Snabba segrar haller motivationen uppe.",
    highbal:   "Attackera storsta skulden forst. Minskar det storsta atagardet snabbast.",
    lowint:    "Betala lagst ranta forst. Frigors kassaflodet snabbt.",
  },
  p3Title: "Vilken skuld attackera forst",
  minPerMo: amt => `Min: ${amt}/man.`,
  attackFirst: (reason, extra) => `Attackera forst -- ${reason}. Alla ${extra}/man. extra gar hit.`,
  payMinimumsOthers: "Betala minimumbelopp pa alla andra skulder.",
  attackSecond: debt => `Attackera andrat. Nar ${debt} ar betalad,`,
  rollPaymentHere: amt => `flytta dess ${amt}/man. hit utover minimumet.`,
  attackN: (n, prevN) => `Attackera #${n}. Betala minimumbelopp tills de ${prevN} skuld${prevN > 1 ? "erna" : "en"} ovan ar betalda,`,
  rollFreed: "flytta sedan alla frigjorda betalningar hit.",
  reasonHighestRate: rate => `hogst ranta pa ${rate}%`,
  reasonSmallestBal: bal => `lagst saldo pa ${bal}`,
  reasonLargestBal: bal => `hogst saldo pa ${bal}`,
  reasonLowestRate: rate => `lagst ranta pa ${rate}%`,
  consistency: {
    avalanche: extra => `Nyckeln till Lavinen ar konsekvens -- missa aldrig ett minimibelopp och lagger alltid ${extra} extra pa skuld #1 tills den ar betald.`,
    snowball:  extra => `Nyckeln till Snobollen ar konsekvens -- missa aldrig ett minimibelopp och lagger alltid ${extra} extra pa lagst saldo tills det ar betalt.`,
    highbal:   extra => `Nyckeln till Hog saldo ar konsekvens -- missa aldrig ett minimibelopp och lagger alltid ${extra} extra pa hogst saldo tills det ar betalt.`,
    lowint:    extra => `Nyckeln till Lag ranta ar konsekvens -- missa aldrig ett minimibelopp och lagger alltid ${extra} extra pa skulden med lagst ranta.`,
  },
  p4Title: "Vad hander om du betalar mer?",
  marginalAnalysis: (method, sweet) => `Marginalanalys — ${method}-metoden · sweet spot: ${sweet}/man.`,
  scenarioCol: "Scenario",
  debtFreeCol: "Skuldfri",
  totalInterestCol: "Total ranta",
  totalSavedCol: "Totalt sparat",
  marginalGainCol: "Marginalvinst",
  minimumsOnlyRow: amt => `${amt}/man. (bara minimum)`,
  scenarioRow: (amt, label) => `${amt}/man. — ${label}`,
  sweetSpotNote: sweet => `Sweet spot: ${sweet}/man. ger bast avkastning — varje extra krona sparar mindre efter detta punkt.`,
  payAsMuchNote: "Betala sa mycket extra som mojligt — varje krona minskar rantan avsebart.",
  aggressiveNote: (saved, extra) => `Aggressiv betalning sparar ${saved} mer men kraver ${extra}/man. extra — bestam utifran din budget.`,
  bonusNote: "Om du far en bonus eller skatteaterbetalning, satt den forst pa skulder.",
  p5Title: "Manad-for-manad schema",
  methodAllDebts: (method, months) => `${method}-metoden - alla skulder - ${months} manader totalt`,
  monthCol: "Manad",
  focusDebt: "Fokusskueld",
  paymentCol: "Betalning",
  principalCol: "Amortering",
  interestCol: "Ranta",
  remainingCol: "Aterstaende",
  paidOffRolling: (names, next) => `${names} BETALD - flytta betalning till ${next}`,
  paidOff: names => `${names} BETALD`,
  debtFreeRow: date => `SKULDFRI - ${date}`,
  allDebtsNote: (n, date, interest) => `Alla ${n} skuld${n !== 1 ? "er" : ""} betald${n !== 1 ? "a" : ""} fore ${date}. Total ranta betald: ${interest}.`,
  p6Title: "Skuldkonsolideringsanalys",
  isItWorthIt: "Ar det vart det for dig?",
  aprConsolidationLoan: "eff. ranta konsolideringslat",
  totalInterestLabel: "total ranta",
  savesAmount: amt => `Sparar ${amt}`,
  costsMore: amt => `Kostar ${amt} mer`,
  breakEvenPoint: fee => `Break-even punkt (inklusive ${fee} upplargsavgift)`,
  neverBreaksEven: "Nar aldrig break-even - rekommenderas ej",
  breakEvenAfterPayoff: "Break-even efter aterbetalningsdatum - knappt vart",
  breakEvenSaves: (month, amt) => `Break-even vid manad ${month} - sparar ${amt}/man. efter`,
  breakEvenBarely: month => `Break-even vid manad ${month} - knappt vart`,
  atApr: rate => `Vid ${rate}% eff. ranta`,
  ourRecommendation: "Var rekommendation",
  consolidationBelowApr: rate => `Konsolidering ar meningsfullt bara under ${rate}% eff. ranta.`,
  noneConsolidationRates: "Ingen av dessa rantor slar ditt nuvarende aterbetalningsplan.",
  debtExpensive: (name, rate) => `Din ${name} pa ${rate}% ar dyr -`,
  qualifyConsolidation: "om du kvalificerar for 10% konsolidering, overvaeg det seriost.",
  stopUsingCard: "Viktigt: sluta anvanda kreditkortet efter konsolidering.",
  p7Title: "Din handlingsplan + FAQ",
  stepsToFreedom: "3 steg till skuldfrihet - borjar idag",
  startingDate: date => `START ${date}`,
  debtFreeDateLabel: "SKULDFRITT DATUM",
  youWillSave: amt => `Du sparar ${amt} vs bara minbetalningar`,
  glossaryFaq: "Ordlista & FAQ",
  step1: (d1, extra, d1combined) => `Nu: Satt upp autobetalning av minimumbelopp pa alla skulder utom ${d1}. Lagg alla ${extra}/man. extra + ${d1} minimum = ${d1combined}/man. totalt pa ${d1}. Rora det inte tills det ar betalt.`,
  step2: (date, d1, d1combined, d2, d2combined) => `Runt ${date}: ${d1} ar betald. Flytta de ${d1combined}/man. till ${d2}. Du betalar nu ${d2combined}/man. pa ${d2}.`,
  step3withD3: (date, d2, totalFreed, d3) => `Runt ${date}: ${d2} ar klar. Flytta allt -- ${totalFreed}/man. -- till ${d3}. Sista pushen. Du ar nastan framme.`,
  step3noD3: (date, d2) => `Runt ${date}: ${d2} ar klar. Du ar skuldfri!`,
  faqs: [
    { q: "Vad ar Lavinen-metoden?",               a: "Betala minimumbelopp pa alla skulder och lagg sedan det extra pa den med hogst ranta. Sparar mest i ranta over tid." },
    { q: "Vad ar Snobolls-metoden?",              a: "Attackera det lagsta saldot forst. Nar det ar betalt, flytta betalningen till nasta. Kostar lite mer i ranta men snabba segrar motiverar." },
    { q: "Vad ar Hog saldo-metoden?",             a: "Fokusera pa det hogsta saldot forst. Minskar den storsta skulden snabbast men ar inte alltid finansiellt effektivast." },
    { q: "Vad ar Lag ranta-metoden?",             a: "Betala skulden med lagst ranta forst. Frigors minimumbetalningarna snabbast men kostar mest i total ranta. Generellt ej rekommenderat." },
    { q: "Vad betyder flytta en betalning?",      a: "Nar en skuld ar betald laggs det beloppet till nasta skuldbetalning. Totala manadliga utgifter forblir desamma -- du omdirigerar dem bara." },
    { q: "Vad ar effektiv ranta?",                a: "Den arliga kreditkostnaden som procent av saldot. Vid 22% eff. ranta betalar du ca 1,83% per manad i ranta." },
    { q: "Vad ar skuldkonsolidering?",            a: "Ett nytt lan for att betala av flera skulder. Om den nya rantan ar lagre sparar du pengar. Risk: att fortsatta anvanda kreditkort efter konsolidering kan oka skulden." },
    { q: "Hur noggranna ar dessa berakningar?",   a: "Forutsatter fasta rantor och konsekventa betalningar. Faktiska resultat kan variera. Betrakta detta som en tillforlitlig vagledning, inte en garanti. Inte finansiell radgivning." },
  ],
};

// ─────────────────────────────────────────────
// NORWEGIAN (Bokmål)
// ─────────────────────────────────────────────
const nb = {
  ...en,
  months: ["jan","feb","mar","apr","mai","jun","jul","aug","sep","okt","nov","des"],
  monthsFull: ["januar","februar","mars","april","mai","juni","juli","august","september","oktober","november","desember"],
  fmtDuration: (months) => {
    const y = Math.floor(months / 12), m = months % 12;
    if (!y) return `${months} mnd.`;
    if (!m) return `${y} ar`;
    return `${y} ar ${m} mnd.`;
  },
  preparedBy: "Utarbeidet av Weelco Inc.",
  disclaimer: "Denne rapporten er kun informativ. Weelco Inc. er ikke en finansiell radgiver.",
  p1Title: "Din gjeldsoversikt",
  whereYouStand: "DIN SITUASJON I DAG",
  totalDebt: "TOTAL GJELD",
  acrossAccounts: n => `pa ${n} konto${n !== 1 ? "er" : ""}`,
  minPaymentPerMo: "Minimumsbetaling per maned",
  avgInterestRate: "Gjennomsnittlig rente",
  payingMinimumsOnly: "Bare minimumsbetalinger",
  minimumsWarning: (cost, date) => `A bare betale minimumsbelop koster ${cost} i renter og varer til ${date}.`,
  withExtraSavings: (extra, saved, time) => `Med ${extra}/mnd. ekstra sparer du ${saved} og er ferdig ${time} tidligere.`,
  recommended: "ANBEFALT",
  methodSavingsDesc: (desc, saved, worst, extra) => `${desc} · sparer ${saved} vs ${worst} med ${extra}/mnd.`,
  yourScenarios: "DINE BETALINGSSCENARIER",
  yourDebts: "DIN GJELD",
  conservative: "Konservativ",
  balanced: "Balansert",
  aggressive: "Aggressiv",
  saves: amt => `sparer ${amt}`,
  p2Title: "Alle 4 metoder sammenlignet",
  basedOn: (debt, extra) => `Basert pa ${debt} gjeld med ${extra}/mnd. ekstra`,
  debtFreeIn: "Gjeldfri om",
  totalInterest: "Total rente",
  interestVsWorst: "Rente vs darligste",
  summary: "Oppsummering",
  recommendedSummary: (method, saved, worst, extra) => `Anbefalt: ${method} — sparer ${saved} vs ${worst}-metoden med ${extra}/mnd. ekstra.`,
  methodLabels: { avalanche: "Lavinen", snowball: "Snoballen", highbal: "Hoy saldo", lowint: "Lav rente" },
  methodDescs: { avalanche: "Hoyest rente forst", snowball: "Lavest saldo forst", highbal: "Hoyest saldo forst", lowint: "Lavest rente forst" },
  methodDescsLong: { avalanche: "Hoyest rente forst", snowball: "Lavest saldo forst", highbal: "Hoyest saldo forst", lowint: "Lavest rentesats forst" },
  methodDescsP2: {
    avalanche: "Betale hoyest rente forst. Matematisk optimalt — sparer mest penger.",
    snowball:  "Betale lavest saldo forst. Raske seire holder motivasjonen oppe.",
    highbal:   "Angripe storste gjelden forst. Reduserer den storste forpliktelsen raskest.",
    lowint:    "Betale lavest rente forst. Frigjoer kontantstromme raskt.",
  },
  p3Title: "Hvilken gjeld angripe forst",
  minPerMo: amt => `Min: ${amt}/mnd.`,
  attackFirst: (reason, extra) => `Angripe forst -- ${reason}. All ${extra}/mnd. ekstra gar hit.`,
  payMinimumsOthers: "Betale minimumsbetaling pa all annen gjeld.",
  attackSecond: debt => `Angripe andret. Nar ${debt} er betalt,`,
  rollPaymentHere: amt => `flytte dens ${amt}/mnd. hit i tillegg til minimumet.`,
  attackN: (n, prevN) => `Angripe #${n}. Betale minimumsbetaling til de ${prevN} gjeld${prevN > 1 ? "ene" : "en"} ovenfor er betalt,`,
  rollFreed: "flytte deretter alle frigjorte betalinger hit.",
  reasonHighestRate: rate => `hoyest rente pa ${rate}%`,
  reasonSmallestBal: bal => `lavest saldo pa ${bal}`,
  reasonLargestBal: bal => `hoyest saldo pa ${bal}`,
  reasonLowestRate: rate => `lavest rente pa ${rate}%`,
  consistency: {
    avalanche: extra => `Nokelen til Lavinen er konsekvens -- glem aldri et minimumsbelop og legg alltid ${extra} ekstra pa gjeld #1 til den er betalt.`,
    snowball:  extra => `Nokelen til Snoballen er konsekvens -- glem aldri et minimumsbelop og legg alltid ${extra} ekstra pa lavest saldo til den er betalt.`,
    highbal:   extra => `Nokelen til Hoy saldo er konsekvens -- glem aldri et minimumsbelop og legg alltid ${extra} ekstra pa hoyest saldo til den er betalt.`,
    lowint:    extra => `Nokelen til Lav rente er konsekvens -- glem aldri et minimumsbelop og legg alltid ${extra} ekstra pa gjelden med lavest rente.`,
  },
  p4Title: "Hva om du betaler mer?",
  marginalAnalysis: (method, sweet) => `Marginalanalyse — ${method}-metoden · sweet spot: ${sweet}/mnd.`,
  scenarioCol: "Scenario",
  debtFreeCol: "Gjeldfri",
  totalInterestCol: "Total rente",
  totalSavedCol: "Totalt spart",
  marginalGainCol: "Marginalgevinst",
  minimumsOnlyRow: amt => `${amt}/mnd. (bare minimum)`,
  scenarioRow: (amt, label) => `${amt}/mnd. — ${label}`,
  sweetSpotNote: sweet => `Sweet spot: ${sweet}/mnd. gir best avkastning — hver ekstra krone sparer mindre etter dette punktet.`,
  payAsMuchNote: "Betal sa mye ekstra som mulig — hver krone reduserer renten betydelig.",
  aggressiveNote: (saved, extra) => `Aggressiv betaling sparer ${saved} mer men krever ${extra}/mnd. ekstra — bestem ut fra budsjettet ditt.`,
  bonusNote: "Hvis du mottar en bonus eller skatterefusjon, sett den forst pa gjeld.",
  p5Title: "Maned-for-maned plan",
  methodAllDebts: (method, months) => `${method}-metoden - all gjeld - ${months} maneder totalt`,
  monthCol: "Maned",
  focusDebt: "Fokusgjeld",
  paymentCol: "Betaling",
  principalCol: "Avdrag",
  interestCol: "Rente",
  remainingCol: "Gjenstaende",
  paidOffRolling: (names, next) => `${names} BETALT - flytte betaling til ${next}`,
  paidOff: names => `${names} BETALT`,
  debtFreeRow: date => `GJELDFRI - ${date}`,
  allDebtsNote: (n, date, interest) => `Alle ${n} gjeld betalt for ${date}. Total rente betalt: ${interest}.`,
  p6Title: "Gjeldskonsolideringsanalyse",
  isItWorthIt: "Er det verdt det for deg?",
  aprConsolidationLoan: "eff. rente konsolideringslat",
  totalInterestLabel: "total rente",
  savesAmount: amt => `Sparer ${amt}`,
  costsMore: amt => `Koster ${amt} mer`,
  breakEvenPoint: fee => `Break-even punkt (inkludert ${fee} etableringsgebyr)`,
  neverBreaksEven: "Nar aldri break-even - anbefales ikke",
  breakEvenAfterPayoff: "Break-even etter tilbakebetalingsdato - knapt verd",
  breakEvenSaves: (month, amt) => `Break-even ved maned ${month} - sparer ${amt}/mnd. etter`,
  breakEvenBarely: month => `Break-even ved maned ${month} - knapt verd`,
  atApr: rate => `Ved ${rate}% eff. rente`,
  ourRecommendation: "Var anbefaling",
  consolidationBelowApr: rate => `Konsolidering er kun fornuftig under ${rate}% eff. rente.`,
  noneConsolidationRates: "Ingen av disse rentene slar din navarende tilbakebetalingsplan.",
  debtExpensive: (name, rate) => `Din ${name} pa ${rate}% er dyr -`,
  qualifyConsolidation: "hvis du kvalifiserer for 10% konsolidering, vurder det seriost.",
  stopUsingCard: "Viktig: slutt a bruke kredittkort etter konsolidering.",
  p7Title: "Din handlingsplan + FAQ",
  stepsToFreedom: "3 skritt mot gjeldfrihet - fra i dag",
  startingDate: date => `START ${date}`,
  debtFreeDateLabel: "GJELDFRI DATO",
  youWillSave: amt => `Du sparer ${amt} vs bare minimumsbetalinger`,
  glossaryFaq: "Ordliste & FAQ",
  step1: (d1, extra, d1combined) => `Na: Sett opp automatisk minimumsbetaling pa all gjeld unntatt ${d1}. Legg all ${extra}/mnd. ekstra + ${d1} minimum = ${d1combined}/mnd. totalt pa ${d1}. Ikke ror det for det er betalt.`,
  step2: (date, d1, d1combined, d2, d2combined) => `Rundt ${date}: ${d1} er betalt. Flytt de ${d1combined}/mnd. til ${d2}. Du betaler na ${d2combined}/mnd. pa ${d2}.`,
  step3withD3: (date, d2, totalFreed, d3) => `Rundt ${date}: ${d2} er ferdig. Flytt alt -- ${totalFreed}/mnd. -- til ${d3}. Siste innspurt. Du er nesten der.`,
  step3noD3: (date, d2) => `Rundt ${date}: ${d2} er ferdig. Du er gjeldfri!`,
  faqs: [
    { q: "Hva er Lavinen-metoden?",               a: "Betale minimumsbetaling pa all gjeld og deretter legge ekstra pa den med hoyest rente. Sparer mest i rente over tid." },
    { q: "Hva er Snoballe-metoden?",              a: "Angripe lavest saldo forst. Nar betalt, flytte betalingen til neste. Koster litt mer i rente men raske seire motiverer." },
    { q: "Hva er Hoy saldo-metoden?",             a: "Fokusere pa hoyest saldo forst. Reduserer storste gjeld raskest men er ikke alltid finansielt mest effektivt." },
    { q: "Hva er Lav rente-metoden?",             a: "Betale gjelden med lavest rente forst. Frigjoer minimumsbetaling raskest men koster mest i total rente. Generelt ikke anbefalt." },
    { q: "Hva betyr a flytte en betaling?",       a: "Nar en gjeld er betalt legges det belop til neste gjeldbetaling. Total manedlig utgift forblir den samme -- du omdirigerer bare." },
    { q: "Hva er effektiv rente?",                a: "Den arlige kredittkostnaden som prosent av saldoen. Ved 22% eff. rente betaler du ca. 1,83% per maned i rente." },
    { q: "Hva er gjeldskonsolidering?",           a: "Et nytt lan for a betale ned flere gjeld. Hvis ny rente er lavere sparer du penger. Risiko: fortsette a bruke kredittkort etter konsolidering kan oke gjelden." },
    { q: "Hvor noyaktige er disse beregningene?", a: "Forutsetter faste renter og konsekvente betalinger. Faktiske resultater kan variere. Betrakt dette som en palitelig veikart, ikke en garanti. Ikke finansiell radgivning." },
  ],
};

// ─────────────────────────────────────────────
// DANISH
// ─────────────────────────────────────────────
const da = {
  ...en,
  months: ["jan","feb","mar","apr","maj","jun","jul","aug","sep","okt","nov","dec"],
  monthsFull: ["januar","februar","marts","april","maj","juni","juli","august","september","oktober","november","december"],
  fmtDuration: (months) => {
    const y = Math.floor(months / 12), m = months % 12;
    if (!y) return `${months} mdr.`;
    if (!m) return `${y} ar`;
    return `${y} ar ${m} mdr.`;
  },
  preparedBy: "Udarbejdet af Weelco Inc.",
  disclaimer: "Denne rapport er kun informativ. Weelco Inc. er ikke en finansiel radgiver.",
  p1Title: "Dit galdsoverblik",
  whereYouStand: "DIN SITUATION I DAG",
  totalDebt: "SAMLET GALD",
  acrossAccounts: n => `pa ${n} konto${n !== 1 ? "er" : ""}`,
  minPaymentPerMo: "Min. betaling pr. maned",
  avgInterestRate: "Gennemsnitlig rentesats",
  payingMinimumsOnly: "Kun minimumsbetalinger",
  minimumsWarning: (cost, date) => `At kun betale minimum koster ${cost} i renter og varer til ${date}.`,
  withExtraSavings: (extra, saved, time) => `Med ${extra}/mdr. ekstra sparer du ${saved} og er faerdig ${time} tidligere.`,
  recommended: "ANBEFALET",
  methodSavingsDesc: (desc, saved, worst, extra) => `${desc} · sparer ${saved} vs ${worst} med ${extra}/mdr.`,
  yourScenarios: "DINE BETALINGSSCENARIER",
  yourDebts: "DIN GALD",
  conservative: "Konservativ",
  balanced: "Afbalanceret",
  aggressive: "Aggressiv",
  saves: amt => `sparer ${amt}`,
  p2Title: "Alle 4 metoder sammenlignet",
  basedOn: (debt, extra) => `Baseret pa ${debt} gald med ${extra}/mdr. ekstra`,
  debtFreeIn: "Galdfri om",
  totalInterest: "Samlet rente",
  interestVsWorst: "Rente vs darligste",
  summary: "Resume",
  recommendedSummary: (method, saved, worst, extra) => `Anbefalet: ${method} — sparer ${saved} vs ${worst}-metoden med ${extra}/mdr. ekstra.`,
  methodLabels: { avalanche: "Lavinen", snowball: "Snebold", highbal: "Hoj saldo", lowint: "Lav rente" },
  methodDescs: { avalanche: "Hojest rente forst", snowball: "Lavest saldo forst", highbal: "Hojest saldo forst", lowint: "Lavest rente forst" },
  methodDescsLong: { avalanche: "Hojest rente forst", snowball: "Lavest saldo forst", highbal: "Hojest saldo forst", lowint: "Lavest rentesats forst" },
  methodDescsP2: {
    avalanche: "Betale hojest rente forst. Matematisk optimalt — sparer mest penge.",
    snowball:  "Betale lavest saldo forst. Hurtige sejre holder motivationen oppe.",
    highbal:   "Angribe storste gald forst. Reducerer den storste forpligtelse hurtigst.",
    lowint:    "Betale lavest rente forst. Frigiver likviditet hurtigt.",
  },
  p3Title: "Hvilken gald angribe forst",
  minPerMo: amt => `Min: ${amt}/mdr.`,
  attackFirst: (reason, extra) => `Angribe forst -- ${reason}. Al din ${extra}/mdr. ekstra gar hertil.`,
  payMinimumsOthers: "Betale minimumsbetaling pa al anden gald.",
  attackSecond: debt => `Angribe andenpladsen. Nar ${debt} er betalt,`,
  rollPaymentHere: amt => `flytte dens ${amt}/mdr. hertil oven pa minimumsbetalingen.`,
  attackN: (n, prevN) => `Angribe #${n}. Betale minimumsbetaling til de ${prevN} gald${prevN > 1 ? "sposter" : "spost"} ovenfor er betalt,`,
  rollFreed: "flytte derefter alle frigjorte betalinger hertil.",
  reasonHighestRate: rate => `hojest rente pa ${rate}%`,
  reasonSmallestBal: bal => `lavest saldo pa ${bal}`,
  reasonLargestBal: bal => `hojest saldo pa ${bal}`,
  reasonLowestRate: rate => `lavest rente pa ${rate}%`,
  consistency: {
    avalanche: extra => `Noglen til Lavinen er konsekvens -- glem aldrig en minimumsbetaling og laeg altid ${extra} ekstra pa gald #1 indtil den er betalt.`,
    snowball:  extra => `Noglen til Snebold er konsekvens -- glem aldrig en minimumsbetaling og laeg altid ${extra} ekstra pa lavest saldo indtil den er betalt.`,
    highbal:   extra => `Noglen til Hoj saldo er konsekvens -- glem aldrig en minimumsbetaling og laeg altid ${extra} ekstra pa hojest saldo indtil den er betalt.`,
    lowint:    extra => `Noglen til Lav rente er konsekvens -- glem aldrig en minimumsbetaling og laeg altid ${extra} ekstra pa galden med lavest rente.`,
  },
  p4Title: "Hvad sker der, hvis du betaler mere?",
  marginalAnalysis: (method, sweet) => `Marginalanalyse — ${method}-metoden · sweet spot: ${sweet}/mdr.`,
  scenarioCol: "Scenarie",
  debtFreeCol: "Galdfri",
  totalInterestCol: "Samlet rente",
  totalSavedCol: "Samlet sparet",
  marginalGainCol: "Marginalgevinst",
  minimumsOnlyRow: amt => `${amt}/mdr. (kun minimum)`,
  scenarioRow: (amt, label) => `${amt}/mdr. — ${label}`,
  sweetSpotNote: sweet => `Sweet spot: ${sweet}/mdr. giver bedst afkast — hver ekstra krone sparer mindre efter dette punkt.`,
  payAsMuchNote: "Betal sa meget ekstra som muligt — hver krone reducerer renten markant.",
  aggressiveNote: (saved, extra) => `Aggressiv betaling sparer ${saved} mere men kraever ${extra}/mdr. ekstra — bestem ud fra dit budget.`,
  bonusNote: "Hvis du modtager en bonus eller skatterefusion, saet den forst pa din gald.",
  p5Title: "Maned-for-maned plan",
  methodAllDebts: (method, months) => `${method}-metoden - al gald - ${months} maneder i alt`,
  monthCol: "Maned",
  focusDebt: "Fokusgald",
  paymentCol: "Betaling",
  principalCol: "Afdrag",
  interestCol: "Rente",
  remainingCol: "Resterende",
  paidOffRolling: (names, next) => `${names} BETALT - flyt betaling til ${next}`,
  paidOff: names => `${names} BETALT`,
  debtFreeRow: date => `GALDFRI - ${date}`,
  allDebtsNote: (n, date, interest) => `Al gald betalt inden ${date}. Samlet rente betalt: ${interest}.`,
  p6Title: "Gaeldskonsolideringsanalyse",
  isItWorthIt: "Er det vaerd det for dig?",
  aprConsolidationLoan: "eff. rente konsolideringslat",
  totalInterestLabel: "samlet rente",
  savesAmount: amt => `Sparer ${amt}`,
  costsMore: amt => `Koster ${amt} mere`,
  breakEvenPoint: fee => `Break-even punkt (inkl. ${fee} stiftelsesgebyr)`,
  neverBreaksEven: "Naar aldrig break-even - anbefales ikke",
  breakEvenAfterPayoff: "Break-even efter tilbagebetalingsdato - knapt vard",
  breakEvenSaves: (month, amt) => `Break-even ved maned ${month} - sparer ${amt}/mdr. efter`,
  breakEvenBarely: month => `Break-even ved maned ${month} - knapt vard`,
  atApr: rate => `Ved ${rate}% eff. rente`,
  ourRecommendation: "Vores anbefaling",
  consolidationBelowApr: rate => `Konsolidering giver kun mening under ${rate}% eff. rente.`,
  noneConsolidationRates: "Ingen af disse renter slar din nuvarende tilbagebetalingsplan.",
  debtExpensive: (name, rate) => `Din ${name} pa ${rate}% er dyr -`,
  qualifyConsolidation: "hvis du kvalificerer til 10% konsolidering, overvej det seriest.",
  stopUsingCard: "Vigtigt: stop med at bruge kreditkortet efter konsolidering.",
  p7Title: "Din handlingsplan + FAQ",
  stepsToFreedom: "3 trin mod galdfrihed - fra i dag",
  startingDate: date => `START ${date}`,
  debtFreeDateLabel: "GALDFRI DATO",
  youWillSave: amt => `Du sparer ${amt} vs kun minimumsbetalinger`,
  glossaryFaq: "Ordliste & FAQ",
  step1: (d1, extra, d1combined) => `Nu: Indstil automatisk minimumsbetaling pa al gald undtagen ${d1}. Saet al din ${extra}/mdr. ekstra + ${d1} minimum = ${d1combined}/mdr. i alt pa ${d1}. Ror det ikke, for det er betalt.`,
  step2: (date, d1, d1combined, d2, d2combined) => `Omkring ${date}: ${d1} er betalt. Flyt de ${d1combined}/mdr. til ${d2}. Du betaler nu ${d2combined}/mdr. pa ${d2}.`,
  step3withD3: (date, d2, totalFreed, d3) => `Omkring ${date}: ${d2} er faerdig. Flyt alt -- ${totalFreed}/mdr. -- til ${d3}. Sidst indspark. Du er naesten der.`,
  step3noD3: (date, d2) => `Omkring ${date}: ${d2} er faerdig. Du er galdfri!`,
  faqs: [
    { q: "Hvad er Lavine-metoden?",               a: "Betale minimumsbetaling pa al gald og derefter laegge det ekstra pa den med hojest rente. Sparer mest i rente over tid." },
    { q: "Hvad er Snebold-metoden?",              a: "Angribe lavest saldo forst. Nar betalt, flytte betalingen til naste. Koster lidt mere i rente men hurtige sejre motiverer." },
    { q: "Hvad er Hoj saldo-metoden?",            a: "Fokusere pa hojest saldo forst. Reducerer storste gald hurtigst men er ikke altid finansielt mest effektivt." },
    { q: "Hvad er Lav rente-metoden?",            a: "Betale galden med lavest rente forst. Frigiver minimumsbetalinger hurtigst men koster mest i samlet rente. Generelt ikke anbefalet." },
    { q: "Hvad betyder at flytte en betaling?",   a: "Nar en gald er betalt laegges det belob til naste galdbetaling. Samlede manedlige udgifter forbliver de samme -- du omdirigerer dem blot." },
    { q: "Hvad er effektiv rente?",               a: "De arlige kreditomkostninger som procent af saldoen. Ved 22% eff. rente betaler du ca. 1,83% pr. maned i rente." },
    { q: "Hvad er gaeldskonsolidering?",          a: "Et nyt lan til at betale flere galdsposters ned. Hvis ny rente er lavere sparer du penge. Risiko: fortsat brug af kreditkort efter konsolidering kan oge galden." },
    { q: "Hvor nojagtige er disse beregninger?",  a: "Forudsatter faste renter og konsekvente betalinger. Faktiske resultater kan variere. Betragt dette som en palidelig vejledning, ikke en garanti. Ikke finansiel radgivning." },
  ],
};

// ─────────────────────────────────────────────
// FINNISH
// ─────────────────────────────────────────────
const fi = {
  ...en,
  months: ["tam","hel","maa","huh","tou","kes","hei","elo","syy","lok","mar","jou"],
  monthsFull: ["tammikuu","helmikuu","maaliskuu","huhtikuu","toukokuu","kesakuu","heinakuu","elokuu","syyskuu","lokakuu","marraskuu","joulukuu"],
  fmtDuration: (months) => {
    const y = Math.floor(months / 12), m = months % 12;
    if (!y) return `${months} kk`;
    if (!m) return `${y} v.`;
    return `${y} v. ${m} kk`;
  },
  preparedBy: "Valmistanut Weelco Inc.",
  disclaimer: "Tama raportti on tarkoitettu vain tiedoksi. Weelco Inc. ei ole talousneuvoja.",
  p1Title: "Velkatilannekatsaus",
  whereYouStand: "TILANTEESI TANAAN",
  totalDebt: "VELKA YHTEENSA",
  acrossAccounts: n => `${n} tilil${n !== 1 ? "la" : "la"}`,
  minPaymentPerMo: "Vahahimmaissuoritus/kk",
  avgInterestRate: "Keskim. korko",
  payingMinimumsOnly: "Vain vahimmaissuoritukset",
  minimumsWarning: (cost, date) => `Vain minimisuoritusten maksaminen maksaa ${cost} korkoa ja kestaa ${date} asti.`,
  withExtraSavings: (extra, saved, time) => `${extra}/kk lisamaksulla saastat ${saved} ja valmistut ${time} aiemmin.`,
  recommended: "SUOSITELTU",
  methodSavingsDesc: (desc, saved, worst, extra) => `${desc} · saastaa ${saved} vs ${worst} lisayksella ${extra}/kk`,
  yourScenarios: "MAKSUSUUNNITELMASI",
  yourDebts: "VELKASI",
  conservative: "Konservatiivinen",
  balanced: "Tasapainoinen",
  aggressive: "Aggressiivinen",
  saves: amt => `saastaa ${amt}`,
  p2Title: "Kaikki 4 metodia vertailussa",
  basedOn: (debt, extra) => `Perustuen ${debt} velkaan lisayksella ${extra}/kk`,
  debtFreeIn: "Velaton",
  totalInterest: "Korko yhteensa",
  interestVsWorst: "Korko vs huonoin",
  summary: "Yhteenveto",
  recommendedSummary: (method, saved, worst, extra) => `Suositeltu: ${method} — saastaa ${saved} vs ${worst}-metodi lisayksella ${extra}/kk.`,
  methodLabels: { avalanche: "Lumivyory", snowball: "Lumipallo", highbal: "Korkea saldo", lowint: "Matala korko" },
  methodDescs: { avalanche: "Korkein korko ensin", snowball: "Pienin saldo ensin", highbal: "Suurin saldo ensin", lowint: "Matalin korko ensin" },
  methodDescsLong: { avalanche: "Korkein korko ensin", snowball: "Pienin saldo ensin", highbal: "Suurin saldo ensin", lowint: "Matalin korkokanta ensin" },
  methodDescsP2: {
    avalanche: "Maksetaan korkein korko ensin. Matemaattisesti optimaalinen — saastaa eniten.",
    snowball:  "Maksetaan pienin saldo ensin. Nopeat voitot pitavat motivaation ylla.",
    highbal:   "Hyokataan suurin velka ensin. Vahentaa suurinta vastuuta nopeimmin.",
    lowint:    "Maksetaan matalin korko ensin. Vapauttaa kassavirtaa nopeasti.",
  },
  p3Title: "Mika velka hyokata ensin",
  minPerMo: amt => `Min: ${amt}/kk`,
  attackFirst: (reason, extra) => `Hyokata ensin -- ${reason}. Kaikki ${extra}/kk lisays menee tanne.`,
  payMinimumsOthers: "Maksetaan vahimmaissuoritukset kaikista muista veloista.",
  attackSecond: debt => `Hyokata toisena. Kun ${debt} on maksettu,`,
  rollPaymentHere: amt => `siirra sen ${amt}/kk tanne minimisuorituksen lisaksi.`,
  attackN: (n, prevN) => `Hyokata #${n}. Maksetaan vahimmaissuoritukset kunnes ${prevN} yllaolevat velat on maksettu,`,
  rollFreed: "siirra sitten kaikki vapautuneet maksut tanne.",
  reasonHighestRate: rate => `korkein korko ${rate}%`,
  reasonSmallestBal: bal => `pienin saldo ${bal}`,
  reasonLargestBal: bal => `suurin saldo ${bal}`,
  reasonLowestRate: rate => `matalin korko ${rate}%`,
  consistency: {
    avalanche: extra => `Lumivyoryn avain on johdonmukaisuus -- ala koskaan jata minimisuoritusta valisiin ja laita aina ${extra} lisays velalle #1 kunnes se on maksettu.`,
    snowball:  extra => `Lumipallon avain on johdonmukaisuus -- ala koskaan jata minimisuoritusta valisiin ja laita aina ${extra} lisays pienimmalle saldolle kunnes se on maksettu.`,
    highbal:   extra => `Korkean saldon avain on johdonmukaisuus -- ala koskaan jata minimisuoritusta valisiin ja laita aina ${extra} lisays suurimmalle saldolle kunnes se on maksettu.`,
    lowint:    extra => `Matalan koron avain on johdonmukaisuus -- ala koskaan jata minimisuoritusta valisiin ja laita aina ${extra} lisays matalimpikorkoiselle velalle.`,
  },
  p4Title: "Mita jos maksat enemman?",
  marginalAnalysis: (method, sweet) => `Marginaalianalyysi — ${method}-metodi · optimipiste: ${sweet}/kk`,
  scenarioCol: "Skenaario",
  debtFreeCol: "Velaton",
  totalInterestCol: "Korko yhteensa",
  totalSavedCol: "Saastettu yhteensa",
  marginalGainCol: "Marginaalihyoty",
  minimumsOnlyRow: amt => `${amt}/kk (vain minimumit)`,
  scenarioRow: (amt, label) => `${amt}/kk — ${label}`,
  sweetSpotNote: sweet => `Optimipiste: ${sweet}/kk antaa parhaan tuoton — jokainen lisaeuron saasto vahenee taman jalkeen.`,
  payAsMuchNote: "Maksa niin paljon lisaa kuin mahdollista -- jokainen euro vahentaa korkoa merkittavasti.",
  aggressiveNote: (saved, extra) => `Aggressiivinen maksaminen saastaa ${saved} lisaa mutta vaatii ${extra}/kk lisaa -- paata budjetin mukaan.`,
  bonusNote: "Jos saat bonuksen tai verohyvityksen, laita se ensin velkoihin.",
  p5Title: "Kuukausittainen maksuaikataulu",
  methodAllDebts: (method, months) => `${method}-metodi - kaikki velat - ${months} kuukautta yhteensa`,
  monthCol: "Kuukausi",
  focusDebt: "Kohdevirka",
  paymentCol: "Maksu",
  principalCol: "Lyhennys",
  interestCol: "Korko",
  remainingCol: "Jaljella",
  paidOffRolling: (names, next) => `${names} MAKSETTU - siirra maksu kohteeseen ${next}`,
  paidOff: names => `${names} MAKSETTU`,
  debtFreeRow: date => `VELATON - ${date}`,
  allDebtsNote: (n, date, interest) => `Kaikki ${n} velka maksettu ennen ${date}. Korot yhteensa maksettu: ${interest}.`,
  p6Title: "Velkojen yhdistamisanalyysi",
  isItWorthIt: "Onko se sinulle kannattavaa?",
  aprConsolidationLoan: "eff. korko yhdistamislaita",
  totalInterestLabel: "korko yhteensa",
  savesAmount: amt => `Saastaa ${amt}`,
  costsMore: amt => `Maksaa ${amt} enemman`,
  breakEvenPoint: fee => `Tasapistearvo (sis. ${fee} toimitusmaksu)`,
  neverBreaksEven: "Ei saavuta koskaan tasapistetta - ei suositella",
  breakEvenAfterPayoff: "Tasapiste maksupaivan jalkeen - tuskin kannattavaa",
  breakEvenSaves: (month, amt) => `Tasapiste kuukaudella ${month} - saastaa ${amt}/kk sen jalkeen`,
  breakEvenBarely: month => `Tasapiste kuukaudella ${month} - tuskin kannattavaa`,
  atApr: rate => `${rate}% eff. korolla`,
  ourRecommendation: "Suosituksemme",
  consolidationBelowApr: rate => `Yhdistaminen on kannattavaa vain alle ${rate}% eff. korolla.`,
  noneConsolidationRates: "Yksikaan naista koroista ei paata nykyista maksusuunnitelmaasi.",
  debtExpensive: (name, rate) => `${name} ${rate}% korolla on kallista -`,
  qualifyConsolidation: "jos paasit 10% yhdistamislainaan, harkitse sita vakavasti.",
  stopUsingCard: "Tarkeaa: lopeta luottokortin kaytto yhdistamisen jalkeen.",
  p7Title: "Toimintasuunnitelmasi + UKK",
  stepsToFreedom: "3 askelta velattomaksi - alkaen tanaan",
  startingDate: date => `ALKAEN ${date}`,
  debtFreeDateLabel: "VELATON PAIVA",
  youWillSave: amt => `Saastat ${amt} verrattuna vain minimisuorituksiin`,
  glossaryFaq: "Sanasto & UKK",
  step1: (d1, extra, d1combined) => `Nyt: Aseta automaattiset minimisuoritukset kaikille veloille paitsi ${d1}. Laita kaikki ${extra}/kk lisays + ${d1} minimi = ${d1combined}/kk yhteensa kohteeseen ${d1}. Ala koske siihen ennen kuin se on maksettu.`,
  step2: (date, d1, d1combined, d2, d2combined) => `Noin ${date}: ${d1} on maksettu. Siirra ${d1combined}/kk kohteeseen ${d2}. Maksat nyt ${d2combined}/kk kohteesta ${d2}.`,
  step3withD3: (date, d2, totalFreed, d3) => `Noin ${date}: ${d2} on valmis. Siirra kaikki -- ${totalFreed}/kk -- kohteeseen ${d3}. Loppukirii. Olet melkein perilla.`,
  step3noD3: (date, d2) => `Noin ${date}: ${d2} on valmis. Olet velaton!`,
  faqs: [
    { q: "Mika on Lumivyory-metodi?",             a: "Maksetaan minimisuoritukset kaikille veloille ja laitetaan loput korkeimpikorvoiselle. Saastaa eniten korkoja kokonaisuudessa." },
    { q: "Mika on Lumipallo-metodi?",             a: "Hyokataan pieninta saldoa ensin. Maksamisen jalkeen siirretaan maksu seuraavalle. Maksaa hieman enemman korkoa mutta nopeat voitot motivoivat." },
    { q: "Mika on Korkea saldo -metodi?",         a: "Keskitytaan suurimpaan saldoon ensin. Vahentaa suurimman velan nopeimmin mutta ei aina ole taloudellisesti tehokkain." },
    { q: "Mika on Matala korko -metodi?",         a: "Maksetaan matalimpikorkoinen velka ensin. Vapauttaa minimisuoritukset nopeimmin mutta maksaa eniten kokonaiskorkoina. Yleisesti ei suositeltua." },
    { q: "Mita tarkoittaa maksun siirtaminen?",   a: "Kun velka on maksettu, se summa lisataan seuraavan velan maksuun. Kokonaismenot pysyvat samoina -- ne vain ohjataan uudelleen." },
    { q: "Mika on efektiivinen korko?",           a: "Luoton vuotuinen hinta prosentteina saldosta. 22% efektiivisella korolla maksat noin 1,83% kuukaudessa korkoa." },
    { q: "Mika on velkojen yhdistaminen?",        a: "Uusi laina useampien velkojen maksamiseen. Jos uusi korko on alempi, saastat rahaa. Riski: luottokorttien jatkuva kaytto yhdistamisen jalkeen voi kasvattaa velkaa." },
    { q: "Kuinka tarkkoja napat laskelmat ovat?", a: "Olettaa kiinteat korot ja johdonmukaiset maksut. Todelliset tulokset voivat vaihdella. Pideta tata luotettavana oppaana, ei takuuna. Ei taloudellista neuvontaa." },
  ],
};

// ─────────────────────────────────────────────
// POLISH
// Note: Polish uses ą ć ę ł ń ó ś ź ż — all in WinAnsi/CP-1252,
// but jsPDF Helvetica may render some as fallback glyphs.
// ─────────────────────────────────────────────
const pl = {
  ...en,
  months: ["sty","lut","mar","kwi","maj","cze","lip","sie","wrz","paz","lis","gru"],
  monthsFull: ["styczen","luty","marzec","kwiecien","maj","czerwiec","lipiec","sierpien","wrzesien","pazdziernik","listopad","grudzien"],
  fmtDuration: (months) => {
    const y = Math.floor(months / 12), m = months % 12;
    if (!y) return `${months} mies.`;
    if (!m) return `${y} r.`;
    return `${y} r. ${m} mies.`;
  },
  preparedBy: "Przygotowano przez Weelco Inc.",
  disclaimer: "Ten raport ma charakter wylacznie informacyjny. Weelco Inc. nie jest doradca finansowym.",
  p1Title: "Twoj przeglad zadluzenia",
  whereYouStand: "TWOJA SYTUACJA DZISIAJ",
  totalDebt: "ZADLUZENIE LACZNIE",
  acrossAccounts: n => `na ${n} koncie${n !== 1 ? "ach" : ""}`,
  minPaymentPerMo: "Min. rata miesiecznie",
  avgInterestRate: "Sr. stopa procentowa",
  payingMinimumsOnly: "Tylko minimalne raty",
  minimumsWarning: (cost, date) => `Placenie tylko minimow kosztuje ${cost} odsetek i trwa do ${date}.`,
  withExtraSavings: (extra, saved, time) => `Z ${extra}/mies. extra oszczedzasz ${saved} i konczysz ${time} wczesniej.`,
  recommended: "ZALECANE",
  methodSavingsDesc: (desc, saved, worst, extra) => `${desc} · oszczedza ${saved} vs ${worst} przy ${extra}/mies.`,
  yourScenarios: "TWOJE SCENARIUSZE SPLATY",
  yourDebts: "TWOJE DLUGI",
  conservative: "Konserwatywny",
  balanced: "Zbalansowany",
  aggressive: "Agresywny",
  saves: amt => `oszczedza ${amt}`,
  p2Title: "Porownanie 4 metod",
  basedOn: (debt, extra) => `Na podstawie ${debt} zadluzenia z ${extra}/mies. extra`,
  debtFreeIn: "Wolny od dlugu za",
  totalInterest: "Odsetki lacznie",
  interestVsWorst: "Odsetki vs najgorsza",
  summary: "Podsumowanie",
  recommendedSummary: (method, saved, worst, extra) => `Zalecane: ${method} — oszczedza ${saved} vs metoda ${worst} przy ${extra}/mies. extra.`,
  methodLabels: { avalanche: "Lawina", snowball: "Sniezka", highbal: "Wysoki saldo", lowint: "Niski procent" },
  methodDescs: { avalanche: "Najwyzszy procent najpierw", snowball: "Najnizszy saldo najpierw", highbal: "Najwyzszy saldo najpierw", lowint: "Najnizszy procent najpierw" },
  methodDescsLong: { avalanche: "Najwyzszy procent najpierw", snowball: "Najnizszy saldo najpierw", highbal: "Najwyzszy saldo najpierw", lowint: "Najnizsza stopa procentowa najpierw" },
  methodDescsP2: {
    avalanche: "Splacac najwyzszy procent jako pierwszy. Optymalny matematycznie — oszczedza najwiecej.",
    snowball:  "Splacac najnizsze saldo jako pierwsze. Szybkie sukcesy podtrzymuja motywacje.",
    highbal:   "Atakowac najwiekszy dlug najpierw. Redukuje najwieksze zobowiazanie najszybciej.",
    lowint:    "Splacac najnizszy procent najpierw. Szybko uwalnia przeplyw gotowki.",
  },
  p3Title: "Ktory dlug atakowac najpierw",
  minPerMo: amt => `Min: ${amt}/mies.`,
  attackFirst: (reason, extra) => `Atakowac najpierw -- ${reason}. Cale ${extra}/mies. extra idzie tutaj.`,
  payMinimumsOthers: "Placic minimalne raty od wszystkich pozostalych dlugow.",
  attackSecond: debt => `Atakowac jako drugi. Gdy ${debt} zostanie splacony,`,
  rollPaymentHere: amt => `przelac jego ${amt}/mies. tutaj ponad minimum.`,
  attackN: (n, prevN) => `Atakowac #${n}. Placic minimalne raty do czasu splaty ${prevN} dlug${prevN > 1 ? "ow" : "u"} powyzej,`,
  rollFreed: "nastepnie przelewac wszystkie zwolnione platnosci tutaj.",
  reasonHighestRate: rate => `najwyzszy procent ${rate}%`,
  reasonSmallestBal: bal => `najnizszy saldo ${bal}`,
  reasonLargestBal: bal => `najwyzszy saldo ${bal}`,
  reasonLowestRate: rate => `najnizszy procent ${rate}%`,
  consistency: {
    avalanche: extra => `Kluczem Lawiny jest konsekwencja -- nigdy nie pomijaj minimalnej raty i zawsze wkladaj ${extra} extra na dlug #1 az do splaty.`,
    snowball:  extra => `Kluczem Sniezki jest konsekwencja -- nigdy nie pomijaj minimalnej raty i zawsze wkladaj ${extra} extra na najnizszy saldo az do splaty.`,
    highbal:   extra => `Kluczem Wysokiego salda jest konsekwencja -- nigdy nie pomijaj minimalnej raty i zawsze wkladaj ${extra} extra na najwyzszy saldo az do splaty.`,
    lowint:    extra => `Kluczem Niskiego procentu jest konsekwencja -- nigdy nie pomijaj minimalnej raty i zawsze wkladaj ${extra} extra na dlug z najnizszym procentem.`,
  },
  p4Title: "Co jesli zaplacisz wiecej?",
  marginalAnalysis: (method, sweet) => `Analiza marginalna — metoda ${method} · optymalny punkt: ${sweet}/mies.`,
  scenarioCol: "Scenariusz",
  debtFreeCol: "Wolny od dlugu",
  totalInterestCol: "Odsetki lacznie",
  totalSavedCol: "Lacznie zaoszczedzone",
  marginalGainCol: "Korzysc marginalna",
  minimumsOnlyRow: amt => `${amt}/mies. (tylko minimum)`,
  scenarioRow: (amt, label) => `${amt}/mies. — ${label}`,
  sweetSpotNote: sweet => `Optymalny punkt: ${sweet}/mies. daje najlepszy zwrot — kazda dodatkowa zlotowka oszczedza mniej po tym punkcie.`,
  payAsMuchNote: "Plac tak duzo extra, jak to mozliwe — kazda zlotowka znaczaco zmniejsza odsetki.",
  aggressiveNote: (saved, extra) => `Agresywna splata oszczedza ${saved} wiecej, ale wymaga ${extra}/mies. extra — zdecyduj wedlug budzetu.`,
  bonusNote: "Jezeli dostaniesz premia lub zwrot podatku, przeznadz to najpierw na dlug.",
  p5Title: "Harmonogram miesiac po miesiacu",
  methodAllDebts: (method, months) => `Metoda ${method} - wszystkie dlugi - ${months} miesiecy lacznie`,
  monthCol: "Miesiac",
  focusDebt: "Priorytetowy dlug",
  paymentCol: "Rata",
  principalCol: "Kapital",
  interestCol: "Odsetki",
  remainingCol: "Pozostalo",
  paidOffRolling: (names, next) => `${names} SPLACONY - przenoszenie platnosci na ${next}`,
  paidOff: names => `${names} SPLACONY`,
  debtFreeRow: date => `WOLNY OD DLUGU - ${date}`,
  allDebtsNote: (n, date, interest) => `Wszystkie ${n} dlug${n !== 1 ? "i" : ""} splacone przed ${date}. Lacznie odsetki zaplacone: ${interest}.`,
  p6Title: "Analiza konsolidacji zadluzenia",
  isItWorthIt: "Czy to sie oplaca?",
  aprConsolidationLoan: "RRSO pozyczka konsolidacyjna",
  totalInterestLabel: "odsetki lacznie",
  savesAmount: amt => `Oszczedza ${amt}`,
  costsMore: amt => `Kosztuje ${amt} wiecej`,
  breakEvenPoint: fee => `Punkt rentownosci (wlacznie z ${fee} prowizja)`,
  neverBreaksEven: "Nigdy nie osiaga rentownosci - niezalecane",
  breakEvenAfterPayoff: "Rentownosc po dacie splaty - ledwo oplacalne",
  breakEvenSaves: (month, amt) => `Rentownosc w miesiacu ${month} - oszczedza ${amt}/mies. potem`,
  breakEvenBarely: month => `Rentownosc w miesiacu ${month} - ledwo oplacalne`,
  atApr: rate => `Przy ${rate}% RRSO`,
  ourRecommendation: "Nasza rekomendacja",
  consolidationBelowApr: rate => `Konsolidacja ma sens tylko ponizej ${rate}% RRSO.`,
  noneConsolidationRates: "Zadna z tych stop nie bije twojego obecnego planu splaty.",
  debtExpensive: (name, rate) => `Twoj ${name} przy ${rate}% jest drogi -`,
  qualifyConsolidation: "jezeli kwalifikujesz sie na 10% konsolidacje, powazanie to rozwaz.",
  stopUsingCard: "Wazne: przestan uzywac karty kredytowej po konsolidacji.",
  p7Title: "Twoj plan dzialania + FAQ",
  stepsToFreedom: "3 kroki do wolnosci finansowej - od dzisiaj",
  startingDate: date => `START ${date}`,
  debtFreeDateLabel: "DATA WOLNOSCI OD DLUGU",
  youWillSave: amt => `Zaoszczedzisz ${amt} vs tylko minimalne raty`,
  glossaryFaq: "Slowniczek i FAQ",
  step1: (d1, extra, d1combined) => `Teraz: Ustaw automatyczne minimalne raty na wszystkich dlugach z wyjatkiem ${d1}. Wkloz wszystkie ${extra}/mies. extra + minimum ${d1} = ${d1combined}/mies. lacznie na ${d1}. Nie ruszaj az do splaty.`,
  step2: (date, d1, d1combined, d2, d2combined) => `Okolo ${date}: ${d1} jest splacony. Przenies ${d1combined}/mies. na ${d2}. Bedziesz teraz placil ${d2combined}/mies. na ${d2}.`,
  step3withD3: (date, d2, totalFreed, d3) => `Okolo ${date}: ${d2} gotowy. Przenies wszystko -- ${totalFreed}/mies. -- na ${d3}. Ostatni sprint. Jestes prawie na mecie.`,
  step3noD3: (date, d2) => `Okolo ${date}: ${d2} gotowy. Jestes wolny od dlugow!`,
  faqs: [
    { q: "Co to jest metoda Lawiny?",              a: "Placic minimalne raty na wszystkich dlugach, a extra na ten z najwyzszym procentem. Raz splacony, przejsc do nastepnego. Oszczedza najwiecej odsetek." },
    { q: "Co to jest metoda Sniezki?",             a: "Atakowac najnizsze saldo najpierw. Po splacie przelasc rate na nastepne. Kosztuje troche wiecej odsetek, ale szybkie sukcesy motywuja." },
    { q: "Co to jest metoda Wysokiego salda?",     a: "Skupic sie na najwyzszym saldzie jako pierwszym. Redukuje najwiekszy dlug najszybciej, ale nie zawsze jest najbardziej efektywne finansowo." },
    { q: "Co to jest metoda Niskiego procentu?",   a: "Splacac dlug z najnizszym procentem najpierw. Najszybciej uwalnia minimalne raty, ale kosztuje najwiecej odsetek lacznie. Generalnie niezalecane." },
    { q: "Co znaczy przenoszenie raty?",           a: "Gdy dlug zostaje splacony, ta kwota jest dodawana do raty nastepnego dlugu. Calkowite miesieczne wydatki pozostaja takie same -- tylko inaczej kierowane." },
    { q: "Co to jest RRSO?",                       a: "Rzeczywista Roczna Stopa Oprocentowania -- roczny koszt kredytu jako procent salda. Przy 22% RRSO placisz okolo 1,83% miesiecznie odsetek." },
    { q: "Co to jest konsolidacja zadluzenia?",    a: "Nowy kredyt do splaty kilku dlugow. Jezeli nowy procent jest nizszy, oszczedzasz. Ryzyko: dalsze uzywanie kart po konsolidacji moze zwiekszyc zadluzenie." },
    { q: "Jak dokladne sa te obliczenia?",         a: "Zaklada stale stopy i regularne platnosci. Faktyczne wyniki moga sie roznic. Traktuj to jako wiarygodna mape drogi, nie gwarancje. Nie jest porada finansowa." },
  ],
};

// ─────────────────────────────────────────────
// CZECH
// ─────────────────────────────────────────────
const cs = {
  ...en,
  months: ["led","uno","bre","dub","kve","cer","cvc","srp","zar","rij","lis","pro"],
  monthsFull: ["leden","unor","brezen","duben","kveten","cerven","cervenec","srpen","zari","rijen","listopad","prosinec"],
  fmtDuration: (months) => {
    const y = Math.floor(months / 12), m = months % 12;
    if (!y) return `${months} mes.`;
    if (!m) return `${y} r.`;
    return `${y} r. ${m} mes.`;
  },
  preparedBy: "Zpracovano spolecnosti Weelco Inc.",
  disclaimer: "Tato zprava je pouze informativni. Weelco Inc. neni financni poradce.",
  p1Title: "Vas prehled dluhu",
  whereYouStand: "VAS STAV DNES",
  totalDebt: "CELKOVY DLUH",
  acrossAccounts: n => `na ${n} uctu${n !== 1 ? "ch" : ""}`,
  minPaymentPerMo: "Min. splatka za mesic",
  avgInterestRate: "Prumerna urokova sazba",
  payingMinimumsOnly: "Jen minimalni splatky",
  minimumsWarning: (cost, date) => `Placeni jen minimalnich splatok stoji ${cost} na urocich a trva do ${date}.`,
  withExtraSavings: (extra, saved, time) => `S ${extra}/mes. navic usetrite ${saved} a splatite o ${time} drive.`,
  recommended: "DOPORUCENO",
  methodSavingsDesc: (desc, saved, worst, extra) => `${desc} · usetri ${saved} vs ${worst} pri ${extra}/mes.`,
  yourScenarios: "VASE SCENARE SPLACENI",
  yourDebts: "VASE DLUHY",
  conservative: "Konzervativni",
  balanced: "Vyvazeny",
  aggressive: "Agresivni",
  saves: amt => `usetri ${amt}`,
  p2Title: "Porovnani vsech 4 metod",
  basedOn: (debt, extra) => `Na zaklade ${debt} dluhu s ${extra}/mes. navic`,
  debtFreeIn: "Bez dluhu za",
  totalInterest: "Celkove uroky",
  interestVsWorst: "Uroky vs nejhorsi",
  summary: "Shrnutí",
  recommendedSummary: (method, saved, worst, extra) => `Doporuceno: ${method} — usetri ${saved} vs metoda ${worst} pri ${extra}/mes. navic.`,
  methodLabels: { avalanche: "Lavina", snowball: "Snehova koule", highbal: "Vysoky zustatek", lowint: "Nizky urok" },
  methodDescs: { avalanche: "Nejvissi urok nejdrive", snowball: "Nejnizsi zustatek nejdrive", highbal: "Nejvyssi zustatek nejdrive", lowint: "Nejnizsi urok nejdrive" },
  methodDescsLong: { avalanche: "Nejvissi urok nejdrive", snowball: "Nejnizsi zustatek nejdrive", highbal: "Nejvyssi zustatek nejdrive", lowint: "Nejnizsi urokova sazba nejdrive" },
  methodDescsP2: {
    avalanche: "Platit nejvissi urok nejdrive. Matematicky optimalni — usetri nejvice.",
    snowball:  "Platit nejnizsi zustatek nejdrive. Rychle uspeochy udrzuji motivaci.",
    highbal:   "Utocit na nejvyssi dluh nejdrive. Nejrychleji snizuje nejvetsi zavazek.",
    lowint:    "Platit nejnizsi urok nejdrive. Rychle uvolnuje penezni tok.",
  },
  p3Title: "Ktery dluh utocit nejdrive",
  minPerMo: amt => `Min: ${amt}/mes.`,
  attackFirst: (reason, extra) => `Utocit nejdrive -- ${reason}. Vsechno ${extra}/mes. navic jde sem.`,
  payMinimumsOthers: "Platit minimalni splatky na vsechny ostatni dluhy.",
  attackSecond: debt => `Utocit jako druhy. Kdyz ${debt} je splacen,`,
  rollPaymentHere: amt => `presunout jeho ${amt}/mes. sem navic k minimu.`,
  attackN: (n, prevN) => `Utocit #${n}. Platit minimalni splatky dokud ${prevN} dluh${prevN > 1 ? "y" : ""} vyse nejsou splaceny,`,
  rollFreed: "pak presunout vsechny uvolnene platby sem.",
  reasonHighestRate: rate => `nejvissi urok ${rate}%`,
  reasonSmallestBal: bal => `nejnizsi zustatek ${bal}`,
  reasonLargestBal: bal => `nejvyssi zustatek ${bal}`,
  reasonLowestRate: rate => `nejnizsi urok ${rate}%`,
  consistency: {
    avalanche: extra => `Klicem Laviny je duslednost -- nikdy nevynechavejte minimalni splatku a vzdy davejte ${extra} navic na dluh c. 1, dokud neni splacen.`,
    snowball:  extra => `Klicem Snehove koule je duslednost -- nikdy nevynechavejte minimalni splatku a vzdy davejte ${extra} navic na nejnizsi zustatek, dokud neni splacen.`,
    highbal:   extra => `Klicem Vysokeho zustatku je duslednost -- nikdy nevynechavejte minimalni splatku a vzdy davejte ${extra} navic na nejvyssi zustatek, dokud neni splacen.`,
    lowint:    extra => `Klicem Nizkeho uroku je duslednost -- nikdy nevynechavejte minimalni splatku a vzdy davejte ${extra} navic na dluh s nejnizsi urokovou sazbou.`,
  },
  p4Title: "Co kdyz zaplatite vice?",
  marginalAnalysis: (method, sweet) => `Marginalni analyza — metoda ${method} · optimalni bod: ${sweet}/mes.`,
  scenarioCol: "Scenar",
  debtFreeCol: "Bez dluhu",
  totalInterestCol: "Celkove uroky",
  totalSavedCol: "Celkem usetreeno",
  marginalGainCol: "Marginalni zisk",
  minimumsOnlyRow: amt => `${amt}/mes. (jen minima)`,
  scenarioRow: (amt, label) => `${amt}/mes. — ${label}`,
  sweetSpotNote: sweet => `Optimalni bod: ${sweet}/mes. dava nejlepsi navratnost — kazda dalsi koruna usetri po tomto bode mene.`,
  payAsMuchNote: "Plaťte co nejvice navic — kazda koruna vyrazne snizuje uroky.",
  aggressiveNote: (saved, extra) => `Agresivni splaceni usetri ${saved} vice, ale vyzaduje ${extra}/mes. navic — rozhodujte podle rozpoctu.`,
  bonusNote: "Pokud dostanete bonus nebo danovy navrat, dejte ho nejdrive na dluhy.",
  p5Title: "Mesicni harmonogram splaceni",
  methodAllDebts: (method, months) => `Metoda ${method} - vsechny dluhy - ${months} mesicu celkem`,
  monthCol: "Mesic",
  focusDebt: "Prioritni dluh",
  paymentCol: "Splatka",
  principalCol: "Jistina",
  interestCol: "Uroky",
  remainingCol: "Zbyvajici",
  paidOffRolling: (names, next) => `${names} SPLACEN - presunem platbu na ${next}`,
  paidOff: names => `${names} SPLACEN`,
  debtFreeRow: date => `BEZ DLUHU - ${date}`,
  allDebtsNote: (n, date, interest) => `Vsechny ${n} dluhy splaceny do ${date}. Celkove zaplacene uroky: ${interest}.`,
  p6Title: "Analyza konsolidace dluhu",
  isItWorthIt: "Vyplati se vam to?",
  aprConsolidationLoan: "RPSN konsolidacni uver",
  totalInterestLabel: "celkove uroky",
  savesAmount: amt => `Usetri ${amt}`,
  costsMore: amt => `Stoji o ${amt} vice`,
  breakEvenPoint: fee => `Bod rentability (vcetne ${fee} poplatku)`,
  neverBreaksEven: "Nikdy nedosahne rentability - nedoporuceno",
  breakEvenAfterPayoff: "Rentabilita po datu splaceni - stezi vyhodne",
  breakEvenSaves: (month, amt) => `Rentabilita v mesici ${month} - usetri ${amt}/mes. pote`,
  breakEvenBarely: month => `Rentabilita v mesici ${month} - stezi vyhodne`,
  atApr: rate => `Pri ${rate}% RPSN`,
  ourRecommendation: "Nase doporuceni",
  consolidationBelowApr: rate => `Konsolidace ma smysl jen pod ${rate}% RPSN.`,
  noneConsolidationRates: "Zadna z techto sazeb neprekonava vas stavajici plan splaceni.",
  debtExpensive: (name, rate) => `Vas ${name} pri ${rate}% je drahy -`,
  qualifyConsolidation: "pokud ziskate 10% konsolidaci, vazne to zvazete.",
  stopUsingCard: "Dulezite: prestante pouzivat kreditni kartu po konsolidaci.",
  p7Title: "Vas akcni plan + FAQ",
  stepsToFreedom: "3 kroky ke svobode od dluhu - od dnes",
  startingDate: date => `ZACATEK ${date}`,
  debtFreeDateLabel: "DATUM BEZ DLUHU",
  youWillSave: amt => `Usetrite ${amt} vs jen minimalni splatky`,
  glossaryFaq: "Glosar a FAQ",
  step1: (d1, extra, d1combined) => `Nyni: Nastavte automaticke minimalni splatky na vsechny dluhy krome ${d1}. Dejte vsechno ${extra}/mes. navic + minimum ${d1} = ${d1combined}/mes. celkem na ${d1}. Nesahejte na to, dokud neni splacen.`,
  step2: (date, d1, d1combined, d2, d2combined) => `Okolo ${date}: ${d1} je splacen. Presunete ${d1combined}/mes. na ${d2}. Budete nyni platit ${d2combined}/mes. na ${d2}.`,
  step3withD3: (date, d2, totalFreed, d3) => `Okolo ${date}: ${d2} hotov. Presunete vse -- ${totalFreed}/mes. -- na ${d3}. Posledni sprint. Jste temer tam.`,
  step3noD3: (date, d2) => `Okolo ${date}: ${d2} hotov. Jste bez dluhu!`,
  faqs: [
    { q: "Co je metoda Laviny?",                   a: "Platit minimalni splatky na vsechny dluhy a extra na ten s nejvyssim urokem. Usetri nejvice na urocich celkove." },
    { q: "Co je metoda Snehove koule?",            a: "Utocit na nejnizsi zustatek nejdrive. Po splaceni presunout platbu na dalsi. Stoji trochu vice na urocich, ale rychle uspechy motivuji." },
    { q: "Co je metoda Vysokeho zustatku?",        a: "Zamerit se na nejvyssi zustatek nejdrive. Snizuje nejvetsi dluh nejrychleji, ale neni vzdy financne nejefektivnejsi." },
    { q: "Co je metoda Nizkeho uroku?",            a: "Splatit dluh s nejnizsi sazbou nejdrive. Nejrychleji uvolnuje minimalni splatky, ale stoji nejvice na celkovych urocich. Obecne nedoporuceno." },
    { q: "Co znamena prenos platby?",              a: "Kdyz je dluh splacen, ta castka se prida k platbe dalsiho dluhu. Celkove mesicni vydaje zustavaji stejne -- jen se jinak smeruji." },
    { q: "Co je RPSN?",                            a: "Rocni procentni sazba nakladu -- rocni cena uveru jako procento zustatku. Pri 22% RPSN platite priblizne 1,83% mesicne na urocich." },
    { q: "Co je konsolidace dluhu?",               a: "Novy uver ke splaceni vice dluhu. Pokud je nova sazba nizsi, usetrujete. Riziko: dalsi pouzivani karet po konsolidaci muze zvysit zadluzeni." },
    { q: "Jak presne jsou tato vypocty?",          a: "Predpoklada fixni sazby a pravidelne platby. Skutecne vysledky se mohou lisit. Povazujte to za spolehlivou mapu, ne zaruku. Neni to financni poradenstvi." },
  ],
};

// ─────────────────────────────────────────────
// HUNGARIAN
// ─────────────────────────────────────────────
const hu = {
  ...en,
  months: ["jan","feb","mar","apr","maj","jun","jul","aug","szep","okt","nov","dec"],
  monthsFull: ["januar","februar","marcius","aprilis","majus","junius","julius","augusztus","szeptember","oktober","november","december"],
  fmtDuration: (months) => {
    const y = Math.floor(months / 12), m = months % 12;
    if (!y) return `${months} ho`;
    if (!m) return `${y} ev`;
    return `${y} ev ${m} ho`;
  },
  preparedBy: "Keszitette: Weelco Inc.",
  disclaimer: "Ez a jelentes csak tajekoztato jellegu. A Weelco Inc. nem penzugyi tanacsado.",
  p1Title: "Adossagattekinted",
  whereYouStand: "HOL TARTASZ MA",
  totalDebt: "OSSZES ADOSSAG",
  acrossAccounts: n => `${n} szamlan`,
  minPaymentPerMo: "Min. torleszto/ho",
  avgInterestRate: "Atl. kamatlab",
  payingMinimumsOnly: "Csak minimumok fizetese",
  minimumsWarning: (cost, date) => `Csak a minimumok fizetese ${cost} kamatba kerul es ${date}-ig tart.`,
  withExtraSavings: (extra, saved, time) => `${extra}/ho extra osszeggel ${saved}-t megtakaritasz es ${time}-vel hamarabb vegzel.`,
  recommended: "JAVASOLT",
  methodSavingsDesc: (desc, saved, worst, extra) => `${desc} · ${saved}-t megsporul vs ${worst} ${extra}/ho mellett`,
  yourScenarios: "FIZETESI SCENARIOID",
  yourDebts: "ADOSSAGAID",
  conservative: "Konzervativ",
  balanced: "Kiegyensulyozott",
  aggressive: "Agressziv",
  saves: amt => `megtakarit ${amt}`,
  p2Title: "Mind a 4 modszer osszehasonlitva",
  basedOn: (debt, extra) => `${debt} adossag alapjan ${extra}/ho extra osszeggel`,
  debtFreeIn: "Adossagmentes",
  totalInterest: "Osszes kamat",
  interestVsWorst: "Kamat vs legrosszabb",
  summary: "Osszefoglalo",
  recommendedSummary: (method, saved, worst, extra) => `Javasolt: ${method} — ${saved}-t megsporul vs ${worst} modszer mellett ${extra}/ho extraval.`,
  methodLabels: { avalanche: "Lavina", snowball: "Hogolyoc", highbal: "Magas egyenleg", lowint: "Alacsony kamat" },
  methodDescs: { avalanche: "Legmagasabb kamat elobb", snowball: "Legalacsonyabb egyenleg elobb", highbal: "Legmagasabb egyenleg elobb", lowint: "Legalacsonyabb kamat elobb" },
  methodDescsLong: { avalanche: "Legmagasabb kamat elobb", snowball: "Legalacsonyabb egyenleg elobb", highbal: "Legmagasabb egyenleg elobb", lowint: "Legalacsonyabb kamatlab elobb" },
  methodDescsP2: {
    avalanche: "Elobb a legmagasabb kamatot fizetni. Matematikailag optimalis — legtobbet megtakarit.",
    snowball:  "Elobb a legalacsonyabb egyenleget fizetni. Gyors sikerek fenntartjak a motivaciot.",
    highbal:   "A legnagyobb adossagot tamadni elobb. Leggyorsabban csokkkenti a legnagyobb kotelemzet.",
    lowint:    "Elobb a legalacsonyabb kamatot fizetni. Gyorsan szabadit fel penzmozgast.",
  },
  p3Title: "Melyik adossagot tamadni elobb",
  minPerMo: amt => `Min: ${amt}/ho`,
  attackFirst: (reason, extra) => `Elobb tamadni -- ${reason}. Az osszes ${extra}/ho extra ide megy.`,
  payMinimumsOthers: "Minimumokat fizetni minden tobbi adossagra.",
  attackSecond: debt => `Masodiknak tamadni. Ha ${debt} kifizetodott,`,
  rollPaymentHere: amt => `a ${amt}/ho osszegot ide atutalni a minimum felett.`,
  attackN: (n, prevN) => `#${n} tamadasa. Minimumokat fizetni amig a fenti ${prevN} adossag ki nem fizetodik,`,
  rollFreed: "majd az osszes felszabadult kifizeteset ide atutalni.",
  reasonHighestRate: rate => `legmagasabb kamat ${rate}%`,
  reasonSmallestBal: bal => `legalacsonyabb egyenleg ${bal}`,
  reasonLargestBal: bal => `legmagasabb egyenleg ${bal}`,
  reasonLowestRate: rate => `legalacsonyabb kamat ${rate}%`,
  consistency: {
    avalanche: extra => `A Lavina kulcsa a kovetkezetesseg -- soha ne hagyj ki minimum kifizetese es mindig tedd ${extra} extrat az 1. adossagra amig ki nem fizetodik.`,
    snowball:  extra => `A Hogolyoc kulcsa a kovetkezetesseg -- soha ne hagyj ki minimum kifizetese es mindig tedd ${extra} extrat a legalacsonyabb egyenlegre amig ki nem fizetodik.`,
    highbal:   extra => `A Magas egyenleg kulcsa a kovetkezetesseg -- soha ne hagyj ki minimum kifizetese es mindig tedd ${extra} extrat a legmagasabb egyenlegre amig ki nem fizetodik.`,
    lowint:    extra => `Az Alacsony kamat kulcsa a kovetkezetesseg -- soha ne hagyj ki minimum kifizetese es mindig tedd ${extra} extrat a legalacsonyabb kamatu adossagra.`,
  },
  p4Title: "Mi tortenik ha tobbet fizetsz?",
  marginalAnalysis: (method, sweet) => `Marginalis elemzes — ${method} modszer · optimalis pont: ${sweet}/ho`,
  scenarioCol: "Scenario",
  debtFreeCol: "Adossagmentes",
  totalInterestCol: "Osszes kamat",
  totalSavedCol: "Osszes megtakaritott",
  marginalGainCol: "Marginalis nyereseg",
  minimumsOnlyRow: amt => `${amt}/ho (csak minimumok)`,
  scenarioRow: (amt, label) => `${amt}/ho — ${label}`,
  sweetSpotNote: sweet => `Optimalis pont: ${sweet}/ho adja a legjobb hozamot — minden extra forint kevesebbet takarit meg e pont utan.`,
  payAsMuchNote: "Fizess annyit extra, amennyit csak tudsz -- minden forint jelentosen csokkkenti a kamatot.",
  aggressiveNote: (saved, extra) => `Agressziv fizetes ${saved}-t tobb takarit meg de ${extra}/ho extrat igenyel -- dontsd el a koltsegveted alapjan.`,
  bonusNote: "Ha bonuszt vagy adovisszateriteset kapsz, tedd azt elobb az adossagokra.",
  p5Title: "Honap-rol honap-ra utemterv",
  methodAllDebts: (method, months) => `${method} modszer - osszes adossag - ${months} honap osszesen`,
  monthCol: "Honap",
  focusDebt: "Focusz adossag",
  paymentCol: "Torlesztes",
  principalCol: "Toke",
  interestCol: "Kamat",
  remainingCol: "Maradek",
  paidOffRolling: (names, next) => `${names} KIFIZETVE - kifizetese atiranyitasa ${next}-re`,
  paidOff: names => `${names} KIFIZETVE`,
  debtFreeRow: date => `ADOSSAGMENTES - ${date}`,
  allDebtsNote: (n, date, interest) => `Osszes ${n} adossag kifizetve ${date} elott. Osszes fizetett kamat: ${interest}.`,
  p6Title: "Adossag-konszolidacios elemzes",
  isItWorthIt: "Megeri neked?",
  aprConsolidationLoan: "THM konszolidacios hitel",
  totalInterestLabel: "osszes kamat",
  savesAmount: amt => `Megtakarit ${amt}`,
  costsMore: amt => `${amt}-val tobb kerul`,
  breakEvenPoint: fee => `Megteriilesi pont (${fee} kezelesi dijjal egyutt)`,
  neverBreaksEven: "Soha nem teri meg -- nem javasolt",
  breakEvenAfterPayoff: "Megterules a kifizetesi datum utan -- alig eeri meg",
  breakEvenSaves: (month, amt) => `Megterules a ${month}. honapban -- ezutan ${amt}/ho megtakaritast`,
  breakEvenBarely: month => `Megterules a ${month}. honapban -- alig eeri meg`,
  atApr: rate => `${rate}% THM-nel`,
  ourRecommendation: "Ajanlasunk",
  consolidationBelowApr: rate => `A konszolidacio csak ${rate}% THM alatt ertelmes.`,
  noneConsolidationRates: "Egyik kamatlab sem verik a jelenlegi visszafizetesi tervedet.",
  debtExpensive: (name, rate) => `A ${name} adossagod ${rate}%-on draga --`,
  qualifyConsolidation: "ha jogosult vagy 10%-os konszolidaciora, vedd komolyan fontolora.",
  stopUsingCard: "Fontos: hagyd abba a hitelkartyad hasznalatat a konszolidacio utan.",
  p7Title: "Akciotervedd + GYIK",
  stepsToFreedom: "3 lepes a penzugyi szabadsag fele - ma kezdodve",
  startingDate: date => `KEZDES ${date}`,
  debtFreeDateLabel: "ADOSSAGMENTES DATUM",
  youWillSave: amt => `${amt}-t takaritasz meg vs csak minimumok fizetese`,
  glossaryFaq: "Szotar es GYIK",
  step1: (d1, extra, d1combined) => `Most: Allitsd be az automatikus minimum kifizeteseket az osszes adossagra a ${d1} kivetelevel. Tedd az osszes ${extra}/ho extrat + ${d1} minimum = ${d1combined}/ho osszeget ${d1}-re. Ne nyulj hozza amig ki nem fizetodik.`,
  step2: (date, d1, d1combined, d2, d2combined) => `Kb. ${date}: ${d1} kifizetodott. Iranitsd at a ${d1combined}/ho osszegot ${d2}-re. Most ${d2combined}/ho-t fogsz fizetni ${d2}-re.`,
  step3withD3: (date, d2, totalFreed, d3) => `Kb. ${date}: ${d2} kesz. Iranitsd at mindent -- ${totalFreed}/ho -- ${d3}-ra. Utolso hajra. Mar majdnem ott vagy.`,
  step3noD3: (date, d2) => `Kb. ${date}: ${d2} kesz. Adossagmentes vagy!`,
  faqs: [
    { q: "Mi a Lavina modszer?",                   a: "Minimumokat fizetni minden adossagra, majd az extrat a legmagasabb kamatura. Legtobbet takarit meg kamatban." },
    { q: "Mi a Hogolyoc modszer?",                 a: "Elobb a legalacsonyabb egyenleget tamadni. Kifizetes utan az osszeg atiranyitasa a kovetkezore. Kicsit tobb kamatba kerul, de a gyors sikerek motivalnak." },
    { q: "Mi a Magas egyenleg modszer?",           a: "A legmagasabb egyenlegre fokuszalni elobb. Leggyorsabban csokkkenti a legnagyobb adossagot, de penzugyileg nem mindig a leghatekonyabb." },
    { q: "Mi az Alacsony kamat modszer?",          a: "A legalacsonyabb kamatu adossagot fizetni elobb. Leggyorsabban szabaditja fel a minimumokat, de legtobb kamatba kerul osszesen. Altalaban nem javasolt." },
    { q: "Mit jelent az atutalas?",                a: "Amikor egy adossag kifizetodik, az osszeget hozzaadja a kovetkezo adossag kifizeteshez. Az osszes havi kiadasok ugyanannyi marad -- csak masfelel iranyitja." },
    { q: "Mi a THM?",                              a: "Teljes Hiteldij Mutato -- az eves hiteldij az egyenleg szazalekaként. 22% THM-nel havonta kb. 1,83% kamatot fizetsz." },
    { q: "Mi az adossag-konszolidacio?",           a: "Uj hitel tobb adossag kifizetesere. Ha az uj kamat alacsonyabb, megtakaritasz. Kockazat: hitelkartyak tovabb hasznalata konszolidacio utan noveelheti az adossagot." },
    { q: "Milyen pontosak ezek a szamitasok?",     a: "Fix kamatokat es rendszeres fizeteseket feltetelezi. A zyakorlati eredmenyek elterohetnek. Tekintsd megbizahto utmutatornak, nem garantanak. Nem penzugyi tanacsadas." },
  ],
};

// ─────────────────────────────────────────────
// ROMANIAN
// ─────────────────────────────────────────────
const ro = {
  ...en,
  months: ["ian","feb","mar","apr","mai","iun","iul","aug","sep","oct","nov","dec"],
  monthsFull: ["ianuarie","februarie","martie","aprilie","mai","iunie","iulie","august","septembrie","octombrie","noiembrie","decembrie"],
  fmtDuration: (months) => {
    const y = Math.floor(months / 12), m = months % 12;
    if (!y) return `${months} luni`;
    if (!m) return `${y} an${y > 1 ? "i" : ""}`;
    return `${y} an${y > 1 ? "i" : ""} ${m} luni`;
  },
  preparedBy: "Pregatit de Weelco Inc.",
  disclaimer: "Acest raport este doar informativ. Weelco Inc. nu este consilier financiar.",
  p1Title: "Situatia dvs. de datorii",
  whereYouStand: "UNDE VA AFLATI AZI",
  totalDebt: "DATORIE TOTALA",
  acrossAccounts: n => `pe ${n} cont${n !== 1 ? "uri" : ""}`,
  minPaymentPerMo: "Plata minima/luna",
  avgInterestRate: "Rata medie a dobanzii",
  payingMinimumsOnly: "Doar plati minime",
  minimumsWarning: (cost, date) => `Plata doar a minimelor costa ${cost} in dobanzi si dureaza pana in ${date}.`,
  withExtraSavings: (extra, saved, time) => `Cu ${extra}/luna extra economisiti ${saved} si terminati cu ${time} mai devreme.`,
  recommended: "RECOMANDAT",
  methodSavingsDesc: (desc, saved, worst, extra) => `${desc} · economiseste ${saved} vs ${worst} la ${extra}/luna`,
  yourScenarios: "SCENARIILE DVS. DE PLATA",
  yourDebts: "DATORIILE DVS.",
  conservative: "Conservator",
  balanced: "Echilibrat",
  aggressive: "Agresiv",
  saves: amt => `economiseste ${amt}`,
  p2Title: "Toate cele 4 metode comparate",
  basedOn: (debt, extra) => `Bazat pe ${debt} datorie cu ${extra}/luna extra`,
  debtFreeIn: "Fara datorii in",
  totalInterest: "Dobanzi totale",
  interestVsWorst: "Dobanzi vs cel mai rau",
  summary: "Rezumat",
  recommendedSummary: (method, saved, worst, extra) => `Recomandat: ${method} — economiseste ${saved} vs metoda ${worst} la ${extra}/luna extra.`,
  methodLabels: { avalanche: "Avalansa", snowball: "Bulgare de zapada", highbal: "Sold ridicat", lowint: "Dobanda mica" },
  methodDescs: { avalanche: "Dobanda cea mai mare intai", snowball: "Soldul cel mai mic intai", highbal: "Soldul cel mai mare intai", lowint: "Dobanda cea mai mica intai" },
  methodDescsLong: { avalanche: "Dobanda cea mai mare intai", snowball: "Soldul cel mai mic intai", highbal: "Soldul cel mai mare intai", lowint: "Rata dobanzii cea mai mica intai" },
  methodDescsP2: {
    avalanche: "Plata celei mai mari dobanzi intai. Optim matematic — economiseste cel mai mult.",
    snowball:  "Plata celui mai mic sold intai. Victorie rapida mentine motivatia.",
    highbal:   "Atacarea celei mai mari datorii intai. Reduce cel mai mare pasiv cel mai rapid.",
    lowint:    "Plata celei mai mici dobanzi intai. Elibereaza rapid fluxul de numerar.",
  },
  p3Title: "Ce datorie sa atacati intai",
  minPerMo: amt => `Min: ${amt}/luna`,
  attackFirst: (reason, extra) => `Atacati intai -- ${reason}. Tot ${extra}/luna extra merge aici.`,
  payMinimumsOthers: "Platiti minimele pe toate celelalte datorii.",
  attackSecond: debt => `Atacati al doilea. Cand ${debt} este platita,`,
  rollPaymentHere: amt => `transferati ${amt}/luna aici peste minimul ei.`,
  attackN: (n, prevN) => `Atacati #${n}. Platiti minimele pana cand cele ${prevN} datorii${prevN > 1 ? "" : ""} de mai sus sunt platite,`,
  rollFreed: "apoi transferati toate platile eliberate aici.",
  reasonHighestRate: rate => `dobanda cea mai mare la ${rate}%`,
  reasonSmallestBal: bal => `soldul cel mai mic la ${bal}`,
  reasonLargestBal: bal => `soldul cel mai mare la ${bal}`,
  reasonLowestRate: rate => `dobanda cea mai mica la ${rate}%`,
  consistency: {
    avalanche: extra => `Cheia Avalansei este consecventa -- nu omiteti niciodata o plata minima si puneti intotdeauna ${extra} extra pe datoria #1 pana este platita.`,
    snowball:  extra => `Cheia Bulgare de zapada este consecventa -- nu omiteti niciodata o plata minima si puneti intotdeauna ${extra} extra pe soldul cel mai mic pana este platit.`,
    highbal:   extra => `Cheia Soldului ridicat este consecventa -- nu omiteti niciodata o plata minima si puneti intotdeauna ${extra} extra pe soldul cel mai mare pana este platit.`,
    lowint:    extra => `Cheia Dobanzii mici este consecventa -- nu omiteti niciodata o plata minima si puneti intotdeauna ${extra} extra pe datoria cu dobanda cea mai mica.`,
  },
  p4Title: "Ce se intampla daca platiti mai mult?",
  marginalAnalysis: (method, sweet) => `Analiza marginala — metoda ${method} · punct optim: ${sweet}/luna`,
  scenarioCol: "Scenariu",
  debtFreeCol: "Fara datorii",
  totalInterestCol: "Dobanzi totale",
  totalSavedCol: "Total economisit",
  marginalGainCol: "Castig marginal",
  minimumsOnlyRow: amt => `${amt}/luna (doar minime)`,
  scenarioRow: (amt, label) => `${amt}/luna — ${label}`,
  sweetSpotNote: sweet => `Punct optim: ${sweet}/luna da cel mai bun randament — fiecare leu extra economiseste mai putin dupa acest punct.`,
  payAsMuchNote: "Platiti cat mai mult extra posibil -- fiecare leu reduce semnificativ dobanzile.",
  aggressiveNote: (saved, extra) => `Plata agresiva economiseste ${saved} mai mult dar necesita ${extra}/luna extra -- decideti in functie de buget.`,
  bonusNote: "Daca primiti un bonus sau o restituire fiscala, puneti-o mai intai pe datorii.",
  p5Title: "Plan de plata luna cu luna",
  methodAllDebts: (method, months) => `Metoda ${method} - toate datoriile - ${months} luni in total`,
  monthCol: "Luna",
  focusDebt: "Datoria prioritara",
  paymentCol: "Plata",
  principalCol: "Principal",
  interestCol: "Dobanda",
  remainingCol: "Ramas",
  paidOffRolling: (names, next) => `${names} PLATITA - transfer plata catre ${next}`,
  paidOff: names => `${names} PLATITA`,
  debtFreeRow: date => `FARA DATORII - ${date}`,
  allDebtsNote: (n, date, interest) => `Toate ${n} datorii platite inainte de ${date}. Total dobanzi platite: ${interest}.`,
  p6Title: "Analiza consolidarii datoriilor",
  isItWorthIt: "Merita pentru dvs.?",
  aprConsolidationLoan: "DAE imprumut de consolidare",
  totalInterestLabel: "dobanzi totale",
  savesAmount: amt => `Economiseste ${amt}`,
  costsMore: amt => `Costa ${amt} mai mult`,
  breakEvenPoint: fee => `Punct de rentabilitate (inclusiv ${fee} comision)`,
  neverBreaksEven: "Nu devine niciodata rentabil - nereomandat",
  breakEvenAfterPayoff: "Rentabilitate dupa data platii - abia merita",
  breakEvenSaves: (month, amt) => `Rentabilitate la luna ${month} - economiseste ${amt}/luna dupa`,
  breakEvenBarely: month => `Rentabilitate la luna ${month} - abia merita`,
  atApr: rate => `La ${rate}% DAE`,
  ourRecommendation: "Recomandarea noastra",
  consolidationBelowApr: rate => `Consolidarea are sens doar sub ${rate}% DAE.`,
  noneConsolidationRates: "Niciuna din aceste rate nu bate planul dvs. actual de plata.",
  debtExpensive: (name, rate) => `${name} dvs. la ${rate}% este scump -`,
  qualifyConsolidation: "daca va calificati pentru 10% consolidare, luati-o in serios in considerare.",
  stopUsingCard: "Important: nu mai folositi cardul de credit dupa consolidare.",
  p7Title: "Planul dvs. de actiune + FAQ",
  stepsToFreedom: "3 pasi spre libertate financiara - incepand de azi",
  startingDate: date => `INCEPUT ${date}`,
  debtFreeDateLabel: "DATA FARA DATORII",
  youWillSave: amt => `Veti economisi ${amt} vs doar plati minime`,
  glossaryFaq: "Glosar si FAQ",
  step1: (d1, extra, d1combined) => `Acum: Configurati plata automata a minimelor pe toate datoriile cu exceptia ${d1}. Puneti tot ${extra}/luna extra + minimul ${d1} = ${d1combined}/luna total pe ${d1}. Nu atingeti pana nu este platita.`,
  step2: (date, d1, d1combined, d2, d2combined) => `Aproximativ ${date}: ${d1} este platita. Transferati ${d1combined}/luna catre ${d2}. Veti plati acum ${d2combined}/luna pe ${d2}.`,
  step3withD3: (date, d2, totalFreed, d3) => `Aproximativ ${date}: ${d2} gata. Transferati totul -- ${totalFreed}/luna -- catre ${d3}. Ultimul sprint. Aproape ati ajuns.`,
  step3noD3: (date, d2) => `Aproximativ ${date}: ${d2} gata. Sunteti liber de datorii!`,
  faqs: [
    { q: "Ce este metoda Avalansa?",               a: "Platiti minimele pe toate datoriile, apoi extra pe cea cu dobanda cea mai mare. Economiseste cel mai mult in dobanzi in timp." },
    { q: "Ce este metoda Bulgare de zapada?",      a: "Atacati soldul cel mai mic intai. Dupa plata, transferati suma la urmatorul. Costa putin mai mult in dobanzi, dar victoriile rapide motiveaza." },
    { q: "Ce este metoda Sold ridicat?",           a: "Focalizati pe soldul cel mai mare intai. Reduce cea mai mare datorie cel mai rapid, dar nu este intotdeauna cel mai eficient financiar." },
    { q: "Ce este metoda Dobanda mica?",           a: "Platiti datoria cu dobanda cea mai mica intai. Elibereaza minimele cel mai rapid, dar costa cel mai mult in total dobanzi. In general nereomandat." },
    { q: "Ce inseamna transferul platii?",         a: "Cand o datorie este platita, suma respectiva se adauga la plata urmatoarei datorii. Totalul cheltuielilor lunare ramane acelasi -- doar se redirectioneaza." },
    { q: "Ce este DAE?",                           a: "Dobanda Anuala Efectiva -- costul anual al creditului ca procent din sold. La 22% DAE platiti aproximativ 1,83% pe luna in dobanzi." },
    { q: "Ce este consolidarea datoriilor?",       a: "Un nou imprumut pentru a plati mai multe datorii. Daca noua rata este mai mica, economisiti. Risc: continuarea utilizarii cardurilor dupa consolidare poate creste datoria." },
    { q: "Cat de precise sunt aceste calcule?",    a: "Presupune rate fixe si plati constante. Rezultatele reale pot varia. Tratati-o ca o harta de drum de incredere, nu o garantie. Nu este sfat financiar." },
  ],
};

// ─────────────────────────────────────────────
// SLOVAK / SLOVENE / ESTONIAN / LATVIAN / LITHUANIAN
// (all use English as base — small populations, similar enough to Czech/German)
// These share a close base; only key UI strings differ.
// ─────────────────────────────────────────────
const sk = {
  ...cs,
  preparedBy: "Pripravil Weelco Inc.",
  disclaimer: "Tato sprava je iba informativna. Weelco Inc. nie je financny poradca.",
  p1Title: "Prehlad vaseho zadlzenia",
  whereYouStand: "VAS STAV DNES",
  totalDebt: "CELKOVY DLH",
  acrossAccounts: n => `na ${n} ucte${n !== 1 ? "och" : ""}`,
  minPaymentPerMo: "Min. splatka za mesiac",
  avgInterestRate: "Priem. urokova sadzba",
  payingMinimumsOnly: "Len minimalne splatky",
  minimumsWarning: (cost, date) => `Platenie len minimalnych splatok stoji ${cost} na urokoch a trva do ${date}.`,
  withExtraSavings: (extra, saved, time) => `S ${extra}/mes. navyse usetrite ${saved} a splatite o ${time} skor.`,
  recommended: "ODPORUCANE",
  methodSavingsDesc: (desc, saved, worst, extra) => `${desc} · usetri ${saved} vs ${worst} pri ${extra}/mes.`,
  yourScenarios: "VASE SCENARE SPLACANIA",
  yourDebts: "VASE DLHY",
  conservative: "Konzervativny",
  balanced: "Vyvazeny",
  aggressive: "Agresivny",
  saves: amt => `usetri ${amt}`,
  p2Title: "Porovnanie vsetkych 4 metod",
  debtFreeIn: "Bez dlhu za",
  summary: "Zhrnutie",
  recommendedSummary: (method, saved, worst, extra) => `Odporucane: ${method} — usetri ${saved} vs metoda ${worst} pri ${extra}/mes. navyse.`,
  methodLabels: { avalanche: "Lavina", snowball: "Snehuliena", highbal: "Vysoky zostatok", lowint: "Nizky urok" },
  methodDescs: { avalanche: "Najvyssi urok najprv", snowball: "Najnizsi zostatok najprv", highbal: "Najvyssi zostatok najprv", lowint: "Najnizsi urok najprv" },
  methodDescsLong: { avalanche: "Najvyssi urok najprv", snowball: "Najnizsi zostatok najprv", highbal: "Najvyssi zostatok najprv", lowint: "Najnizszia urokova sadzba najprv" },
  p3Title: "Ktory dlh utocit najprv",
  payMinimumsOthers: "Platit minimalne splatky na vsetky ostatne dlhy.",
  p4Title: "Co ked zaplatite viac?",
  p5Title: "Mesacny harmonogram splacania",
  p6Title: "Analyza konsolidacie dlhov",
  isItWorthIt: "Oplati sa vam to?",
  aprConsolidationLoan: "RPMN konsolidacny uver",
  p7Title: "Vas akcny plan + FAQ",
  stepsToFreedom: "3 kroky k slobode od dlhov - od dnes",
  debtFreeDateLabel: "DATUM BEZ DLHU",
  glossaryFaq: "Glosar a FAQ",
  faqs: [
    { q: "Co je metoda Laviny?",                   a: "Platit minimalne splatky na vsetky dlhy a extra na ten s najvyssim urokom. Usetri najviac na urokoch celkovo." },
    { q: "Co je metoda Snehulieny?",               a: "Utocit na najnizsi zostatok najprv. Po splateni presuniet platbu na dalsi. Stoji trochu viac na urokoch, ale rychle uspeochy motivuju." },
    { q: "Co je metoda Vysokeho zostatku?",        a: "Zamerit sa na najvyssi zostatok najprv. Znizuje najvacsi dlh najrychlejsie, ale nie vzdy je financne najefektivnejsia." },
    { q: "Co je metoda Nizkeho uroku?",            a: "Splatit dlh s najnizsou sadzbou najprv. Najrychlejsie uvolnuje minimalne splatky, ale stoji najviac na celkovych urokoch. Vseobecne neodporucane." },
    { q: "Co znamena prenos platby?",              a: "Ked je dlh splateny, ta suma sa prida k platbe dalsieho dlhu. Celkove mesacne vydavky zostanu rovnake -- len sa inak smeruju." },
    { q: "Co je RPMN?",                            a: "Rocna Percentualna Miera Nakladov -- rocna cena uveru ako percento zostatku. Pri 22% RPMN platite priblizne 1,83% mesacne na urokoch." },
    { q: "Co je konsolidacia dlhov?",              a: "Novy uver na splatenie viacerych dlhov. Ak je nova sadzba nizsia, usetrujete. Riziko: dalsia pouzivanie kariet po konsolidacii moze zvysit zadlzenie." },
    { q: "Ako presne su tieto vypocty?",           a: "Predpoklada fixne sadzby a pravidelne platby. Skutocne vysledky sa mozu lisit. Povazujte to za spolahlivu mapu, nie zaruku. Nie je to financne poradenstvo." },
  ],
};

const sl = {
  ...en,
  months: ["jan","feb","mar","apr","maj","jun","jul","avg","sep","okt","nov","dec"],
  monthsFull: ["januar","februar","marec","april","maj","junij","julij","avgust","september","oktober","november","december"],
  fmtDuration: (months) => {
    const y = Math.floor(months / 12), m = months % 12;
    if (!y) return `${months} mes.`;
    if (!m) return `${y} l.`;
    return `${y} l. ${m} mes.`;
  },
  preparedBy: "Pripravil Weelco Inc.",
  disclaimer: "To porocilo je zgolj informativno. Weelco Inc. ni financni svetovalec.",
  p1Title: "Pregled vasih dolgov",
  whereYouStand: "VAS POLOZAJ DANES",
  totalDebt: "SKUPAJ DOLG",
  acrossAccounts: n => `na ${n} racunu${n !== 1 ? "ih" : ""}`,
  minPaymentPerMo: "Min. obrok/mesec",
  avgInterestRate: "Povprecna obrestna mera",
  payingMinimumsOnly: "Le minimalni obroki",
  minimumsWarning: (cost, date) => `Placevanje le minimumov stane ${cost} obresti in traja do ${date}.`,
  withExtraSavings: (extra, saved, time) => `Z ${extra}/mes. dodatno prihrankite ${saved} in koncate ${time} prej.`,
  recommended: "PRIPOROCENO",
  methodSavingsDesc: (desc, saved, worst, extra) => `${desc} · prihranka ${saved} vs ${worst} pri ${extra}/mes.`,
  yourScenarios: "VASI SCENARIJI PLACILA",
  yourDebts: "VASI DOLGOVI",
  conservative: "Konzervativen",
  balanced: "Uravnotezen",
  aggressive: "Agresiven",
  saves: amt => `prihranka ${amt}`,
  p2Title: "Vse 4 metode primerjane",
  basedOn: (debt, extra) => `Na podlagi ${debt} dolga z ${extra}/mes. dodatno`,
  debtFreeIn: "Brez dolga v",
  totalInterest: "Skupaj obresti",
  interestVsWorst: "Obresti vs najslabsa",
  summary: "Povzetek",
  recommendedSummary: (method, saved, worst, extra) => `Priporoceno: ${method} — prihranka ${saved} vs metoda ${worst} pri ${extra}/mes. dodatno.`,
  methodLabels: { avalanche: "Plaz", snowball: "Snezna krogla", highbal: "Visoko stanje", lowint: "Nizka obrest" },
  methodDescs: { avalanche: "Najvisjsa obrest najprej", snowball: "Najnizje stanje najprej", highbal: "Najvisje stanje najprej", lowint: "Najnizja obrest najprej" },
  methodDescsLong: { avalanche: "Najvisjsa obrest najprej", snowball: "Najnizje stanje najprej", highbal: "Najvisje stanje najprej", lowint: "Najnizja obrestna mera najprej" },
  methodDescsP2: {
    avalanche: "Placati visjo obrest najprej. Matematicno optimalno — prihranka najvec.",
    snowball:  "Placati nizje stanje najprej. Hitre zmage vzdrzujejo motivacijo.",
    highbal:   "Napasti najvecji dolg najprej. Najhitreje zmanjsa najvecjo obveznost.",
    lowint:    "Placati nizjo obrest najprej. Hitro sprosti denarni tok.",
  },
  p3Title: "Kateri dolg napasti najprej",
  minPerMo: amt => `Min: ${amt}/mes.`,
  attackFirst: (reason, extra) => `Napasti najprej -- ${reason}. Vsak ${extra}/mes. extra gre sem.`,
  payMinimumsOthers: "Placevati minimalne obroke na vse ostale dolgove.",
  attackSecond: debt => `Napasti kot drugi. Ko je ${debt} poplacen,`,
  rollPaymentHere: amt => `prenesti ${amt}/mes. sem poleg minimuma.`,
  attackN: (n, prevN) => `Napasti #${n}. Placevati minimume dokler ${prevN} dolg${prevN > 1 ? "ov" : ""} zgoraj ni poplacen,`,
  rollFreed: "nato prenesti vsa sprosc payena placila sem.",
  reasonHighestRate: rate => `najvisja obrest ${rate}%`,
  reasonSmallestBal: bal => `najnizje stanje ${bal}`,
  reasonLargestBal: bal => `najvisje stanje ${bal}`,
  reasonLowestRate: rate => `najnizja obrest ${rate}%`,
  consistency: {
    avalanche: extra => `Kljuc do Plazu je doslednost -- nikoli ne izpustite minimalnega obroka in vedno vlagajte ${extra} extra v dolg #1 dokler ni poplacan.`,
    snowball:  extra => `Kljuc do Snezne krogle je doslednost -- nikoli ne izpustite minimalnega obroka in vedno vlagajte ${extra} extra v najnizje stanje dokler ni poplacano.`,
    highbal:   extra => `Kljuc do Visokega stanja je doslednost -- nikoli ne izpustite minimalnega obroka in vedno vlagajte ${extra} extra v najvisje stanje dokler ni poplacano.`,
    lowint:    extra => `Kljuc do Nizke obresti je doslednost -- nikoli ne izpustite minimalnega obroka in vedno vlagajte ${extra} extra v dolg z najnizjo obrestjo.`,
  },
  p4Title: "Kaj ce placate vec?",
  marginalAnalysis: (method, sweet) => `Mejna analiza — metoda ${method} · optimalna tocka: ${sweet}/mes.`,
  scenarioCol: "Scenarij",
  debtFreeCol: "Brez dolga",
  totalInterestCol: "Skupaj obresti",
  totalSavedCol: "Skupaj prihranjeno",
  marginalGainCol: "Mejna korist",
  minimumsOnlyRow: amt => `${amt}/mes. (le minimum)`,
  scenarioRow: (amt, label) => `${amt}/mes. — ${label}`,
  sweetSpotNote: sweet => `Optimalna tocka: ${sweet}/mes. daje najboljsi donos — vsak dodaten evro prihranka manj po tej tocki.`,
  payAsMuchNote: "Placajte kar najvec extra -- vsak evro znatno zmanjsa obresti.",
  aggressiveNote: (saved, extra) => `Agresivno placanje prihranka ${saved} vec, vendar zahteva ${extra}/mes. extra -- odlocite glede na proracun.`,
  bonusNote: "Ce prejmete bonus ali davčno vracilo, ga najprej namenite dolgom.",
  p5Title: "Mesecni urnik placil",
  methodAllDebts: (method, months) => `Metoda ${method} - vsi dolgovi - ${months} mesecev skupaj`,
  monthCol: "Mesec",
  focusDebt: "Prioritetni dolg",
  paymentCol: "Obrok",
  principalCol: "Glavnica",
  interestCol: "Obresti",
  remainingCol: "Preostalo",
  paidOffRolling: (names, next) => `${names} POPLACAN - prenos placila na ${next}`,
  paidOff: names => `${names} POPLACAN`,
  debtFreeRow: date => `BREZ DOLGA - ${date}`,
  allDebtsNote: (n, date, interest) => `Vsi ${n} dolgov poplacani do ${date}. Skupaj placane obresti: ${interest}.`,
  p6Title: "Analiza konsolidacije dolgov",
  isItWorthIt: "Ali se vam splaca?",
  aprConsolidationLoan: "EOM konsolidacijsko posojilo",
  totalInterestLabel: "skupaj obresti",
  savesAmount: amt => `Prihranka ${amt}`,
  costsMore: amt => `Stane ${amt} vec`,
  breakEvenPoint: fee => `Tocka preloma (vkljucno s ${fee} provizijo)`,
  neverBreaksEven: "Nikoli ne dosezeee preloma -- ni priporocljivo",
  breakEvenAfterPayoff: "Prelom po datumu poplačila -- komaj vredno",
  breakEvenSaves: (month, amt) => `Prelom v mesecu ${month} -- prihranka ${amt}/mes. po tem`,
  breakEvenBarely: month => `Prelom v mesecu ${month} -- komaj vredno`,
  atApr: rate => `Pri ${rate}% EOM`,
  ourRecommendation: "Nase priporocilo",
  consolidationBelowApr: rate => `Konsolidacija ima smisel le pod ${rate}% EOM.`,
  noneConsolidationRates: "Nobena od teh stopenj ne presezeee vasega trenutnega nacr ta odplacevanja.",
  debtExpensive: (name, rate) => `Vas ${name} pri ${rate}% je drag --`,
  qualifyConsolidation: "ce se kvalificirate za 10% konsolidacijo, resno premislite.",
  stopUsingCard: "Pomembno: po konsolidaciji prenehajte z uporabo kreditne kartice.",
  p7Title: "Vas akcijski nacr t + FAQ",
  stepsToFreedom: "3 koraki do financne svobode -- od danes",
  startingDate: date => `ZACETEK ${date}`,
  debtFreeDateLabel: "DATUM BREZ DOLGA",
  youWillSave: amt => `Prihranili boste ${amt} vs le minimalni obroki`,
  glossaryFaq: "Glosar in FAQ",
  step1: (d1, extra, d1combined) => `Zdaj: Nastavite samodejne minimalne obroke na vse dolgove razen ${d1}. Vlozeite vse ${extra}/mes. extra + minimum ${d1} = ${d1combined}/mes. skupaj v ${d1}. Ne dotikajte se dokler ni poplacan.`,
  step2: (date, d1, d1combined, d2, d2combined) => `Okrog ${date}: ${d1} je poplacan. Prenesite ${d1combined}/mes. na ${d2}. Zdaj boste placevali ${d2combined}/mes. za ${d2}.`,
  step3withD3: (date, d2, totalFreed, d3) => `Okrog ${date}: ${d2} je koncan. Prenesite vse -- ${totalFreed}/mes. -- na ${d3}. Zadnji sprint. Ze skoraj ste tam.`,
  step3noD3: (date, d2) => `Okrog ${date}: ${d2} je koncan. Ste brez dolgov!`,
  faqs: [
    { q: "Kaj je metoda Plaz?",                    a: "Placevati minimume na vse dolgove, nato extra na tistega z najvijo obrestjo. Prihranka najvec obresti v casu." },
    { q: "Kaj je metoda Snezna krogla?",           a: "Napasti najnizje stanje najprej. Po poplačilu prenesti placilo na nasled nji. Stane malo vec obresti, vendar hitre zmage motivirajo." },
    { q: "Kaj je metoda Visoko stanje?",           a: "Osredotociti se na najvisje stanje najprej. Najhitreje zmanjsa najvecji dolg, a ni vedno financno najucinkovitejse." },
    { q: "Kaj je metoda Nizka obrest?",            a: "Poplacati dolg z najnizjo obrestjo najprej. Najhitreje sprosti minimalne obroke, toda stane najvec obresti skupaj. Sploso ni priporocljivo." },
    { q: "Kaj pomeni prenos placila?",             a: "Ko je dolg poplacan, se znesek doda k placilu naslednjega dolga. Skupne mesecne izdatke ostanejo enake -- le drugace se usmerjajo." },
    { q: "Kaj je EOM?",                            a: "Efektivna Obrestna Mera -- letni strošek kredita kot odstotek stanja. Pri 22% EOM placujete priblizno 1,83% mesecno obresti." },
    { q: "Kaj je konsolidacija dolgov?",           a: "Novo posojilo za poplacilo vec dolgov. Ce je nova stopnja nizja, prihranite. Tveganje: nadaljnja uporaba kartic po konsolidaciji lahko poveca dolg." },
    { q: "Kako natancni so ti izracuni?",          a: "Predpostavlja fiksne stopnje in redna placila. Dejanski rezultati se lahko razlikujejo. Obravnavajte to kot zanesljiv nacr t, ne garancijo. Ni financni nasvet." },
  ],
};

// Estonian, Latvian, Lithuanian — small populations; use English as base with key UI strings translated
const et = {
  ...en,
  months: ["jaan","veebr","mär","apr","mai","juun","juul","aug","sept","okt","nov","dets"],
  monthsFull: ["jaanuar","veebruar","märts","aprill","mai","juuni","juuli","august","september","oktoober","november","detsember"],
  fmtDuration: (months) => {
    const y = Math.floor(months / 12), m = months % 12;
    if (!y) return `${months} kuud`;
    if (!m) return `${y} a.`;
    return `${y} a. ${m} kuud`;
  },
  preparedBy: "Koostanud Weelco Inc.",
  disclaimer: "See aruanne on ainult informatiivne. Weelco Inc. ei ole finantsnoustaja.",
  p1Title: "Teie vola ulevaade",
  whereYouStand: "TEIE OLUKORD TANA",
  totalDebt: "VOLA KOKKU",
  acrossAccounts: n => `${n} kontol`,
  minPaymentPerMo: "Min. makse/kuus",
  avgInterestRate: "Keskim. intressimaar",
  payingMinimumsOnly: "Ainult miinimummaksed",
  minimumsWarning: (cost, date) => `Ainult miinimumide maksmine maksab ${cost} intressi ja kestab kuni ${date}.`,
  withExtraSavings: (extra, saved, time) => `${extra}/kuus lisaga sastate ${saved} ja lopetate ${time} varem.`,
  recommended: "SOOVITATAV",
  methodSavingsDesc: (desc, saved, worst, extra) => `${desc} · saastab ${saved} vs ${worst} lisaga ${extra}/kuus`,
  yourScenarios: "TEIE MAKSESSTSENAARIUMID",
  yourDebts: "TEIE VOLAD",
  conservative: "Konservatiivne",
  balanced: "Tasakaalustatud",
  aggressive: "Agressiivne",
  saves: amt => `saastab ${amt}`,
  p2Title: "Koik 4 meetodit vorreldud",
  basedOn: (debt, extra) => `Pohieneb ${debt} volal lisaga ${extra}/kuus`,
  debtFreeIn: "Volavabal",
  totalInterest: "Intress kokku",
  interestVsWorst: "Intress vs halvim",
  summary: "Kokkuvote",
  recommendedSummary: (method, saved, worst, extra) => `Soovitatav: ${method} — saastab ${saved} vs ${worst} meetod lisaga ${extra}/kuus.`,
  methodLabels: { avalanche: "Laviin", snowball: "Lumipall", highbal: "Korkea jook", lowint: "Madal intress" },
  methodDescs: { avalanche: "Korgeim intress esmalt", snowball: "Madalaim jook esmalt", highbal: "Korgeim jook esmalt", lowint: "Madalaim intress esmalt" },
  methodDescsLong: { avalanche: "Korgeim intress esmalt", snowball: "Madalaim jook esmalt", highbal: "Korgeim jook esmalt", lowint: "Madalaim intressimaar esmalt" },
  methodDescsP2: {
    avalanche: "Maksta korgeima intressiga esmalt. Matemaatiliselt optimaalne — saastab enim.",
    snowball:  "Maksta madalaima joogiga esmalt. Kiired voiud hoiavad motivatsiooni ullal.",
    highbal:   "Rundida suurimat vola esmalt. Vahendab suurimat kohustust kiireimalt.",
    lowint:    "Maksta madalaima intressiga esmalt. Vabastab rahavoo kiiresti.",
  },
  p3Title: "Millist vola esmalt rundida",
  minPerMo: amt => `Min: ${amt}/kuus`,
  attackFirst: (reason, extra) => `Rundida esmalt -- ${reason}. Kogu ${extra}/kuus lisa laheb siia.`,
  payMinimumsOthers: "Maksta miinimumid koigile teistele voladele.",
  attackSecond: debt => `Rundida teisena. Kui ${debt} on makstud,`,
  rollPaymentHere: amt => `kanda selle ${amt}/kuus siia miinimumi peale.`,
  attackN: (n, prevN) => `Rundida #${n}. Maksta miinimumid kuni ulmaltoodud ${prevN} vola on makstud,`,
  rollFreed: "seejarel kanda koik vabastatud maksed siia.",
  reasonHighestRate: rate => `korgeim intress ${rate}%`,
  reasonSmallestBal: bal => `madalaim jook ${bal}`,
  reasonLargestBal: bal => `korgeim jook ${bal}`,
  reasonLowestRate: rate => `madalaim intress ${rate}%`,
  consistency: {
    avalanche: extra => `Laviini votmeks on jarjepidevus -- ara kunagi jata miinimummakset vahele ja pane alati ${extra} lisa vola nr 1 peale kuni see on makstud.`,
    snowball:  extra => `Lumipalli votmeks on jarjepidevus -- ara kunagi jata miinimummakset vahele ja pane alati ${extra} lisa madalaima joogi peale kuni see on makstud.`,
    highbal:   extra => `Korkea joogi votmeks on jarjepidevus -- ara kunagi jata miinimummakset vahele ja pane alati ${extra} lisa korgeima joogi peale kuni see on makstud.`,
    lowint:    extra => `Madala intressi votmeks on jarjepidevus -- ara kunagi jata miinimummakset vahele ja pane alati ${extra} lisa madalaima intressiga vola peale.`,
  },
  p4Title: "Mis siis, kui maksate rohkem?",
  marginalAnalysis: (method, sweet) => `Marginaalanalüüs — ${method} meetod · optimaalne punkt: ${sweet}/kuus`,
  scenarioCol: "Stsenaarium",
  debtFreeCol: "Volavabal",
  totalInterestCol: "Intress kokku",
  totalSavedCol: "Saastatud kokku",
  marginalGainCol: "Marginaalne kasu",
  minimumsOnlyRow: amt => `${amt}/kuus (ainult miinimumid)`,
  scenarioRow: (amt, label) => `${amt}/kuus — ${label}`,
  sweetSpotNote: sweet => `Optimaalne punkt: ${sweet}/kuus annab parima tulususe -- iga lisaeurot saastab parast seda punkti vahem.`,
  payAsMuchNote: "Makske nii palju lisa kui voimalik -- iga euro vahendab intressi oluliselt.",
  aggressiveNote: (saved, extra) => `Agressiivne maksmine saastab ${saved} rohkem, kuid nouab ${extra}/kuus lisa -- otsustage eelarve jargi.`,
  bonusNote: "Kui saate boonuse voi maksutagastuse, suunake see esmalt voladesse.",
  p5Title: "Kuukuuline makseplaan",
  methodAllDebts: (method, months) => `${method} meetod - koik volad - ${months} kuud kokku`,
  monthCol: "Kuu",
  focusDebt: "Prioriteetne vola",
  paymentCol: "Makse",
  principalCol: "Pohiosa",
  interestCol: "Intress",
  remainingCol: "Jarele jaanud",
  paidOffRolling: (names, next) => `${names} MAKSTUD - kanda makse ${next} peale`,
  paidOff: names => `${names} MAKSTUD`,
  debtFreeRow: date => `VOLAVABAL - ${date}`,
  allDebtsNote: (n, date, interest) => `Koik ${n} vola makstud enne ${date}. Makstud intress kokku: ${interest}.`,
  p6Title: "Volade konsolideerimise analyys",
  isItWorthIt: "Kas see tasub teile ara?",
  aprConsolidationLoan: "KMM konsolideerimislaen",
  totalInterestLabel: "intress kokku",
  savesAmount: amt => `Saastab ${amt}`,
  costsMore: amt => `Maksab ${amt} rohkem`,
  breakEvenPoint: fee => `Tasuvuspunkt (sh ${fee} tasu)`,
  neverBreaksEven: "Ei saavuta kunagi tasuvust -- ei soovitata",
  breakEvenAfterPayoff: "Tasuvus parast makstud kuupaeva -- vaevalt tasub",
  breakEvenSaves: (month, amt) => `Tasuvus kuul ${month} -- saastab ${amt}/kuus parast`,
  breakEvenBarely: month => `Tasuvus kuul ${month} -- vaevalt tasub`,
  atApr: rate => `${rate}% KMM juures`,
  ourRecommendation: "Meie soovitus",
  consolidationBelowApr: rate => `Konsolideerimine on moistlik ainult alla ${rate}% KMM.`,
  noneConsolidationRates: "Ukski neist maadest ei voida teie praegust makseplaani.",
  debtExpensive: (name, rate) => `Teie ${name} ${rate}% juures on kallis --`,
  qualifyConsolidation: "kui te kvalifitseerute 10% konsolideerimiseks, kaaluge seda totseralt.",
  stopUsingCard: "Tahtis: lopetage krediitkaardi kasutamine parast konsolideerimist.",
  p7Title: "Teie tegevusplaan + KKK",
  stepsToFreedom: "3 sammu finantsvabaduse poole -- alates tanast",
  startingDate: date => `ALGUS ${date}`,
  debtFreeDateLabel: "VOLAVABAL KUUPAEV",
  youWillSave: amt => `Saastate ${amt} vs ainult miinimummaksed`,
  glossaryFaq: "Sonastik ja KKK",
  step1: (d1, extra, d1combined) => `Nüüd: Seadistage automaatsed miinimummaksed koigile voladele, v.a ${d1}. Pange kogu ${extra}/kuus lisa + ${d1} miinimum = ${d1combined}/kuus kokku ${d1} peale. Ara puutu seda enne kui see on makstud.`,
  step2: (date, d1, d1combined, d2, d2combined) => `Umbes ${date}: ${d1} on makstud. Kandke ${d1combined}/kuus ${d2} peale. Nüüd maksate ${d2combined}/kuus ${d2} eest.`,
  step3withD3: (date, d2, totalFreed, d3) => `Umbes ${date}: ${d2} on valmis. Kandke koik -- ${totalFreed}/kuus -- ${d3} peale. Viimane sprint. Olete peaaegu seal.`,
  step3noD3: (date, d2) => `Umbes ${date}: ${d2} on valmis. Olete volavabal!`,
  faqs: [
    { q: "Mis on Laviini meetod?",                 a: "Maksta miinimumid koigile voladele ja extra korgeima intressiga. Saastab koige rohkem intressi aja jooksul." },
    { q: "Mis on Lumipalli meetod?",               a: "Rundida madalaim jook esmalt. Parast maksmist kanda makse jargmisele. Maksab natuke rohkem intressi, kuid kiired voiud motiveerivad." },
    { q: "Mis on Korkea joogi meetod?",            a: "Fokuseerida korgeimaale joogile esmalt. Vahendab suurimat vola kiireimalt, kuid ei ole alati rahaliselt koige tõhusam." },
    { q: "Mis on Madala intressi meetod?",         a: "Maksta madalaima intressiga vola esmalt. Vabastab miinimumid kiireimalt, kuid maksab koige rohkem intressi kokku. Uldiselt ei soovitata." },
    { q: "Mida tahendab makse ule kandmine?",      a: "Kui vola on makstud, lisatakse see summa jargneva vola maksele. Kogu kuised kulud jaavad samaks -- lihtsalt suunatakse umberem." },
    { q: "Mis on KMM?",                            a: "Krediidi Maksumus Masstaapis -- aastane krediidikulu protsendina joogist. 22% KMM juures maksate umbes 1,83% kuus intressi." },
    { q: "Mis on volade konsolideerimine?",        a: "Uus laen mitme vola tasumiseks. Kui uus maarr on madalam, sastate. Risk: kaartide jatk kasutamine parast konsolideerimist voib vola suurendada." },
    { q: "Kui tapsed need arvutused on?",          a: "Eeldab fikseeritud maarasid ja korrapäraseid makseid. Tegelikud tulemused voivad erineda. Kasitlege seda usaldusvaarse juhisena, mitte garantiina. Ei ole finantsnoustamine." },
  ],
};

const lv = {
  ...en,
  months: ["jan","feb","mar","apr","mai","jun","jul","aug","sep","okt","nov","dec"],
  monthsFull: ["janvaris","februaris","marts","aprilis","maijs","junijs","julijs","augusts","septembris","oktobris","novembris","decembris"],
  fmtDuration: (months) => {
    const y = Math.floor(months / 12), m = months % 12;
    if (!y) return `${months} men.`;
    if (!m) return `${y} g.`;
    return `${y} g. ${m} men.`;
  },
  preparedBy: "Sagatavoja Weelco Inc.",
  disclaimer: "Šis zinojums ir tikai informatīvs. Weelco Inc. nav finansu konsultants.",
  p1Title: "Jusu paradu parskats",
  whereYouStand: "JUSU SITUACIJA ŠODIEN",
  totalDebt: "PARADS KOPĀ",
  acrossAccounts: n => `${n} kontā`,
  minPaymentPerMo: "Min. maksajums/men.",
  avgInterestRate: "Vid. procentu likme",
  payingMinimumsOnly: "Tikai minimalie maksajumi",
  minimumsWarning: (cost, date) => `Tikai minimumu maksasana izmaksa ${cost} procentos un ilgst lidz ${date}.`,
  withExtraSavings: (extra, saved, time) => `Ar ${extra}/men. papildus ietaupat ${saved} un pabeidzat ${time} aatrāk.`,
  recommended: "IETEICAMS",
  methodSavingsDesc: (desc, saved, worst, extra) => `${desc} · ietaupa ${saved} vs ${worst} ar ${extra}/men.`,
  yourScenarios: "JUSU MAKSASANAS SCENARIJI",
  yourDebts: "JUSU PARADI",
  conservative: "Konservativs",
  balanced: "Lidzsvarts",
  aggressive: "Agresivs",
  saves: amt => `ietaupa ${amt}`,
  p2Title: "Visi 4 metodi salīdzinati",
  basedOn: (debt, extra) => `Pamatojoties uz ${debt} paradu ar ${extra}/men. papildus`,
  debtFreeIn: "Bez parada par",
  totalInterest: "Procenti kopā",
  interestVsWorst: "Procenti vs sliktakais",
  summary: "Kopsavilkums",
  recommendedSummary: (method, saved, worst, extra) => `Ieteicams: ${method} — ietaupa ${saved} vs ${worst} metode ar ${extra}/men. papildus.`,
  methodLabels: { avalanche: "Lavīna", snowball: "Sniegpika", highbal: "Augsts atlikums", lowint: "Zems procents" },
  methodDescs: { avalanche: "Augstakais procents vispirms", snowball: "Zemakais atlikums vispirms", highbal: "Augstakais atlikums vispirms", lowint: "Zemakais procents vispirms" },
  methodDescsLong: { avalanche: "Augstakais procents vispirms", snowball: "Zemakais atlikums vispirms", highbal: "Augstakais atlikums vispirms", lowint: "Zemaka procentu likme vispirms" },
  methodDescsP2: {
    avalanche: "Maksāt augstako procentu vispirms. Matematiski optimals — ietaupa visvairak.",
    snowball:  "Maksāt zemako atlikumu vispirms. Atri panakumi uztur motivaciju.",
    highbal:   "Uzbrukt lielakajam paradam vispirms. Visaatrāk samazina lielako saistību.",
    lowint:    "Maksāt zemako procentu vispirms. Atri atbriva naudas plūsmu.",
  },
  p3Title: "Kuram paradam uzbrukt vispirms",
  minPerMo: amt => `Min: ${amt}/men.`,
  attackFirst: (reason, extra) => `Uzbrukt vispirms -- ${reason}. Visi ${extra}/men. papildus iet šeit.`,
  payMinimumsOthers: "Maksāt minimums visiem parējiem paradiem.",
  attackSecond: debt => `Uzbrukt otrajam. Kad ${debt} ir nomaksats,`,
  rollPaymentHere: amt => `parsut sava ${amt}/men. šeit virs minimuma.`,
  attackN: (n, prevN) => `Uzbrukt #${n}. Maksāt minimums lidz augstāk minetie ${prevN} paradi ir nomaksati,`,
  rollFreed: "tad parsut visus atbrivotus maksajumus šeit.",
  reasonHighestRate: rate => `augstakais procents ${rate}%`,
  reasonSmallestBal: bal => `zemakais atlikums ${bal}`,
  reasonLargestBal: bal => `augstakais atlikums ${bal}`,
  reasonLowestRate: rate => `zemakais procents ${rate}%`,
  consistency: {
    avalanche: extra => `Lavīnas atslega ir konsekvence -- nekad nepalaid garam minimumu un vienmēr liec ${extra} papildus uz paradu #1 lidz tas nomaksats.`,
    snowball:  extra => `Sniegpikas atslega ir konsekvence -- nekad nepalaid garam minimumu un vienmēr liec ${extra} papildus uz zemako atlikumu lidz tas nomaksats.`,
    highbal:   extra => `Augsta atlikuma atslega ir konsekvence -- nekad nepalaid garam minimumu un vienmēr liec ${extra} papildus uz augstako atlikumu lidz tas nomaksats.`,
    lowint:    extra => `Zema procenta atslega ir konsekvence -- nekad nepalaid garam minimumu un vienmēr liec ${extra} papildus uz paradu ar zemako procentu.`,
  },
  p4Title: "Kas notiek, ja maksājat vairak?",
  marginalAnalysis: (method, sweet) => `Marginalā analīze — ${method} metode · optimala punkts: ${sweet}/men.`,
  scenarioCol: "Scenarijs",
  debtFreeCol: "Bez parada",
  totalInterestCol: "Procenti kopā",
  totalSavedCol: "Kopā ietaupīts",
  marginalGainCol: "Marginalie ieguvumi",
  minimumsOnlyRow: amt => `${amt}/men. (tikai minimumi)`,
  scenarioRow: (amt, label) => `${amt}/men. — ${label}`,
  sweetSpotNote: sweet => `Optimala punkts: ${sweet}/men. dod labako atdevi -- katrs papildu eiro ietaupa mazak pec sia punkta.`,
  payAsMuchNote: "Maksājiet tik daudz papildu cik vien iespejams -- katrs eiro butiski samazina procentus.",
  aggressiveNote: (saved, extra) => `Agresiva maksasana ietaupa ${saved} vairak, bet prasa ${extra}/men. papildus -- lemiet pec budzeta.`,
  bonusNote: "Ja saprecat bonusu vai nodoklu atmaksu, novirziet to vispirms paradiem.",
  p5Title: "Menesis pec meneša grafiks",
  methodAllDebts: (method, months) => `${method} metode - visi paradi - ${months} menesi kopā`,
  monthCol: "Menesis",
  focusDebt: "Prioritatais parads",
  paymentCol: "Maksājums",
  principalCol: "Pamatsumma",
  interestCol: "Procenti",
  remainingCol: "Atlikusais",
  paidOffRolling: (names, next) => `${names} NOMAKSATS - parskaitam maksajumu uz ${next}`,
  paidOff: names => `${names} NOMAKSATS`,
  debtFreeRow: date => `BEZ PARADA - ${date}`,
  allDebtsNote: (n, date, interest) => `Visi ${n} paradi nomaksati lidz ${date}. Kopā samaksatie procenti: ${interest}.`,
  p6Title: "Paradu konsolidacijas analīze",
  isItWorthIt: "Vai tas jums ir verts?",
  aprConsolidationLoan: "GPL konsolidacijas aizdevums",
  totalInterestLabel: "procenti kopā",
  savesAmount: amt => `Ietaupa ${amt}`,
  costsMore: amt => `Izmaksa ${amt} vairak`,
  breakEvenPoint: fee => `Ietaupijuma punkts (ieskaitot ${fee} maksu)`,
  neverBreaksEven: "Nekad nesasniedz ietaupijumu -- nav ieteicams",
  breakEvenAfterPayoff: "Ietaupijums pec atmaksas datuma -- tikko verts",
  breakEvenSaves: (month, amt) => `Ietaupijums menesi ${month} -- ietaupa ${amt}/men. pec tam`,
  breakEvenBarely: month => `Ietaupijums menesi ${month} -- tikko verts`,
  atApr: rate => `Pie ${rate}% GPL`,
  ourRecommendation: "Musu ieteikums",
  consolidationBelowApr: rate => `Konsolidacija ir saprātīga tikai zemāk par ${rate}% GPL.`,
  noneConsolidationRates: "Neviena no šim likmēm neuzvar jusu pasreizejo atmaksas planu.",
  debtExpensive: (name, rate) => `Jusu ${name} pie ${rate}% ir dargi --`,
  qualifyConsolidation: "ja kvalificejaties 10% konsolidacijai, nopietni apsverciet to.",
  stopUsingCard: "Svarigi: partrauciet kredītkartites lietošanu pec konsolidacijas.",
  p7Title: "Jusu rīcības plans + BUJ",
  stepsToFreedom: "3 soli uz finansialo brivību -- sakot ar šodien",
  startingDate: date => `SAKUMS ${date}`,
  debtFreeDateLabel: "DATUMS BEZ PARADA",
  youWillSave: amt => `Ietaupīsiet ${amt} vs tikai minimalie maksajumi`,
  glossaryFaq: "Vārdnīca un BUJ",
  step1: (d1, extra, d1combined) => `Tagad: Iestatiet automātiskos minimālos maksājumus visiem paradiem iznemot ${d1}. Ielieciet visus ${extra}/men. papildus + ${d1} minimumu = ${d1combined}/men. kopā uz ${d1}. Neaizskariet to lidz nomaksajam.`,
  step2: (date, d1, d1combined, d2, d2combined) => `Apmēram ${date}: ${d1} ir nomaksats. Parskaitiet ${d1combined}/men. uz ${d2}. Tagad maksat ${d2combined}/men. par ${d2}.`,
  step3withD3: (date, d2, totalFreed, d3) => `Apmēram ${date}: ${d2} ir pabeigts. Parskaitiet visu -- ${totalFreed}/men. -- uz ${d3}. Pedejais sprints. Jūs esat gandrīz tur.`,
  step3noD3: (date, d2) => `Apmēram ${date}: ${d2} ir pabeigts. Jūs esat bez parada!`,
  faqs: [
    { q: "Kas ir Lavīnas metode?",                 a: "Maksāt minimums visiem paradiem un extra augstākajam procentam. Ietaupa visvairak procentos kopumā." },
    { q: "Kas ir Sniegpikas metode?",              a: "Uzbrukt zemākajam atlikumam vispirms. Pec nomaksas parsut maksajumu uz nakamo. Izmaksa nedaudz vairak procentos, bet atri panakumi motivē." },
    { q: "Kas ir Augsta atlikuma metode?",         a: "Fokusēties uz augstako atlikumu vispirms. Visatrāk samazina lielako paradu, bet ne vienmēr ir finansiāli visefektivakais." },
    { q: "Kas ir Zema procenta metode?",           a: "Nomaksāt paradu ar zemako procentu vispirms. Visatrāk atbriva minimālus maksājumus, bet izmaksa visvairak kopējos procentos. Kopumā nav ieteicams." },
    { q: "Ko nozime maksajuma parsutisana?",       a: "Kad parads ir nomaksats, ta summa tiek pievienota nakama parada maksājumam. Kopejie menesa izdevumi paliek tadi pasi -- tikai citadi virziti." },
    { q: "Kas ir GPL?",                            a: "Gada Procentu Likme -- gada kredīta izmaksas kā procents no atlikuma. Pie 22% GPL maksājat aptuveni 1,83% menesi procentos." },
    { q: "Kas ir paradu konsolidacija?",           a: "Jauns aizdevums vairaku paradu nomaksai. Ja jaunā likme ir zemāka, ietaupat. Risks: karsu turpmaka lietošana pec konsolidacijas var palielinat paradu." },
    { q: "Cik precīzi ir šie aprekini?",           a: "Pienem fiksetas likmes un regularus maksajumus. Faktiskie rezultati var atšķirties. Uzskatiet to par uzticamu ceļvedit, ne garantiju. Nav finansu konsultācija." },
  ],
};

const lt = {
  ...en,
  months: ["sau","vas","kov","bal","geg","bir","lie","rgp","rgs","spa","lap","grd"],
  monthsFull: ["sausis","vasaris","kovas","balandis","geguzis","birzelis","liepa","rugpjutis","rugsejis","spalis","lapkritis","gruodis"],
  fmtDuration: (months) => {
    const y = Math.floor(months / 12), m = months % 12;
    if (!y) return `${months} men.`;
    if (!m) return `${y} m.`;
    return `${y} m. ${m} men.`;
  },
  preparedBy: "Parenge Weelco Inc.",
  disclaimer: "Sis pranesimas yra tik informacinis. Weelco Inc. nera finansu patarejas.",
  p1Title: "Jusu skolu apzvalga",
  whereYouStand: "JUSU PADETIS ŠIANDIEN",
  totalDebt: "SKOLA IŠ VISO",
  acrossAccounts: n => `${n} saskaita${n !== 1 ? "se" : ""}`,
  minPaymentPerMo: "Min. imoka/men.",
  avgInterestRate: "Vid. palukanu norma",
  payingMinimumsOnly: "Tik minimalios imokos",
  minimumsWarning: (cost, date) => `Mokant tik minimumas kainuoja ${cost} palukanu ir trunka iki ${date}.`,
  withExtraSavings: (extra, saved, time) => `Su ${extra}/men. papildomai sutaupote ${saved} ir baigiate ${time} anksciau.`,
  recommended: "REKOMENDUOJAMA",
  methodSavingsDesc: (desc, saved, worst, extra) => `${desc} · taupo ${saved} vs ${worst} su ${extra}/men.`,
  yourScenarios: "JUSU MOKEJIMO SCENARIJAI",
  yourDebts: "JUSU SKOLOS",
  conservative: "Konservatyvus",
  balanced: "Subalansuotas",
  aggressive: "Agresyvus",
  saves: amt => `taupo ${amt}`,
  p2Title: "Visi 4 metodai palyginti",
  basedOn: (debt, extra) => `Remiantis ${debt} skola su ${extra}/men. papildomai`,
  debtFreeIn: "Be skolu per",
  totalInterest: "Palukanos is viso",
  interestVsWorst: "Palukanos vs blogiausias",
  summary: "Santrauka",
  recommendedSummary: (method, saved, worst, extra) => `Rekomenduojama: ${method} — taupo ${saved} vs ${worst} metodas su ${extra}/men. papildomai.`,
  methodLabels: { avalanche: "Laviną", snowball: "Sniego kamuolys", highbal: "Aukštas balansas", lowint: "Zemos palukanos" },
  methodDescs: { avalanche: "Aukšciausios palukanos pirma", snowball: "Zemiauses balansas pirma", highbal: "Aukšciausias balansas pirma", lowint: "Zemiausios palukanos pirma" },
  methodDescsLong: { avalanche: "Aukšciausios palukanos pirma", snowball: "Zemiauses balansas pirma", highbal: "Aukšciausias balansas pirma", lowint: "Zemiausia palukanu norma pirma" },
  methodDescsP2: {
    avalanche: "Moketi aukšciausias palukanas pirma. Matematiskai optimalu — taupo daugiausiai.",
    snowball:  "Moketi zemiause balansa pirma. Greiti laimejima issaugo motivacija.",
    highbal:   "Puolti didziausia skola pirma. Greiciausiai sumažina didziausią igipareigojima.",
    lowint:    "Moketi zemiausias palukanas pirma. Greitai atlaisvina pinigu srautą.",
  },
  p3Title: "Kokia skola puolti pirma",
  minPerMo: amt => `Min: ${amt}/men.`,
  attackFirst: (reason, extra) => `Puolti pirma -- ${reason}. Visi ${extra}/men. papildomai eina cia.`,
  payMinimumsOthers: "Moketi minimumas visoms kitoms skoloms.",
  attackSecond: debt => `Puolti antruoju. Kai ${debt} sumoketa,`,
  rollPaymentHere: amt => `perkelti jos ${amt}/men. cia virš minimalios imokos.`,
  attackN: (n, prevN) => `Puolti #${n}. Moketi minimumas kol ${prevN} aukšciaus skola sumoketa,`,
  rollFreed: "tada perkelti visas atlaisvintas imokas cia.",
  reasonHighestRate: rate => `aukšciausios palukanos ${rate}%`,
  reasonSmallestBal: bal => `zemiauses balansas ${bal}`,
  reasonLargestBal: bal => `aukšciausias balansas ${bal}`,
  reasonLowestRate: rate => `zemiausios palukanos ${rate}%`,
  consistency: {
    avalanche: extra => `Lavinos raktas yra nuoseklumas -- niekada nepraleiskite minimalios imokos ir visada dėkite ${extra} papildomai i skola Nr. 1 kol sumoketa.`,
    snowball:  extra => `Sniego kamuolio raktas yra nuoseklumas -- niekada nepraleiskite minimalios imokos ir visada dėkite ${extra} papildomai i zeemiausio balanso skola kol sumoketa.`,
    highbal:   extra => `Aukšto balanso raktas yra nuoseklumas -- niekada nepraleiskite minimalios imokos ir visada dėkite ${extra} papildomai i aukšciausia balansa kol sumoketa.`,
    lowint:    extra => `Zemu palukanu raktas yra nuoseklumas -- niekada nepraleiskite minimalios imokos ir visada dėkite ${extra} papildomai i skola su zemiausiomis palkanuomis.`,
  },
  p4Title: "Kas atsitinka jei mokate daugiau?",
  marginalAnalysis: (method, sweet) => `Marginaline analize — ${method} metodas · optimalus taskas: ${sweet}/men.`,
  scenarioCol: "Scenarijus",
  debtFreeCol: "Be skolu",
  totalInterestCol: "Palukanos is viso",
  totalSavedCol: "Is viso sutaupyta",
  marginalGainCol: "Marginali nauda",
  minimumsOnlyRow: amt => `${amt}/men. (tik minimumai)`,
  scenarioRow: (amt, label) => `${amt}/men. — ${label}`,
  sweetSpotNote: sweet => `Optimalus taskas: ${sweet}/men. duoda geriausią graza -- kiekvienas papildomas euras taupo maziau po šio taskio.`,
  payAsMuchNote: "Mokekite kiek imanoma daugiau papildomai -- kiekvienas euras zenkliai sumažina palukanas.",
  aggressiveNote: (saved, extra) => `Agresyvus mokejimas taupo ${saved} daugiau, bet reikalauja ${extra}/men. papildomai -- nuspresekite pagal biudzeta.`,
  bonusNote: "Jei gaunate premija ar mokesciu grazinima, skirkite ji pirmiausia skoloms.",
  p5Title: "Menuo po menesio grafikas",
  methodAllDebts: (method, months) => `${method} metodas - visos skolos - ${months} menesiu is viso`,
  monthCol: "Menuo",
  focusDebt: "Prioritetine skola",
  paymentCol: "Imoka",
  principalCol: "Pagrindine suma",
  interestCol: "Palukanos",
  remainingCol: "Likusios",
  paidOffRolling: (names, next) => `${names} SUMOKETA - perkeliame imoka i ${next}`,
  paidOff: names => `${names} SUMOKETA`,
  debtFreeRow: date => `BE SKOLU - ${date}`,
  allDebtsNote: (n, date, interest) => `Visos ${n} skolos sumoketos iki ${date}. Is viso sumoketos palukanos: ${interest}.`,
  p6Title: "Skolu konsolidavimo analize",
  isItWorthIt: "Ar apsimoka?",
  aprConsolidationLoan: "BKND konsolidavimo paskola",
  totalInterestLabel: "palukanos is viso",
  savesAmount: amt => `Taupo ${amt}`,
  costsMore: amt => `Kainuoja ${amt} daugiau`,
  breakEvenPoint: fee => `Atsipirkimo taskas (iskaitant ${fee} komisini)`,
  neverBreaksEven: "Niekada neatsiperkaa -- nerekomenduojama",
  breakEvenAfterPayoff: "Atsipirkimas po grazinimo datos -- vos apsimoka",
  breakEvenSaves: (month, amt) => `Atsipirkimas ${month} menesį -- taupo ${amt}/men. veliau`,
  breakEvenBarely: month => `Atsipirkimas ${month} menesį -- vos apsimoka`,
  atApr: rate => `Prie ${rate}% BKND`,
  ourRecommendation: "Musu rekomendacija",
  consolidationBelowApr: rate => `Konsolidavimas prasmingas tik zemiau ${rate}% BKND.`,
  noneConsolidationRates: "Ne vienas is šiu norms nepralenkia jusu esamo grazinimo plano.",
  debtExpensive: (name, rate) => `Jusu ${name} prie ${rate}% yra brangi --`,
  qualifyConsolidation: "jei tenkinate 10% konsolidavimo salygias, rimtai apsvarstykite.",
  stopUsingCard: "Svarbu: nustokite naudoti kreditine kortele po konsolidavimo.",
  p7Title: "Jusu veiksmų planas + DUK",
  stepsToFreedom: "3 zingsniai i finansine laisve -- pradedant šiandien",
  startingDate: date => `PRADZIA ${date}`,
  debtFreeDateLabel: "DATA BE SKOLU",
  youWillSave: amt => `Sutaupysite ${amt} vs tik minimalios imokos`,
  glossaryFaq: "Zodynėlis ir DUK",
  step1: (d1, extra, d1combined) => `Dabar: Nustatykite automatines minimalias imokas visoms skoloms, isskyrusi ${d1}. Dėkite visus ${extra}/men. papildomai + ${d1} minimumas = ${d1combined}/men. is viso i ${d1}. Neliesk kol nesumoketa.`,
  step2: (date, d1, d1combined, d2, d2combined) => `Mazdaug ${date}: ${d1} sumoketa. Perkelkite ${d1combined}/men. i ${d2}. Dabar mokėsite ${d2combined}/men. už ${d2}.`,
  step3withD3: (date, d2, totalFreed, d3) => `Mazdaug ${date}: ${d2} baigta. Perkelkite viska -- ${totalFreed}/men. -- i ${d3}. Paskutinis sprints. Jus beveik ten.`,
  step3noD3: (date, d2) => `Mazdaug ${date}: ${d2} baigta. Jus be skolu!`,
  faqs: [
    { q: "Kas yra Lavinos metodas?",               a: "Moketi minimumas visoms skoloms ir extra aukšciausioms palkanuoms. Taupo daugiausiai palukanu per laika." },
    { q: "Kas yra Sniego kamuolio metodas?",       a: "Puolti zemiause balansa pirma. Sumokejus perkelti imoka i sekancia. Kainuoja šiek tiek daugiau palukanu, bet greiti laimejima motyvuoja." },
    { q: "Kas yra Aukšto balanso metodas?",        a: "Sutelkti demesi i aukšciausia balansa pirma. Greiciausiai sumažina didziausia skola, bet ne visada finansiskai efektyviausias." },
    { q: "Kas yra Zemu palukanu metodas?",         a: "Sumoketi skola su zemiausiomis palkanuomis pirma. Greiciausiai atlaisvina minimalias imokas, bet kainuoja daugiausiai bendrų palukanu. Paprastai nerekomenduojama." },
    { q: "Ka reiškia imokos perkėlimas?",          a: "Kai skola sumoketa, ta suma pridedama prie sekamos skolos imokos. Bendros menesiniu isslaidu lieka tos pacios -- tik kitaip nukreipiamos." },
    { q: "Kas yra BKND?",                          a: "Bendras Kredito Normos Dydis -- metinis kredito kainaa kaip procentas nuo balanso. Prie 22% BKND mokate apytiksliai 1,83% per mënesi palukanu." },
    { q: "Kas yra skolu konsolidavimas?",          a: "Nauja paskola kelioms skoloms grazinti. Jei nauja norma zemesne, sutaupote. Rizika: korteles toliau naudojant po konsolidavimo gali padidinti skola." },
    { q: "Kiek tikslūs šie skaiciavimai?",         a: "Prisiima fiksuotas normas ir reguliarias imokas. Tikri rezultatai gali skirtis. Laikykite tai patikimu gidu, ne garantija. Tai nera finansinis patarimas." },
  ],
};

const STRINGS = { en, de, fr, es, pt, it, nl, sv, nb, da, fi, pl, cs, hu, ro, sk, sl, et, lv, lt };
